/**
 * TutorStageController.js — "The stage that breathes" (Task #221)
 *
 * A cinematic, lecture-style tutor lesson that is DISTINCT from the
 * Brilliant-style problem screen (ProblemLessonController). The live 3D legend
 * keeps the stage; a crisp 2D "board" (NEVER rendered inside the 3D scene)
 * builds composed cards beside them while the legend narrates the lesson in
 * auto-playing "beats".
 *
 * What it does:
 *   - fetches a beat script from /api/tutor-stage/lesson
 *   - plays each beat: reveal a board card -> the legend speaks the narration
 *   - "breathes": tutor-forward while explaining, board-forward while showing
 *     something concrete (root[data-focus] drives the CSS composition)
 *   - transport: pause/resume autoplay, replay this beat, prev/next, and free
 *     scroll-back through everything shown so far
 *   - drops in an interactive problem from the templated engine (renderProblem
 *     + /api/problems/generate), then returns to the lecture
 *   - felt-not-shown assessment: interrupt with a question any time + gentle
 *     checkpoints; problem drop-ins record mastery via /api/problems/assess
 *
 * Reuses the proven, A2F-safe speak path
 * (window.chatController.speakTopicIntroduction / speakDiagnosticQuestion) —
 * it NEVER touches the sacred audio pipeline directly. Self-registers as
 * window.tutorStage; reads globals lazily so load order never matters.
 */

import { renderProblem } from "./ProblemTemplates.js";
import { isGradable } from "./graders.mjs";
import { LessonReactionController, SPEECH_LEAD_MS } from "./LessonReactionController.js";
import { LessonDirectionController } from "./LessonDirectionController.js";
import { MiniGameHost } from "./MiniGameHost.js";
import { fractionSVG, renderFractionWidget, boardFigureHTML, describeWidgetState } from "./FractionWidgets.js";
import { renderHoldCardInto } from "./HoldCard.js";
// Player ceremony Iteration 2 — pure course-flow helpers (forward control,
// scoped completion stats, local-first result store). Node-testable.
import { resolveNext, scopedStats, LessonResultStore, FirstAttemptLedger, courseHref } from "./CourseFlow.js";
// Task #301 — the board card renderer is a SHARED module (the admin panel
// preview imports the same one), extracted from this file. Never fork it.
import { boardHTML, boardRevealPlan, boardMarkPlan } from "./LessonBoardRenderer.js";
// Task #347 — THE BOARD GRAMMAR: the ONE scheduler that fires anchored board
// performances off the narration audio playhead (Law 1: narration is the only
// clock). Reveals/marks/pans/direction register performers on it later; today
// zero performers are registered, so behavior is byte-identical while the
// timing plumbing (timing_data from /api/lesson/speak → cue clock) goes live.
import { CueScheduler, substituteStudentTokens } from "./CueScheduler.js";
import lessonSound from "./LessonSound.js";
import { RewardMotion, cleanHousePraise } from "./RewardMotion.js";
import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";
import { shouldUseA2FLipSync } from "./A2FLipSyncConfig.js";
import {
  A2F_FORWARD_PACING_ENABLED as LESSON_A2F_PACING_ENABLED,
  A2F_FORWARD_PACING_RATE as LESSON_A2F_PACING_RATE,
  A2F_FORWARD_PACING_WARMUP_MS as LESSON_A2F_PACING_WARMUP_MS,
} from "./A2FTuning.js";

// 16 kHz · 16-bit · mono → 32000 B/s; ~100 ms per bridge chunk. Mirrors the
// greeting path's PCM framing so the lesson A2F stream behaves identically.
const LESSON_A2F_PCM_BYTES_PER_MS = 32;
const LESSON_A2F_PCM_CHUNK_BYTES = 3200;
// Task #262 — Safari lesson lip-sync "prepare-then-play" gate. Hold the blessed
// <audio> element's start until the first A2F lip-frames are buffered (mirrors
// the lab's greeting sync gate) so the mouth tracks the voice from the FIRST
// word instead of lagging the ~1.3 s A2F pipeline lead-in. LEAD = buffered
// coverage (ms of audio the frames cover) that counts as "ready"; CEILING =
// hard cap so a slow / silent bridge can never hang a line.
const LESSON_A2F_START_LEAD_MS = 300;
const LESSON_A2F_START_GATE_CEILING_MS = 3000;
const CAPTURE_REQUEST_CEILING_MS = 6000;

// Rotating praise for a correct answer (Brilliant-style — never the same twice
// in a row). Pulled at random with a no-immediate-repeat guard.
export const HOUSE_PRAISE_FALLBACK = "Nicely done.";
export const PRAISE_POOL = [
  "Nice.", "Exactly.", "You've got it.", "Beautiful.", "That's it.",
  "Sharp.", "Well done.", "Spot on.", "Yes — perfect.", "Excellent.",
];
// Rotating, gentle encouragement for a teaching-problem miss (never the same
// twice in a row). Skill-check misses get the strict "Incorrect" instead.
const ENCOURAGE_POOL = [
  "Not quite — give it another go.",
  "Close — take another look.",
  "Almost. Try once more.",
  "Not yet — you're nearly there.",
  "Hmm, not that one — try again.",
];
const XP_PER_PROBLEM = 15;
export const isCustomCeremonyBeat = (beat) => !!(beat && beat.kind === "custom" &&
  (beat.role === "celebration" || beat.xp === 0));
// Player ceremony Iteration 2 — the moment belongs to the player for EVERY
// course. A lesson that authors no celebration gets a built-in silent one
// (~4 s: title card + teacher `celebrate` + stage burst — no praise, no XP,
// no bubble); a lesson that authors no completion board gets a synthesized
// one appended as a SYNTHETIC beat so _playCompletion / progress / the
// endstate / the completion beacon all run through the one existing path.
export const DEFAULT_CEREMONY_MS = 4000;
export const STAGE_CELEBRATION_MS = 2600;
export const SYNTHETIC_COMPLETION_ID = "brd_lesson_complete";
/** A beat the lesson GRADES (its completion is "the work is done"): checks,
 * games, and gated custom activities that are not ceremony. */
export const isGradedBeat = (beat) => !!(beat && (beat.kind === "problem" ||
  beat.kind === "game" ||
  (beat.kind === "custom" && beat.gate === "complete" && !isCustomCeremonyBeat(beat))));
/** A beat the player classifies as celebration (Iteration 1 predicate + the
 * explicit role marker on any kind). */
export const isCelebrationBeat = (beat) => !!(beat && (beat.role === "celebration" ||
  isCustomCeremonyBeat(beat)));
export const hasCompletionBoard = (lesson) => !!(lesson && Array.isArray(lesson.beats) &&
  lesson.beats.some((b) => b && b.board && b.board.kind === "completion"));
/** Normalize the completion board's authored scope. This is the same scope
 * used by the completion-card totals and by the player-delivered ceremony. */
export const completionScope = (beat) => {
  const scope = beat && beat.board && beat.board.kind === "completion"
    ? beat.board.scope : null;
  return scope === "level" || scope === "course" ? scope : "lesson";
};
/** The "lesson end" index: the LAST graded beat, or — when nothing is graded —
 * the last beat before any completion board (the last beat itself otherwise). */
export function lessonEndIndex(lesson) {
  const beats = (lesson && Array.isArray(lesson.beats)) ? lesson.beats : [];
  let last = -1;
  beats.forEach((b, i) => { if (isGradedBeat(b)) last = i; });
  if (last >= 0) return last;
  let end = beats.length - 1;
  while (end > 0 && beats[end] && beats[end].board && beats[end].board.kind === "completion") end--;
  return end;
}
/** Append the player's default completion board when the lesson authors none.
 * Idempotent; returns true when a beat was appended. Never touches a lesson
 * that already carries a completion board (never two cards). */
export function ensureDefaultCompletion(lesson) {
  if (!lesson || !Array.isArray(lesson.beats) || !lesson.beats.length) return false;
  if (hasCompletionBoard(lesson)) return false;
  lesson.beats.push({
    kind: "teach",
    synthetic: "completion",
    focus: "board",
    board: {
      kind: "completion",
      id: SYNTHETIC_COMPLETION_ID,
      kicker: "Lesson complete",
      title: String(lesson.title || "Lesson complete"),
      scope: "lesson",
    },
  });
  return true;
}

// Task #491 — explicit escape for gated custom activities (the silent
// two-minute force-unlock is gone). "Skip for now" appears a short think
// after the FIRST real attempt (the widget reported progress); a widget
// that boots but never reports any activity gets a clearly-labeled offer
// after a much longer wait instead.
const SKIP_OFFER_AFTER_TRY_MS = 20000;
const SKIP_OFFER_IDLE_MS = 90000;

// Task #492 — never-strand boot floor. Three bounded windows guarantee the
// begin chain can never hang a blank stage, each degrading per the existing
// degradation law (visible content beats choreography; the speaker is never
// paused — watchdogs only ever act when NOTHING is audibly playing):
//   • SPEAK_FETCH_TIMEOUT_MS — /api/lesson/speak deadline; on expiry the line
//     runs the proven no-audio path (estimate pacing, cues at line start).
//   • SPEECH_START_WATCHDOG_MS — playback never audibly started after the
//     clip was handed to the spoken path; treat as declined → isolated
//     fallback element (already bounded by its own hard cap).
//   • LINE_CLOCK_FLOOR_MS — the last net: if the line's clock never started,
//     snap staged board content visible (beginLine(null) = the degradation
//     law's all-at-once) so the learner is never left staring at nothing.
// QA harnesses may shrink these via window.__llBootTuning (never real users).
const SPEAK_FETCH_TIMEOUT_MS = 15000;
const SPEECH_START_WATCHDOG_MS = 8000;
const LINE_CLOCK_FLOOR_MS = 10000;
function bootTuned(key, dflt) {
  try {
    const t = typeof window !== "undefined" && window.__llBootTuning;
    const v = t && t[key];
    if (typeof v === "number" && isFinite(v) && v > 0) return v;
  } catch (_) {}
  return dflt;
}

// Task #302 — GENERIC fallbacks only. The real pools are DATA served by
// API.stageLines (static/data/stage_lines.json today; Task #301's store next).
// These exist solely so a slow/down fetch or an empty pool still gets theater
// instead of silence — do NOT grow per-legend voice lines here.
const GENERIC_GATHER_LINES = [
  "Give me just a moment — I'm gathering my thoughts on {topic}.",
  "Let me think about the best way to show you {topic}.",
  "One moment. I want to open this exactly right.",
];
const GENERIC_FAILURE_LINES = [
  "Hm — my notes are in disarray. Give me a moment, then ask me to try again.",
  "Even a good lecture sometimes needs a second attempt. Shall we try once more?",
];

const API = {
  lesson: "/api/tutor-stage/lesson",
  stageLines: "/api/tutor-stage/stage-lines",
  ask: "/api/tutor-stage/ask",
  problemGenerate: "/api/problems/generate",
  assess: "/api/problems/assess",
  speak: "/api/lesson/speak",
  grade: "/api/learn/grade",
  holdCommit: "/api/learn/hold-commit", // Task #353 — reveal ships only on commit (Trap 5)
};

// Widget types whose verdict is decided SERVER-SIDE for skill checks come
// straight from THE grader table (graders.mjs) — the same module the server
// executes for /api/learn/grade and derives its answer-stripping manifest
// from. Gradable = has a grader entry; there is no hand-maintained list here
// to fall out of lockstep (Task #485 killed the 4-registry trap).

const BEAT_GAP_MS = 650; // breathing room between beats when autoplaying

// Task #348 — film capture mode v1: fixed, deterministic pause after each
// teaching beat's narration before the hands-free advance (founder filming).
const FILM_BEAT_GAP_MS = 1100;

// Task #348 — board kinds whose fragments stage AUTOMATICALLY from the
// narration alignment when no authored anchors exist ("the board writes
// itself" for the whole current catalog). completion/leagues keep their
// bespoke play-on-land choreography unless an author STAGES them explicitly
// with `at`/`fx`; problem/game cards have no board to stage.
const AUTO_STAGE_KINDS = new Set([
  "heading", "equation", "visual", "definition", "diagram",
  "points", "example", "summary",
]);

// Effect durations (runtime-owned — authors only pick the verb). Keep in
// step with the tsFx* keyframes in tutor-stage.css.
const FX_MS = { write: 420, draw: 650, fade: 380, none: 0 };

// Task #284 — lesson narration pace. Sent as an EXPLICIT flag on the
// /api/lesson/speak request so ONLY /learn narration slows down; the /lab
// conversation + greeting paths never send it and are byte-identical.
// ElevenLabs voice_settings.speed supported range is 0.7–1.2 (1.0 = normal).
const LESSON_NARRATION_SPEED = 0.87;

export class TutorStageController {
  constructor() {
    this.isOpen = false;
    this.lesson = null;
    this.idx = -1;
    this.playing = false;
    this.character = "your mentor";
    this.legendToken = "";
    this.topic = "";
    this._runToken = 0;
    this._dom = null;
    this._busy = false;
    // Task #239 — standalone /learn mode. When true, this controller runs on a
    // dedicated page with NO /lab globals (no window.chatController / window.tts).
    // Speech routes through the resident legend's own A2F-safe greeting path and
    // "exit" navigates away instead of hiding an overlay.
    this._standalone = false;
    this._resident = null;
    this._record = null;
    this._characterId = "";
    this._characterSlug = "";
    this._speechSeq = 0;
    // Task #347 — the one cue scheduler (see CueScheduler.js for the grammar,
    // determinism and clock-hold contracts). ?cueDebug=1 exposes its log at
    // window.__cueLog (the bundle strips console.*, so the array IS the
    // debug surface).
    let cueDebug = false;
    try {
      cueDebug = typeof location !== "undefined" &&
        /[?&]cueDebug=1/.test(location.search || "");
    } catch (_) {}
    this._cues = new CueScheduler({ debug: cueDebug });
    if (cueDebug) { try { window.__cueLog = this._cues.log; } catch (_) {} }
    // Task #348 — the board writes itself. One reveal performer projects EVERY
    // cue onto staged fragments (idempotent: reveal-once per fragment), and the
    // scheduler's word ticks drive the captions bar. Both are registered up
    // front and are inert until _stageCurrentCard stages something.
    this._revealPlan = null;        // boardRevealPlan of the CURRENT card
    this._revealFrags = null;       // [{f, els, revealed}] aligned to plan
    this._revealByContainer = null; // anchor container → [fragment indices]
    this._revealCardIdx = -1;
    this._revealGen = 0;            // bumps on every (re)stage/finalize — see _revealFrag
    this._cardDoneSent = false;
    this._chalkLastAt = 0;
    this._rmMql = null;             // prefers-reduced-motion media query
    // Task #350 — the lecturer's hand. Mark state mirrors reveal state but
    // arms INDEPENDENTLY of staging (marks fire in preview and on legacy
    // unstaged boards too). _marks[i] ↔ boardMarkPlan(board).marks[i]; the
    // authored mark dict itself is the cue container the scheduler collected.
    this._markPlan = null;
    this._marks = null;             // [{m, els, applied, nodes}]
    this._marksByContainer = null;  // mark dict → [mark indices]
    this._markCardIdx = -1;
    this._markResize = null;        // keeps overlay strokes glued on reflow
    // Task #352 — the plate camera. Focus state for the CURRENT card's plate
    // board (if any): steps[i] ↔ board.focus[i]; the authored focus entry
    // dict itself is the cue container the scheduler collected. Transform is
    // percentage-based so viewport reflow (chat-split drag) never drifts an
    // applied focus — no ResizeObserver needed.
    this._plateFocus = null;        // {cardIdx, steps, byContainer, viewport, img, resolved}
    try { this._cues.registerPerformer("*", (p) => this._onRevealCue(p)); } catch (_) {}
    try { this._cues.registerWordListener((e) => this._onWordTick(e)); } catch (_) {}
    this._pendingDirection = [];
    try {
      this._cues.registerLineEndListener(({ reason, beatIndex, wasActive }) => {
        if (reason === "ended") {
          // Player ceremony Iteration 2 (A1) — a board nobody STAGED (e.g. a
          // completion card, which never auto-stages) still finishes its
          // line: emit its card-done here so `after:<boardId>` cues resolve
          // and fire at line end, exactly like a staged board's last
          // fragment would. Interrupts (below) discard them instead.
          if (wasActive) this._emitUnstagedCardDone(beatIndex);
          this._drainPendingDirection(beatIndex);
        } else this._pendingDirection = [];
      });
    } catch (_) {}
    // Captions primitive (Trap 7): persisted preference; film mode (below)
    // forces them ON by default because the speech bubble is chrome.
    this._captionsOn = false;
    try { this._captionsOn = localStorage.getItem("legendlabs.learnCaptions") === "on"; } catch (_) {}
    this._capSpans = null;
    this._capKaraoke = false;
    // Task #348 — film capture mode v1 (?film=1): deterministic hands-free
    // autoplay with a clean feed. &quiet=1 silences UI cues for the take
    // (session-only — never persisted into the learner's mute preference);
    // &cc=0 opts the take out of captions.
    this._filmMode = false;
    this._quiet = false;
    try {
      const qs = (typeof location !== "undefined" && location.search) || "";
      this._filmMode = /[?&]film=1/.test(qs);
      if (this._filmMode) {
        this._quiet = /[?&]quiet=1/.test(qs);
        this._captionsOn = !/[?&]cc=0/.test(qs);
      }
    } catch (_) {}
    // Task #353 — while a check/hold interaction is OPEN the narration clock
    // is held (counted CueScheduler hold). One flag pairs exactly one hold()
    // with one release() no matter how many exit paths run.
    this._interactionHoldOn = false;
    // Task #251 — XP + praise + combo state (Brilliant-style gamification).
    this.xp = this._loadXp();
    this._combo = 0;
    this._lastPraise = -1;
    this._lastEncourage = -1;
    this._chatOpen = false;
    // Ambient-reaction throttle — keep Newton attentive, not twitchy. Low-stakes
    // "glance / I'm-with-you" events fire at most once per window; high-stakes
    // feedback (answer_correct/wrong, hint, reveal, lesson_complete, chat_opened)
    // bypasses the throttle entirely (see _emit).
    this._AMBIENT_EVENTS = new Set([
      "board_shown", "drag_active", "student_working", "beat_idle", "long_think",
    ]);
    this._AMBIENT_MS = 3800;
    this._emitAt = {};
    // Stall nudge + live-widget handle (used for "long_think" + chat student-state)
    this._stallTimer = null;
    this._currentWidget = null;
    this._hintsUsed = 0;
    // Task #524 — the chat tutor "sees" the student's work through three
    // session-only holds: what they DID (interaction ledger ring), what was
    // SAID (recent chat turns ring), and which mini-game is live. Client-held
    // only; dies with the page; never persisted anywhere.
    this._ledger = [];
    this._chatTurns = [];
    // Beats whose board is CURRENTLY displaying the revealed answer (strict
    // skill-check miss, see-answer, film quiet reveal). Cleared when the
    // beat's widget re-mounts fresh or is answered correctly, so the ask
    // context always matches what is actually on the student's screen.
    this._revealedBeats = new Set();
    this._currentGame = null;
    // Task #259 — Safari lesson lip-sync. On Safari narration is played on the
    // gesture-blessed _narrationEl (audible), and IN PARALLEL the same audio is
    // decoded to PCM and streamed to a dedicated A2F driver whose lip cursor is
    // anchored to that element's currentTime. State for that side stream:
    this._a2fLessonDriver = null;   // reused across lines, reset per line
    this._a2fLessonWs = null;       // bridge WebSocket for the active line
    this._a2fLessonGen = 0;         // per-line generation (stale-frame guard)
    this._a2fLessonActive = false;  // a line's A2F stream is in flight
    this._a2fLessonPacerTimer = null;
    this._a2fLessonAudioEl = null;  // element the cursor reads currentTime from
  }

  _loadXp() {
    try { return parseInt(localStorage.getItem("legendlabs.learnXp") || "0", 10) || 0; }
    catch (_) { return 0; }
  }

  _saveXp() {
    try { localStorage.setItem("legendlabs.learnXp", String(this.xp)); } catch (_) {}
  }

  _sfx(name, opts) {
    if (this._quiet) return; // film-quiet take (session-only, never persisted)
    try { lessonSound.play(name, opts); } catch (_) {}
  }

  async start(opts = {}) {
    // Task #302 — a double-fired open (twitchy click, duplicate handler) must
    // never run two boots (and two lesson POSTs) side by side. One in-flight
    // start at a time; _loadLesson has its own single-flight guard as well.
    if (this._starting) return;
    this._starting = true;
    try {
      await this._start(opts);
    } finally {
      this._starting = false;
    }
  }

  async _start(opts = {}) {
    this._bootMark("stage-start", { authored: !!(opts.lesson && opts.lesson.beats) });
    this._previewMode = !!opts.previewMode;
    this._captureMode = opts.captureMode === true;
    this._captureBeat = this._captureMode ? (opts.captureBeat | 0) : -1;
    // Targeted capture is user-operated and must never inherit ?film=1's
    // auto-answer/auto-advance behavior.
    if (this._captureMode) this._filmMode = false;
    if (opts.standalone) {
      this._standalone = true;
      this._resident = opts.resident || null;
      this._record = opts.record || null;
      this._characterId = opts.characterId || "";
      this._characterSlug = opts.characterSlug || "";
      if (opts.characterName) this.character = opts.characterName;
      if (this._characterSlug) this.legendToken = this._characterSlug;
    }
    if (opts.courseId) this.courseId = opts.courseId;
    if (opts.lessonId) this.lessonId = opts.lessonId;
    // Standalone /learn supplies the verified Flask-session UID, including an
    // explicit null for anonymous visitors. Pin it for the whole run; this page
    // deliberately has no Firebase initialization, and late auth readiness
    // must never repartition results after the lesson starts. Other hosts keep
    // the existing best-effort Firebase snapshot.
    if (Object.prototype.hasOwnProperty.call(opts, "resultLearnerId")) {
      this._resultLearner = typeof opts.resultLearnerId === "string" && opts.resultLearnerId
        ? opts.resultLearnerId
        : null;
    } else {
      let auth = null;
      try { auth = window.firebaseAuth || null; } catch (_) {}
      if (auth && typeof auth.authStateReady === "function") {
        let authTimer = null;
        try {
          await Promise.race([
            auth.authStateReady(),
            new Promise((resolve) => { authTimer = setTimeout(resolve, 1000); }),
          ]);
        } catch (_) {
          // Identity is best-effort on legacy Firebase-initialized hosts.
        } finally {
          if (authTimer) clearTimeout(authTimer);
        }
      }
      this._resultLearner = null;
      try { this._resultLearner = auth?.currentUser?.uid || null; } catch (_) {}
    }
    // Task #301 — reset the store pin. Authored (/learn catalog) lessons never
    // set one, so they never fire the completion beacon or store-pinned
    // problem fetches; generated lessons re-pin in _loadLesson.
    this._lessonKey = "";
    this._lessonVersion = null;
    this._completeSent = false;
    // Task #308 — Resume shelf: reopen the exact {lessonKey, version} the
    // student was served (never a re-roll). Cleared on every open so a stale
    // pin can never leak into a normal "Lecture" open.
    this._resumePin = (opts.resume && opts.resume.lessonKey && opts.resume.version)
      ? { key: String(opts.resume.lessonKey), version: opts.resume.version | 0 }
      : null;
    this._readContext(opts);
    this._ensureDom();
    // Task #302 — hall mode (/lab overlay over the full-screen legend): the
    // stage becomes a SPLIT — legend column left, board docked right — and all
    // hall chrome stands down (CSS keys off ts-hall + body.tutor-stage-open).
    // /learn's standalone player keeps its own composition untouched.
    this._hall = !this._standalone;
    if (this._dom && this._dom.root) {
      this._dom.root.classList.toggle("ts-hall", this._hall);
      // Task #348 — film capture: clean feed, no chrome (CSS keys off ts-film).
      this._dom.root.classList.toggle("ts-film", !!this._filmMode);
    }
    this._show();
    this._attachReaction();
    // Best-effort: resend any problem reports that were saved offline because a
    // previous submission couldn't reach the server. Deferred so it never
    // blocks lesson boot.
    if (!this._captureMode) {
      try { setTimeout(() => this._drainOfflineFlags(), 4000); } catch (_) {}
    }
    // Task #246 — a preauthored Brilliant lesson: play the authored beats
    // directly and SKIP topic-based generation (/api/tutor-stage/lesson). The
    // beats arrive in the exact stage shape, so render + play from beat 0.
    if (opts.lesson && Array.isArray(opts.lesson.beats) && opts.lesson.beats.length) {
      this.lesson = opts.lesson;
      this._prepareLessonEnd();
      // Warm the cached auth header now: the end-of-lesson beacon must be
      // initiated synchronously (keepalive) and can never await a token.
      try { this._authHeaderAsync().catch(() => {}); } catch (_) {}
      if (opts.lesson.topic) this.topic = opts.lesson.topic;
      else if (opts.lesson.title) this.topic = opts.lesson.title;
      this._renderShell();
      this._bootMark("shell-rendered", { beats: this.lesson.beats.length });
      this._emit("lesson_open");
      // Preview (QA/screenshot): render a specific beat with NO autoplay and NO
      // speech, so the static look can be captured without WebGL or audio.
      if (this._previewMode) {
        this.playing = false;
        this._syncTransport();
        const pIdx = Math.max(0, opts.previewBeat | 0);
        await this._goTo(pIdx, { autoplay: false });
        // Surface the narration caption statically (no audio) so the QA preview
        // shows the same on-screen text the legend would speak.
        const pBeat = this.lesson.beats[pIdx];
        if (pBeat && pBeat.say) this._setCaption(pBeat.say);
        // QA-only: render the chat-open split layout statically (`&chat=1`).
        if (opts.previewChat) {
          this._toggleChatPanel(true);
          this._appendBubble("legend", (pBeat && pBeat.say) || "Now, the shape has more pieces to work with.");
          this._appendBubble("you", "How can I help?");
        }
        return;
      }
      if (this._captureMode) {
        this.playing = false;
        this._syncTransport();
        await this._goTo(this._captureBeat, { autoplay: false });
        return;
      }
      this.playing = true;
      try { lessonSound.unlock(); } catch (_) {}
      this._sfx("start");
      // Task #264 — the learner arrived from the course page's "Ask the teacher"
      // dock with a question. Answer it first (open chat, render beat 0 silently
      // as context), then offer to begin the lecture — instead of autoplaying.
      if (opts.pendingAsk) {
        await this._askPending(String(opts.pendingAsk));
        return;
      }
      // Demo deep link (&beat=N): fast-forward to beat N — _goTo's
      // _revealUpTo projects every prior beat instantly (same reveal the
      // review/jump navigation uses, no narration re-run), then the lesson
      // plays normally from N. Default 0 = unchanged behavior.
      const startIdx = Math.max(
        0,
        Math.min(opts.startBeat | 0, this.lesson.beats.length - 1),
      );
      await this._goTo(startIdx, { autoplay: true });
      return;
    }
    await this._loadLesson(!!opts.fresh);
  }

  close() {
    this._runToken++; // cancel any in-flight beat
    this._destroyGameHost();
    // Task #351 — stop the derived performance with the stage (any running
    // gesture envelope decays out on its own; nothing is hard-cut).
    try { if (this._direction) { this._direction.dispose(); this._direction = null; } } catch (_) {}
    // Task #302 — closing mid-generation must actually cancel the request, so
    // an open→close→open can never stack a second POST on a live one.
    this._lessonRun = (this._lessonRun || 0) + 1;
    this._gathering = false;
    try { if (this._lessonAbort) this._lessonAbort.abort(); } catch (_) {}
    this._lessonAbort = null;
    this._interruptSpeech();
    this.playing = false;
    if (this._standalone) {
      // Dedicated page — leave the surface entirely rather than hide an overlay.
      try {
        if (window.history.length > 1) window.history.back();
        else window.location.assign("/");
      } catch (_) {
        window.location.assign("/");
      }
      return;
    }
    this._hide();
    this.isOpen = false;
  }

  toggle(opts = {}) {
    if (this.isOpen) this.close();
    else this.start(opts);
  }

  _readContext(opts) {
    if (!this._standalone) {
      try {
        if (typeof window.getActiveLegendContext === "function") {
          const ctx = window.getActiveLegendContext() || {};
          if (ctx.name) this.character = ctx.name;
          if (ctx.token) this.legendToken = ctx.token;
          if (ctx.topic && !opts.topic) this.topic = ctx.topic;
        }
      } catch (_) {}
    }
    if (opts.topic) this.topic = opts.topic;
    if (!this.topic) {
      this.topic = this.character !== "your mentor"
        ? `the world of ${this.character}` : "a key idea";
    }
  }

  get chat() { return window.chatController || null; }

  async _loadLesson(fresh) {
    // Single-flight: ONE lesson POST per open, no matter how many callers race
    // in (Task #302 guard; Task #301 store-first serving on the server side).
    if (this._lessonReq) return;
    const run = (this._lessonRun = (this._lessonRun || 0) + 1);
    this._renderLoading();
    // Alive opening: the legend gathers their thoughts OUT LOUD while the board
    // skeleton assembles behind. Never blocks the fetch; the hand-off into
    // beat 0 waits for an in-flight line so it is never cut mid-sentence.
    const gatherP = this._playGathering();
    const ac = typeof AbortController !== "undefined" ? new AbortController() : null;
    this._lessonAbort = ac;
    // Task #492 — the generated-lesson POST is the one remaining unbounded
    // await on this path. Generation can legitimately take a while (the
    // gathering theater exists for exactly that), but a fetch that outlives
    // this ceiling is a wedge, not a slow LLM — abort → the existing Retry
    // card, never an infinite skeleton. Cleared when the request settles.
    let genTimedOut = false;
    const genCeiling = ac
      ? setTimeout(() => {
          genTimedOut = true;
          this._bootMark("lesson-generate-timeout");
          try { ac.abort(); } catch (_) {}
        }, bootTuned("lessonGenerateMs", 120000))
      : null;
    // The promise is assigned SYNCHRONOUSLY (single-flight guard above), and
    // the auth token is awaited inside it — Task #308 needs a real Bearer
    // token so signed-in plays are attributed and land on the Resume shelf.
    this._lessonReq = (async () => {
      const authH = await this._authHeaderAsync();
      const res = await fetch(API.lesson, {
      method: "POST",
      // Auth is used server-side to exclude flagged test accounts from the
      // counters (Task #301) and to record the signed-in student's
      // recent-lesson history for the Resume shelf (Task #308).
      headers: { "Content-Type": "application/json", ...authH },
      body: JSON.stringify({
        topic: this.topic,
        character_name: this.character,
        // Revision #9: fresh is honored ONLY after a failed serve — the sole
        // caller passing fresh=true is the failure card's Retry button.
        fresh: !!fresh,
        // Task #301 — store-first serving: name the legend tree explicitly,
        // and only honor fresh AFTER a failed serve in this session.
        legend: this.legendToken || this._characterSlug || "",
        after_error: !!(fresh && this._hadServeError),
        client_id: this._clientId(),
        // Task #308 — Resume pin (server serves this exact version when it
        // still exists; otherwise it falls back to normal precedence).
        lesson_key: this._resumePin ? this._resumePin.key : undefined,
        pin_version: this._resumePin ? this._resumePin.version : undefined,
      }),
      signal: ac ? ac.signal : undefined,
      });
      if (!res.ok) throw new Error("lesson failed " + res.status);
      const data = await res.json();
      if (!data.lesson || !Array.isArray(data.lesson.beats) || !data.lesson.beats.length) {
        throw new Error("empty lesson");
      }
      return data;
    })();
    let data = null;
    let err = null;
    try {
      data = await this._lessonReq;
    } catch (e) {
      err = e;
    } finally {
      if (genCeiling) { try { clearTimeout(genCeiling); } catch (_) {} }
      this._lessonReq = null;
      this._lessonAbort = null;
    }
    // Superseded (closed, or a newer load started) — stand down silently.
    if (run !== this._lessonRun || !this.isOpen) return;
    if (err) {
      // Task #492 — a CEILING abort is a wedge, not a close(): surface the
      // Retry card instead of standing down into an infinite skeleton.
      if (err && err.name === "AbortError" && !genTimedOut) return;
      console.error("[TutorStage] load error", err);
      this._stopGathering();
      // A failed SERVE unlocks Retry (which re-requests the same endpoint
      // with fresh+after_error) — the professor pauses, never a dead end.
      this._hadServeError = true;
      this._renderError(true);
      this._speakFailureLine();
      return;
    }
    // Task #301 — pin {lessonKey, version} for the WHOLE play. The embedded
    // problem fetch and the completion beacon echo this pin even if an
    // approve/retire lands mid-play.
    this._lessonKey = data.lessonKey || "";
    this._lessonVersion = data.version || null;
    this._completeSent = false;
    this._hadServeError = false;
    // Let a mid-sentence gathering line finish before the lecture begins —
    // with a hard ceiling so a wedged speak path can never hold beat 0 hostage.
    this._gathering = false;
    try {
      await Promise.race([gatherP, new Promise((r) => setTimeout(r, 12000))]);
    } catch (_) {}
    if (run !== this._lessonRun || !this.isOpen) return;
    this.lesson = data.lesson;
    this._renderShell();
    this._emit("lesson_open");
    this.playing = true;
    await this._goTo(0, { autoplay: true });
  }

  /** "Gathering his thoughts" / "professor pauses" theater. Pools are DATA
   * fetched from the store contract (#301) at /api/tutor-stage/stage-lines —
   * never hardcoded here beyond the tiny generic fallback the task mandates
   * for when the fetch is slow/down or a pool is empty. Spoken strictly via
   * the existing speak path (_speak → speakTopicIntroduction). */
  async _fetchStageLines(kind, timeoutMs) {
    let pools = null;
    try {
      pools = await Promise.race([
        (async () => {
          if (this._stageLines) return this._stageLines;
          const res = await fetch(
            API.stageLines + "?character=" + encodeURIComponent(this.character));
          if (!res.ok) throw new Error("stage-lines " + res.status);
          this._stageLines = (await res.json()) || {};
          return this._stageLines;
        })(),
        new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs || 1200)),
      ]);
    } catch (_) { pools = null; }
    const generic = kind === "failure" ? GENERIC_FAILURE_LINES : GENERIC_GATHER_LINES;
    const arr = pools && Array.isArray(pools[kind]) && pools[kind].length
      ? pools[kind] : generic;
    return arr;
  }

  _pickStageLine(lines) {
    if (!lines || !lines.length) return "";
    let last = "";
    try { last = localStorage.getItem("legendlabs.lastStageLine") || ""; } catch (_) {}
    const fresh = lines.filter((l) => l !== last);
    const pool = fresh.length ? fresh : lines;
    const line = pool[Math.floor(Math.random() * pool.length)] || "";
    try { localStorage.setItem("legendlabs.lastStageLine", line); } catch (_) {}
    return line
      .replace(/\{topic\}/g, this.topic || "this idea")
      .replace(/\{name\}/g, this.character || "your mentor");
  }

  async _playGathering() {
    // Hall mode only: /learn's authored lessons never hit generation, and the
    // preview path must stay silent for QA screenshots.
    if (!this._hall || this._previewMode) return;
    const token = this._runToken;
    this._gathering = true;
    try {
      const lines = await this._fetchStageLines("gathering", 1400);
      // The lesson may have landed while we fetched (cache hit): no theater —
      // an instant lesson needs no filler, and we never START a line late.
      if (!this._gathering || token !== this._runToken || !this.isOpen) return;
      let spoken = 0;
      while (spoken < 2 && this._gathering && token === this._runToken &&
             this.isOpen && this._lessonReq) {
        const line = this._pickStageLine(lines);
        if (!line) break;
        await this._speak(line, token);
        spoken++;
        if (!this._gathering || !this._lessonReq) break;
        // A musing beat of silence before a second line on a long wait.
        await this._sleep(2600, token);
      }
    } catch (_) {}
    finally { this._gathering = false; }
  }

  _stopGathering() {
    this._gathering = false;
    this._interruptSpeech();
  }

  async _speakFailureLine() {
    if (!this._hall || this._previewMode) return;
    try {
      const lines = await this._fetchStageLines("failure", 1200);
      const line = this._pickStageLine(lines);
      // Fire-and-forget: the card + Retry are already on screen.
      if (line && this.isOpen) this._speak(line, this._runToken);
    } catch (_) {}
  }

  _authHeader() {
    try {
      const t = window.__lastIdToken || (window.firebaseAuth &&
        window.firebaseAuth.currentUser && window.firebaseAuth.__idToken);
      if (t) return { Authorization: `Bearer ${t}` };
    } catch (_) {}
    return {};
  }

  /** Task #308 — the sync `_authHeader` above only sees a token when some
   * other module happened to cache one, so signed-in plays usually arrived
   * anonymous and the Resume shelf never recorded them. This async variant
   * actually mints a fresh Firebase ID token (getIdToken serves from its
   * local cache, so it's near-instant) and is used by the lesson request and
   * the completion beacon. Resolves to {} when signed out. */
  async _authHeaderAsync() {
    try {
      const u = window.currentSignedInUser ||
        (window.firebaseAuth && window.firebaseAuth.currentUser);
      if (u && typeof u.getIdToken === "function") {
        const t = await u.getIdToken();
        if (t) {
          // Cache for unload-sensitive paths (the completion beacon must fire
          // SYNCHRONOUSLY — keepalive only protects an already-initiated
          // fetch, so it can never afford to await token minting). Also feed
          // the shared sync cache other modules read.
          this._cachedAuthHeader = { Authorization: `Bearer ${t}` };
          try { window.__lastIdToken = t; } catch (_) {}
          return this._cachedAuthHeader;
        }
      }
    } catch (_) {}
    return this._authHeader();
  }

  /** Attach the thin local face/pose reaction controller to the resident's live
   * viewer ONCE. It is stepped from ModelViewer.animate and stands down while
   * A2F owns the face, so it can never fight the sacred audio path. The stage
   * and the mini-games speak to it ONLY via semantic events through _emit(). */
  _attachReaction() {
    if (this._reaction) return;
    try {
      const resident = this._resident;
      const viewer = resident && typeof resident.getViewer === "function"
        ? resident.getViewer() : null;
      if (!viewer) return;
      if (viewer.lessonReactionController) {
        this._reaction = viewer.lessonReactionController;
      } else {
        this._reaction = new LessonReactionController(viewer);
        viewer.lessonReactionController = this._reaction;
      }
      this._attachDirection();
    } catch (_) {}
  }

  /** Task #351 — attach the direction policy layer (the derived performance)
   * ONCE, and only where the performance belongs: the standalone player with a
   * live resident legend. Preview (static QA), reduced-motion, and the Lab
   * shell never get one — those surfaces behave exactly as before. The layer
   * is stepped from the reaction controller's update() (shared facial loop)
   * and only ever calls direct()/primitives, so the A2F face gate and the
   * never-pause-the-speaker rule hold mechanically. */
  _attachDirection() {
    if (this._direction || !this._reaction) return;
    if (!this._standalone || this._previewMode) return;
    if (this._prefersReducedMotion()) return;
    try {
      this._direction = new LessonDirectionController(this._reaction);
      this._direction.setDeferredDrain(() => this._drainPendingDirection(this.idx));
      this._reaction.director = this._direction;
    } catch (_) {}
  }

  // The ONE place the stage/widgets express a reaction: name WHAT happened and
  // let the mapper decide how the legend responds. No clip/gaze names here.
  _emit(event, payload) {
    if (this._AMBIENT_EVENTS?.has(event) &&
        (this._pendingDirection?.length || this._ceremonyPerformance)) return;
    if (!this._reaction) this._attachReaction();
    try {
      // Throttle only the low-stakes ambient events so Newton stays calm; let
      // meaningful feedback (correct/wrong/hint/reveal/complete/chat) through.
      if (this._AMBIENT_EVENTS && this._AMBIENT_EVENTS.has(event)) {
        const now = Date.now();
        if (now - (this._emitAt[event] || 0) < this._AMBIENT_MS) return;
        this._emitAt[event] = now;
      }
      if (this._reaction) this._reaction.emit(event, payload);
    } catch (_) {}
  }

  async _goTo(index, { autoplay } = {}) {
    if (!this.lesson) return;
    if (this._captureMode && index !== this._captureBeat) return;
    const beats = this.lesson.beats;
    index = Math.max(0, Math.min(index, beats.length - 1));
    // Old celebration widgets may start confetti before accepting context.
    // Skip the whole authored ceremony on replay, not its sandbox boundary.
    while (!this._captureMode && index < beats.length - 1 && isCelebrationBeat(beats[index]) &&
        !this._bigCeremonyAllowed()) index++;
    // cancel anything currently speaking / pending
    this._runToken++;
    const token = this._runToken;
    this._interruptSpeech();
    this._clearCheckpoint();
    this._destroyGameHost();
    this._clearActiveProblem();
    // Navigating away from an open check/game/hold must free the clock it
    // held (Task #353) — the new beat starts with this controller's slot
    // released (a modal's own hold, if any, survives: counted holds).
    this._releaseClockForInteraction();
    this._clearFeedback();
    // New beat — reset per-problem state used for reactions + chat student-state.
    this._currentWidget = null;
    this._currentProblem = null;
    this._currentGame = null; // Task #524 — a game registers itself on mount
    this._hintsUsed = 0;
    this._lastAttempts = 0;
    if (this._dom && this._dom.root) delete this._dom.root.dataset.skillcheck;

    this.idx = index;
    const beat = beats[index];
    // Task #491 — backdrop phases: the warm brand frame always keeps the
    // entry gate (stage closed) and the end-phase boards; the while-solving
    // A/B token (body.stage-bg-white via ?bg=white) covers everything else.
    try {
      const endPhase = !!(beat && beat.board &&
        (beat.board.kind === "completion" || beat.board.kind === "leagues"));
      document.body.classList.toggle("ts-endphase", endPhase);
    } catch (_) {}
    // Task #347 — seek determinism (Trap 3): every navigation fully re-arms the
    // scheduler (fired-set cleared, cues re-collected from the beat), so board
    // state is rebuilt from (playhead, graded answers) — _revealUpTo is the
    // graded-answer projection, the scheduler owns the playhead projection —
    // never resumed from stale fire-and-forget state.
    this._cues.armBeat(index, beat);
    this._revealUpTo(index);
    // Task #348 — stage the new card's fragments so the narration can write
    // them in (no-op in preview / reduced motion / Lab mode / legacy boards).
    this._stageCurrentCard(index, beat);
    // Task #492 — the first beat's card is in the DOM (staged fragments and
    // all): the boot rendered SOMETHING. One-shot per page life.
    if (!this._bootBeatMarked) {
      this._bootBeatMarked = true;
      this._bootMark("first-beat-rendered", { index });
    }
    // Task #350 — arm the card's marks (the lecturer's hand). Independent of
    // staging: preview flushes them instantly, reduced motion jumps to the
    // final marked state, Lab mode leaves the stamps inert.
    this._armMarks(index, beat);
    // Task #352 — arm the plate camera (primary sources on the board): focus
    // entries pan/zoom to named manifest rects on narration anchors.
    this._armPlateFocus(index, beat);
    if (!beat.say) {
      this._dismissNarrationBubble();
      this._renderCaptionBar("");
      // No narration will ever cue this card — it performs ON LAND instead:
      // authored verbs animate once on entry (the fx-only timing policy for
      // completion/leagues and any say-less staged beat); nothing may strand.
      this._revealStagedOnLand();
      // Player ceremony Iteration 2 (A1) — a say-less UNSTAGED board lands
      // complete: its card-done fires now so `after:<id>` chains resolve.
      this._emitUnstagedCardDone(index);
      // Same degradation for marks: no narration → no clock → a word-anchored
      // mark applies at beat start (after-anchored ones ride event chains).
      this._finalizeMarksNow();
      // Same for plate focus: no clock → snap to the LAST authored focus
      // state at beat start (the plate still shows what matters most).
      this._finalizePlateFocusNow();
    }
    this._setFocus(beat.focus || "tutor");
    this._updateProgress();
    this._syncActionBar(beat);
    this._scrollToCurrent();
    // A small glance toward whatever just appeared on the board (throttled).
    this._emit("board_shown");
    // Task #351 — derived performance stance for the new beat: an interactive
    // beat opens facing the student (attentive); everything else returns to
    // the teaching stance. Never touches the rig directly — the direction
    // layer only issues rate-capped head/body gestures via the reaction
    // controller, which stands the face down whenever A2F owns it.
    if (this._direction) {
      try {
        if (beat.kind === "problem" || beat.kind === "game" ||
            beat.kind === "hold" || beat.kind === "custom") {
          this._direction.onCheckOpened();
        } else this._direction.setMode("teaching");
      } catch (_) {}
    }

    // Special end-phase boards (completion / leagues) animate the moment the
    // learner lands on them, independent of the narration that follows.
    if (!this._captureMode && beat.kind !== "problem" && beat.kind !== "game" && beat.board) {
      if (beat.board.kind === "completion") this._playCompletion();
      else if (beat.board.kind === "leagues") this._playLeagues();
    }

    // Skill-check arc: a one-time transition gate before the first skill-check
    // question (never during a review pass). The learner taps Start to begin.
    // Film mode (Trap 10): the intro gate is chrome — the take skips it and
    // rolls straight into the auto-answered check.
    if (beat.skillCheck && this._isFirstSkill(index) && !this._scIntroShown &&
        !this._reviewMode && !this._filmMode && !this._captureMode) {
      this._showSkillIntro(index);
      return;
    }

    if (beat.kind === "problem") {
      await this._runProblem(beat, token);
      return;
    }

    if (beat.kind === "game") {
      await this._runGame(beat, token);
      return;
    }

    if (beat.kind === "hold") {
      // Task #353 — the ungraded predict-pause: prompt → commit → server-held
      // reveal. The clock holds while it is open; the legend faces the student.
      await this._runHold(beat, token);
      return;
    }

    if (beat.kind === "custom") {
      // v9 — the authored widget: a complete little page performed inside a
      // null-origin sandbox. The card owns the flow (gate/complete bridge).
      await this._runCustom(beat, token);
      return;
    }

    // teaching beat — Brilliant feel: the legend narrates the line, and the
    // action bar's "Continue" (always available) advances when the learner is
    // ready. We no longer auto-chain to the next beat — the learner drives the
    // pace, exactly like Brilliant. Narration still plays (sacred speak path).
    await this._speak(beat.say, token);
    if (token !== this._runToken) return; // superseded by a navigation
    if (this._captureMode) return;

    // Task #348 + #353 (Trap 10 complete) — film capture mode: hands-free
    // deterministic advance after each teaching beat's narration (fixed gap;
    // checkpoint modals skipped — no chrome on the take). Interactive beats
    // no longer stop the take: holds auto-commit and checks auto-answer
    // deterministically inside their own run paths, so the film advances
    // into EVERY beat and a full lesson films end to end.
    if (this._filmMode) {
      await this._sleep(FILM_BEAT_GAP_MS, token);
      if (token !== this._runToken) return;
      if (this.idx >= this.lesson.beats.length - 1) { this._onLessonEnd(); return; }
      this._next();
      return;
    }

    if (beat.checkpoint && beat.checkpoint.ask) {
      this._showCheckpoint(beat, token);
    }
  }

  // Brilliant-style primary advance. Teaching beats are driven by this Continue
  // click (the action bar), not autoplay. Plays the continue cue, then advances.
  _onContinue() {
    if (!this.lesson) return;
    // Bug fix (v2 handoff #5): never let Continue skip an unsolved activity.
    // On interactive beats the widget owns the flow (Check → feedback →
    // its own Continue); the action bar should be hidden there, but guard
    // anyway in case a stray/stale click lands while a problem is live.
    const beat = this.lesson.beats[this.idx];
    const interactive = beat && (beat.kind === "problem" || beat.kind === "game" ||
      beat.kind === "hold" || beat.kind === "custom" ||
      (beat.widget && typeof beat.widget === "object"));
    // While a problem is live-and-unsolved, _activeProblem is set (it is
    // cleared on correct / strict-wrong / reveal / skip), so its presence is
    // the "unsolved" signal.
    if (interactive && this._activeProblem) return;
    this._sfx("continue");
    this._next();
  }

  _next() {
    if (!this.lesson) return;
    if (this._captureMode) return;
    const from = this.idx;
    this._proceedAfter(from, () => {
      if (from >= this.lesson.beats.length - 1) { this._onLessonEnd(); return; }
      this._goTo(from + 1, { autoplay: this.playing });
    });
  }

  _prev() {
    if (!this.lesson) return;
    if (this._captureMode) return;
    this._goTo(Math.max(0, this.idx - 1), { autoplay: this.playing });
  }

  _replay() {
    this._goTo(this.idx, { autoplay: this.playing });
  }

  // Retake without reloading the resident/avatar. _goTo remounts the selected
  // widget and advances the run token, invalidating late grading and iframe
  // callbacks from the previous take.
  resetCapture() {
    if (!this._captureMode || !this.lesson || this._captureBeat < 0) return false;
    this._goTo(this._captureBeat, { autoplay: false });
    return true;
  }

  _togglePlay() {
    // Transport removed in the Brilliant reskin (Task #251); kept as a harmless
    // re-run of the current beat for any legacy caller.
    this._goTo(this.idx, { autoplay: false });
  }

  _onLessonEnd() {
    if (this._captureMode) return;
    this.playing = false;
    this._destroyGameHost();
    this._releaseClockForInteraction(); // never carry a held clock past the end
    this._sendCompletion();
    // Pending reaction-before-speech and widget callbacks use the run token,
    // not just the speech sequence. Finish invalidates both before leaving.
    this._runToken = (this._runToken || 0) + 1;
    this._clearActiveProblem();
    const d = this._dom;
    if (!d) {
      this._interruptSpeech();
      return;
    }
    if (d.actionbar) d.actionbar.classList.remove("is-on");
    if (d.endNote) d.endNote.classList.add("show");
    this._sfx("endstate");
    // Player ceremony Iteration 2 (B4) — the forward control: a finished
    // lesson leads somewhere. The beacon above is initiated synchronously
    // (keepalive) BEFORE we leave; "Finish" (nothing follows) stays put with
    // the end-note / replay affordances.
    // Tear the finished line down at the last possible moment before leaving.
    // Natural line-end processing (including card-done cues) has already run;
    // this only prevents speech/carry-over telemetry surviving slow navigation.
    this._interruptSpeech();
    this._navigateForward();
  }

  _navigateForward() {
    const nx = this._forwardControl();
    if (!nx || nx.kind === "none" || !nx.href) return false;
    if (this._previewMode || this._filmMode) return false;
    const go = typeof this._navigate === "function"
      ? this._navigate : (href) => window.location.assign(href);
    try { go(nx.href); } catch (_) { return false; }
    return true;
  }

  // Cut whatever is currently speaking. In standalone /learn mode the only
  // speaker is the resident legend's GreetingService — stop() is the proven,
  // A2F-safe interrupt: it tears down the in-flight line and NEVER pauses the
  // speaker. In Lab mode we use the shared window.tts.interrupt() as before.
  _interruptSpeech() {
    this._pendingDirection = [];
    this._ceremonyPerformance = null;
    try { if (this._cancelRewardMotion) this._cancelRewardMotion(); } catch (_) {}
    // Invalidate every async speech continuation even when this boundary is
    // lesson-end navigation (which does not otherwise advance _runToken).
    this._speechSeq = (this._speechSeq || 0) + 1;
    try {
      const rc = this._reaction;
      if (rc && typeof rc.interrupt === "function") rc.interrupt();
    } catch (_) {}
    // Stop any isolated fallback clip (the non-A2F <audio> used when the spoken
    // lip-synced path declined) so an interrupt never leaves it talking AND so a
    // stale timeout/handler from it can't later pause or clear a NEWER narration
    // line that reuses the same persistent _narrationEl. _fallbackStop tears the
    // active clip down cleanly and bumps the generation counter so its pending
    // timer becomes a no-op.
    // Task #492 — the interrupted line's boot floor is moot (the interrupter
    // rebuilds state); clear its timer so it can never fire late.
    this._clearLineFloor();
    // Task #347 — stop the cue line FIRST (no flush): the interrupter is about
    // to rebuild board state deterministically (_goTo → armBeat + _revealUpTo),
    // so pending timed cues must NOT fire on the way down.
    try { this._cues.endLine("interrupt"); } catch (_) {}
    // Task #348 — an interrupted line's staged fragments snap to their final
    // visible state (never strand hidden); board-event chains still complete.
    try { this._finalizeStagedNow(true); } catch (_) {}
    try { if (this._fallbackStop) this._fallbackStop(); } catch (_) {}
    try {
      if (this._fallbackAudio) { this._fallbackAudio.pause(); this._fallbackAudio = null; }
    } catch (_) {}
    // Task #259 — also tear down any in-flight Safari A2F face stream (idempotent;
    // _fallbackStop already does this, but cover the case where it was null).
    try { this._stopLessonA2F(); } catch (_) {}
    if (this._standalone) {
      try {
        const gs = this._resident && this._resident.viewer &&
          this._resident.viewer.greetingService;
        if (gs && typeof gs.stop === "function") gs.stop();
      } catch (_) {}
      // Player ceremony Iteration 2 (A2) — truthful telemetry: a line cut by a
      // beat transition ends the SAME way a natural end does for the body
      // animation controller (its TTS-active flag + SPEAK state), so live
      // verification can read them. gs.stop() tears the voice/face stream
      // down but never told the controller; the natural end does
      // (GreetingService → onTTSEnd). No audio touched, no face writer.
      try { this._resetAnimationTelemetry(); } catch (_) {}
      return;
    }
    try { window.tts && window.tts.interrupt && window.tts.interrupt(); } catch (_) {}
  }

  _resetAnimationTelemetry() {
    const ac = this._resident && this._resident.viewer &&
      this._resident.viewer.animationController;
    if (!ac || !ac.isTTSActive) return;
    if (typeof ac.onTTSEnd === "function") ac.onTTSEnd();
    else ac.isTTSActive = false;
  }

  async _speak(text, token, onStart) {
    if (!text) return;
    if (this._captureMode) {
      // Narration normally reveals staged fragments. Capture has no narration
      // clock, so project the selected board to its final visible state now.
      try { this._finalizeStagedNow(false); } catch (_) {}
      try { this._finalizeMarksNow(); } catch (_) {}
      try { this._finalizePlateFocusNow(); } catch (_) {}
      try { this._emitUnstagedCardDone(this.idx); } catch (_) {}
      this._dismissNarrationBubble();
      this._renderCaptionBar("");
      return;
    }
    this._setCaption(text);
    this._setSpeaking(true);
    if (this._previewMode) {
      // Preview has no narration clock — degradation law: word cues fire at
      // line start so the previewed board reaches the same final state.
      try { this._cues.beginLine(null); } catch (_) {}
      this._setSpeaking(false);
      return;
    }
    if (this._standalone) {
      await this._speakResident(text, token, onStart);
      // Task #492 — the line ran its course (or degraded); its boot floor is
      // done. Guarded: a NEWER line's floor stays armed (seq mismatch).
      if (token === this._runToken) this._clearLineFloor();
      if (token === this._runToken) this._setSpeaking(false);
      return;
    }
    try {
      const chat = this.chat;
      if (chat && typeof chat.speakTopicIntroduction === "function") {
        await chat.speakTopicIntroduction(text);
      } else if (chat && typeof chat.speakDiagnosticQuestion === "function") {
        chat.speakDiagnosticQuestion(text);
        await this._sleep(this._estimateMs(text), token);
      } else {
        await this._sleep(this._estimateMs(text), token);
      }
    } catch (e) {
      // interrupt() rejects the in-flight promise — that's expected on nav
    }
    if (token === this._runToken) this._setSpeaking(false);
  }

  // React-then-speak (Task #257): wait for any head reaction the caller just
  // emitted (nod / shake / celebrate) to play to COMPLETION before speaking, so
  // the reaction and the spoken line (A2F) never overlap. A hard ceiling means a
  // dropped animation frame can never stall the lesson. Respects the run token.
  async _speakAfterReaction(text, token, opts) {
    const reactAtVoice = opts && opts.reactAtVoice;
    const sfxAtVoice = opts && opts.sfxAtVoice;
    // OPTIONAL voice-onset sync: if a caller passes reactAtVoice/sfxAtVoice we
    // fire the reaction + chime via `onStart` at the EXACT moment audio begins.
    // The default lesson feedback NO LONGER uses this — per user feedback the
    // verdict (nod/shake/smile + chime) fires INSTANTLY at grading and the spoken
    // line follows (the else branch below), which feels far more rewarding. Kept
    // as a capability. Once-guarded so it fires exactly once.
    let onStart = null;
    if (reactAtVoice || sfxAtVoice) {
      let started = false;
      onStart = () => {
        if (started) return;
        started = true;
        try { if (sfxAtVoice) this._sfx(sfxAtVoice); } catch (_) {}
        try { if (reactAtVoice) this._emit(reactAtVoice); } catch (_) {}
      };
    } else {
      // Legacy react-then-speak: a reaction was already emitted at click time;
      // release the line a touch BEFORE the head gesture fully settles so the
      // nod/shake rides into the first moment of speech (Task #258), instead of
      // finishing first. The controller still owns the face hand-off.
      try {
        const rc = this._reaction;
        if (rc && typeof rc.whenSettled === "function") {
          await Promise.race([rc.whenSettled(SPEECH_LEAD_MS), this._sleep(2200, token)]);
        }
      } catch (_) {}
      if (token !== this._runToken) return;
      // Belt-and-suspenders: ensure our local face footprint is at rest before
      // we hand the face to the voice. No-op if A2F is already active or already
      // clean; never writes a face morph during speech (idle-only, gated inside).
      try {
        const rc = this._reaction;
        if (rc && typeof rc.handoffFaceNow === "function") rc.handoffFaceNow();
      } catch (_) {}
    }
    await this._speak(text, token, onStart);
  }

  // Task #492 — boot breadcrumb pass-through (recorder lives inline in
  // tutor_stage.html; absent on /lab hall mode → safe no-op).
  _bootMark(name, detail) {
    try {
      if (typeof window !== "undefined" && window.__llBoot &&
          typeof window.__llBoot.mark === "function") {
        window.__llBoot.mark(name, detail);
      }
    } catch (_) {}
  }

  // Task #399 — announce the first AUDIBLE words of the session, exactly once,
  // as a DOM event. The tutor-stage warming pill (inline in tutor_stage.html)
  // listens and clears itself the moment the teacher truly starts speaking.
  // Pure chrome signal — never touches the audio path or its timing.
  _announceFirstWords() {
    if (this._firstWordsSent) return;
    this._firstWordsSent = true;
    this._bootMark("first-words");
    try { document.dispatchEvent(new CustomEvent("ll:first-words")); } catch (_) {}
  }

  // Poll a caller-supplied "is audio audibly playing?" probe until it flips
  // true (→ announce), the line window passes, or the announcement already
  // happened. Polling (150ms) is deliberate: the GreetingService path plays
  // through sacred internals we must not instrument, but its currentAudio
  // playhead is public — the same signal the cue clock reads.
  _watchFirstWords(isPlaying) {
    if (this._firstWordsSent || !this._standalone) return;
    const startedAt = Date.now();
    const tick = () => {
      if (this._firstWordsSent) return;
      let on = false;
      try { on = !!isPlaying(); } catch (_) {}
      if (on) { this._announceFirstWords(); return; }
      if (Date.now() - startedAt > 20000) return; // line failed/ended — the next line re-arms
      setTimeout(tick, 150);
    };
    setTimeout(tick, 150);
  }

  // ── Task #492 — never-strand boot floor ─────────────────────────────────
  // The last net under every speech path: if the line's cue clock never
  // starts within the floor window (fetch wedged, playback never began,
  // element never fired play), snap the staged board content visible via
  // beginLine(null) — the degradation law's all-at-once — and stop promising
  // a voice. Fires ONLY while nothing is audibly playing; every reveal is
  // idempotent (the scheduler's fired-set), so a late-starting real line
  // simply narrates over an already-visible board. Never touches audio.
  _armLineFloor(token, seq) {
    this._clearLineFloor();
    const ms = bootTuned("lineFloorMs", LINE_CLOCK_FLOOR_MS);
    this._lineFloorTimer = setTimeout(() => {
      this._lineFloorTimer = null;
      if (token !== this._runToken || seq !== this._speechSeq) return;
      if (this._previewMode) return;
      if (this._lineAudiblyStarted()) return;
      console.log("[LEARN-BOOT] line floor fired — snapping staged content visible");
      this._bootMark("line-floor-fired", { beat: this.idx });
      this._announceFirstWords();
      try { this._cues.beginLine(null); } catch (_) {}
    }, ms);
  }

  _clearLineFloor() {
    if (this._lineFloorTimer) {
      try { clearTimeout(this._lineFloorTimer); } catch (_) {}
      this._lineFloorTimer = null;
    }
  }

  // The same PUBLIC playheads the cue clock and _watchFirstWords read — the
  // greeting service's current element and the isolated fallback element.
  _lineAudiblyStarted() {
    try {
      const gs = this._resident && this._resident.viewer &&
        this._resident.viewer.greetingService;
      const a = gs && gs.currentAudio;
      if (a && (a.currentTime > 0 || !a.paused)) return true;
    } catch (_) {}
    try {
      const el = this._fallbackAudio;
      if (el && (el.currentTime > 0 || !el.paused)) return true;
    } catch (_) {}
    return false;
  }

  // Task #492 — await a speech promise with (a) a start watchdog that only
  // fires while playback has NOT audibly begun, and (b) a generous end
  // ceiling once it HAS (clip length × 3 + 20s — nothing plausible plays that
  // long). Resolves {kind:"resolved",spoke} | {kind:"stalled"} |
  // {kind:"end-ceiling"} | {kind:"superseded"}; rejects only if the speech
  // promise itself rejects first. The audio is never paused here.
  _raceSpeechStart(p, startedProbe, token, seq, text) {
    const watchdogMs = bootTuned("startWatchdogMs", SPEECH_START_WATCHDOG_MS);
    const endCeilingMs = this._estimateMs(text) * 3 + 20000;
    return new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;
      let startedAt = null;
      const armedAt = Date.now();
      const finish = (v, isErr) => {
        if (settled) return;
        settled = true;
        if (timer) { try { clearTimeout(timer); } catch (_) {} }
        if (isErr) reject(v); else resolve(v);
      };
      p.then(
        (spoke) => finish({ kind: "resolved", spoke }),
        (e) => finish(e, true),
      );
      const tick = () => {
        if (settled) return;
        if (token !== this._runToken || seq !== this._speechSeq) {
          finish({ kind: "superseded" });
          return;
        }
        let on = false;
        try { on = !!startedProbe(); } catch (_) {}
        if (on && startedAt == null) startedAt = Date.now();
        if (startedAt == null) {
          if (Date.now() - armedAt >= watchdogMs) { finish({ kind: "stalled" }); return; }
        } else if (Date.now() - startedAt >= endCeilingMs) {
          finish({ kind: "end-ceiling" });
          return;
        }
        timer = setTimeout(tick, 250);
      };
      timer = setTimeout(tick, 250);
    });
  }

  // Standalone speak: synthesize the line via /api/lesson/speak and play it in
  // the legend's own voice through the resident's A2F-safe greeting path. The
  // promise resolves at end-of-audio (GreetingService.playPersonalizedGreeting
  // resolves when the clip ends), which is exactly what paces the beat loop.
  //
  // Sequencing mirrors GreetingService.playGreeting's own proven order:
  //   stop() the prior line -> set greetingCanceled=false -> play.
  // stop() leaves greetingCanceled=true and playPersonalizedGreeting bails while
  // it's true, so we MUST re-arm the flag before playing. We only touch that
  // public flag — never the sacred audio internals.
  async _speakResident(text, token, onStart) {
    const resident = this._resident;
    if (!resident || typeof resident.speakGreeting !== "function") {
      // No speaker → no clock: staged fragments must never strand hidden.
      try { this._finalizeStagedNow(true); } catch (_) {}
      // Task #399 — no voice is coming; the lesson runs on captions, so the
      // "gathering the first words" pill must not keep promising one.
      this._announceFirstWords();
      await this._sleep(this._estimateMs(text), token);
      return;
    }
    const seq = ++this._speechSeq;
    // Task #492 — never-strand: whatever happens to the fetch/playback below,
    // staged board content must become visible within a bounded window.
    this._armLineFloor(token, seq);
    // Interrupt now increments the speech generation even before first play.
    // Boot observability tracks fetches, not that cancellation generation.
    const firstLine = !this._residentFetchStarted;
    this._residentFetchStarted = true;
    let data = null;
    try {
      // Task #492 — bounded: a wedged /api/lesson/speak (the first-line fetch
      // that must stay OUTSIDE the Begin-gesture stack for Safari) used to
      // hang this await forever — beginLine was never reached and the staged
      // card sat blank. On expiry we fall into the proven no-audio branch.
      const ac = typeof AbortController !== "undefined" ? new AbortController() : null;
      const deadline = bootTuned("speakFetchMs", SPEAK_FETCH_TIMEOUT_MS);
      const fetchTimer = ac
        ? setTimeout(() => { try { ac.abort(); } catch (_) {} }, deadline)
        : null;
      if (firstLine) this._bootMark("first-line-fetch-start");
      try {
        const res = await fetch(API.speak, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text,
            character_id: this._characterId,
            // Task #284 — lesson-only slower narration (explicit opt-in flag).
            speed: LESSON_NARRATION_SPEED,
          }),
          signal: ac ? ac.signal : undefined,
        });
        console.log("[LEARN-AUDIO] /api/speak ok=" + (res && res.ok) +
          " status=" + (res && res.status));
        // The deadline covers the BODY too (a multi-MB base64 clip on a
        // stalled stream strands exactly like stalled headers) — the timer
        // stays armed until json() settles, and abort() rejects json().
        if (res.ok) data = await res.json();
      } finally {
        if (fetchTimer) clearTimeout(fetchTimer);
      }
      if (firstLine) this._bootMark("first-line-fetched", { audio: !!(data && data.audio_base64) });
    } catch (e) {
      const timedOut = !!(e && e.name === "AbortError");
      console.log("[LEARN-AUDIO] /api/speak fetch FAILED" +
        (timedOut ? " (deadline)" : ""), e);
      if (firstLine) {
        this._bootMark(timedOut ? "first-line-fetch-timeout" : "first-line-fetch-failed");
      }
    }
    // Superseded by a newer line or a navigation while we were fetching.
    if (seq !== this._speechSeq || token !== this._runToken) return;
    // Task #347 — attach the hash-pinned word timing that now rides with the
    // narration clip (or null → every cue degrades to line start). This is the
    // ONLY consumer of timing_data; the greeting path below still receives
    // timing_data:null so the legacy phoneme lip-sync stays dormant (A2F is
    // king of the face — the scheduler drives the BOARD, never the face).
    let timingOk = false;
    try { timingOk = this._cues.setLineTiming((data && data.timing_data) || null); } catch (_) {}
    // Task #348 — default staging + word-paced captions ride the SAME
    // alignment. Authored anchors already sit in the scheduler; this only
    // synthesizes cues for anchor-less staged fragments (or snaps them
    // visible when there is no usable alignment — degradation law).
    try { this._armAutoReveals(timingOk ? data.timing_data : null); } catch (_) {}
    try { this._armCaptionWords(timingOk ? data.timing_data : null); } catch (_) {}
    if (!data || !data.audio_base64) {
      console.log("[LEARN-AUDIO] no audio_base64 — pacing by estimate");
      // No audio came back — pace by estimate so the lecture doesn't race
      // ahead. No clock → cues fire at line start (degradation law).
      // Task #399 — same rule as the no-speaker branch: captions are rolling,
      // stop promising a voice.
      this._announceFirstWords();
      try { this._cues.beginLine(null); } catch (_) {}
      await this._sleep(this._estimateMs(text), token);
      return;
    }
    // Safari / iOS: GreetingService plays a freshly-created <audio> element AFTER
    // this fetch — out of the Begin-gate gesture stack — which Safari silently
    // blocks (it then falls back to text-only and reports spoke=true, so the
    // lesson went mute). Route Safari narration through the persistent element we
    // blessed inside the gesture so it is reliably audible. Task #259: the same
    // clip is ALSO decoded and streamed to A2F in parallel (see _playFallbackClip
    // / _startLessonA2F), with the lip cursor anchored to that element, so the
    // face lip-syncs on Safari too — matching every other browser.
    if (this._isSafari() && this._narrationEl) {
      console.log("[LEARN-AUDIO][Safari] narration via blessed <audio> element");
      await this._playFallbackClip(data.audio_base64, text, token, onStart);
      return;
    }
    console.log("[LEARN-AUDIO] audio_base64 len=" + data.audio_base64.length +
      " → speakGreeting()");
    try {
      const gs = resident.viewer && resident.viewer.greetingService;
      if (gs) {
        if (typeof gs.stop === "function") gs.stop();
        gs.greetingCanceled = false;
      }
    } catch (_) {}
    // Task #262 — fire the at-voice reaction + chime as the spoken line is handed
    // to playback. The greeting path gates internally (prepare-then-play), so the
    // nod + chime + first word land together. Once-guarded by the caller, so the
    // declined-fallback path below can call it again without double-firing.
    try { if (typeof onStart === "function") onStart(); } catch (_) {}
    // Task #347 — begin the cue line on the SAME time base the lip-sync path
    // anchors to: GreetingService's live <audio> element playhead. The clock
    // returns null until playback actually starts (the rAF loop just waits),
    // so cues can never fire against wall-clock time (Law 1).
    try {
      const viewer = resident.viewer;
      this._cues.beginLine(() => {
        const gs2 = viewer && viewer.greetingService;
        const a = gs2 && gs2.currentAudio;
        const t = a ? a.currentTime : null;
        return typeof t === "number" && isFinite(t) && (t > 0 || (a && !a.paused))
          ? t * 1000 : null;
      });
    } catch (_) {}
    // Task #399 — clear the warming pill at the FIRST audible moment (same
    // public playhead the cue clock reads; read-only).
    this._watchFirstWords(() => {
      const gs3 = resident.viewer && resident.viewer.greetingService;
      const a = gs3 && gs3.currentAudio;
      return !!(a && (a.currentTime > 0 || !a.paused));
    });
    // Task #492 — never-strand: speakGreeting could hang without EVER starting
    // playback (decode wedge, A2F prepare gate stuck, viewer half-ready) and
    // this await then stranded the whole boot — resolved cues waited on a clock
    // that never started, so the staged card stayed blank forever. Race it
    // against a start watchdog that fires ONLY while nothing is audibly
    // playing; once audio truly starts we NEVER interfere (the speaker is
    // sacred). A generous end ceiling (vs. clip length) frees the beat loop in
    // the pathological played-but-never-resolved case.
    const startedProbe = () => {
      const gsX = resident.viewer && resident.viewer.greetingService;
      const a = gsX && gsX.currentAudio;
      return !!(a && (a.currentTime > 0 || !a.paused));
    };
    let spoke = false;
    let outcome = null;
    try {
      outcome = await this._raceSpeechStart(
        resident.speakGreeting(
          {
            greeting_text: text,
            audio_base64: data.audio_base64,
            timing_data: null,
          },
          this._record,
        ),
        startedProbe, token, seq, text,
      );
    } catch (e) {
      // A stop()-on-interrupt can leave the awaited promise unresolved; the new
      // _runToken-bearing beat drives forward, so this is harmless.
      console.log("[LEARN-AUDIO] speakGreeting() threw (likely interrupt)", e);
      return;
    }
    if (outcome.kind === "superseded") return;
    if (outcome.kind === "stalled") {
      // Playback never began — this is NOT a playing speaker, so tearing the
      // wedged attempt down is safe. Recover audibly through the isolated
      // fallback element (itself hard-capped, so it can never strand).
      console.log("[LEARN-AUDIO] speakGreeting() never started — watchdog fallback");
      this._bootMark("speech-start-stalled", { beat: this.idx });
      try {
        const gsW = resident.viewer && resident.viewer.greetingService;
        if (gsW && typeof gsW.stop === "function") gsW.stop();
      } catch (_) {}
      if (seq === this._speechSeq && token === this._runToken) {
        await this._playFallbackClip(data.audio_base64, text, token, onStart);
      }
      return;
    }
    if (outcome.kind === "end-ceiling") {
      // Audio started but the promise never resolved long past any plausible
      // clip end. Treat as a natural end: flush cues so the board reaches its
      // final state, release the beat loop. Nothing is paused — by this point
      // nothing is audibly playing anymore.
      console.log("[LEARN-AUDIO] speakGreeting() end ceiling — releasing the line");
      this._bootMark("speech-end-ceiling", { beat: this.idx });
      spoke = true;
    } else {
      spoke = !!outcome.spoke;
      console.log("[LEARN-AUDIO] speakGreeting() resolved spoke=" + spoke);
    }
    // Task #347 — natural end of the spoken line: flush still-pending timed
    // cues so the board reaches its final state (projectAt(∞) equivalent).
    // Interrupts never get here (endLine("interrupt") already ran, flush-free).
    if (spoke && seq === this._speechSeq && token === this._runToken) {
      try { this._cues.endLine("ended"); } catch (_) {}
    }
    // The lip-synced path DECLINED (viewer not ready / playback failed) and we
    // were NOT interrupted — guarantee the learner still HEARS the line by
    // playing the SAME clip through an isolated <audio> element. This is the
    // documented fallback contract of ResidentLegend.speakGreeting (it returns
    // false when it didn't speak) and never touches the sacred audio pipeline.
    if (!spoke && seq === this._speechSeq && token === this._runToken) {
      console.log("[LEARN-AUDIO] spoken path declined — isolated audio fallback");
      await this._playFallbackClip(data.audio_base64, text, token, onStart);
    }
  }

  // Isolated fallback player: plays a base64 mp3 through a throwaway <audio>
  // element (NOT GreetingService / A2F / the shared TTS AudioContext). Resolves
  // at end-of-audio so it paces the beat loop just like the spoken path; a hard
  // ceiling guarantees it can never hang the lecture if decoding fails.
  _playFallbackClip(b64, text, token, onStart) {
    return new Promise((resolve) => {
      const src = "data:audio/mpeg;base64," + b64;
      // Prefer the persistent <audio> element we blessed INSIDE the Begin-gate
      // gesture. Safari/iOS unlocks audio PER-ELEMENT, so only THIS element may
      // play once the gesture call stack has unwound (a throwaway `new Audio()`
      // is silently blocked, which is exactly why the lesson was silent on
      // Safari). Falls back to a throwaway element when none was blessed.
      let el = this._narrationEl;
      try {
        if (!el) {
          el = new Audio();
          el.preload = "auto";
        }
        el.src = src;
        el.muted = false;
      } catch (_) { resolve(); return; }
      // Generation guard: every clip claims a fresh id. A clip that has been
      // superseded (an interrupt advanced the generation, or a newer overlapping
      // line started on the SAME persistent element) must NOT pause that element
      // or null the live handlers when its own stale timeout/onended finally
      // fires — that is exactly what cut off the next Safari line.
      const gen = (this._fallbackGen = (this._fallbackGen || 0) + 1);
      let done = false;
      let cap = null;
      // Task #284 — drive the SPEAK body animation on this path too. The
      // GreetingService path already calls these AnimationController hooks
      // (onTTSStart/onAudioPlaybackStart/onTTSEnd), but this fallback/Safari
      // path bypassed it entirely, so the teacher stood in idle while
      // narrating. Same hooks, same order; animStarted guards the end call so
      // an interrupt can't fire onTTSEnd for a clip that never animated.
      const ac = (this._resident && this._resident.viewer &&
        this._resident.viewer.animationController) || null;
      let animStarted = false;
      const animEnd = () => {
        if (!animStarted || !ac) return;
        animStarted = false;
        try { ac.onTTSEnd(); } catch (_) {}
      };
      // Task #259 — Safari lip-sync: in ADDITION to the audible clip above,
      // decode the SAME audio and stream it to A2F so the face animates. This is
      // purely additive — it never delays, pauses, or alters the audio element.
      // The driver's lip cursor is anchored to el.currentTime inside
      // _startLessonA2F, so the mouth tracks the audio exactly. Kicked off now
      // (before play) so frames start buffering as early as possible.
      const a2fOn = this._a2fLessonAvailable();
      let a2fGen = 0;
      let framesReady = null;
      if (a2fOn) {
        try {
          this._startLessonA2F(b64, el);
          a2fGen = this._a2fLessonGen;
          // Task #262 — the prepare-then-play gate: resolves once the first
          // lip-frames are buffered (or the stream ends / fails), so we hold the
          // audio start below until the mouth can move on the first word.
          framesReady = this._a2fLessonFramesReady;
        } catch (_) {}
      }
      const finish = () => {
        if (done) return;
        if (gen !== this._fallbackGen) { done = true; resolve(); return; }
        done = true;
        // Task #347 — natural end of this clip: flush pending timed cues so the
        // board reaches final state. On interrupt, _interruptSpeech already ran
        // endLine("interrupt") BEFORE _fallbackStop invoked us (line inactive →
        // this flush is a no-op by contract), so an interrupted clip never
        // fires its tail. A superseded clip (stale gen above) never gets here.
        try { this._cues.endLine("ended"); } catch (_) {}
        try { clearTimeout(cap); } catch (_) {}
        try { el.onended = null; el.onerror = null; el.onplay = null; el.pause(); } catch (_) {}
        if (a2fOn) { try { this._stopLessonA2F(); } catch (_) {} }
        animEnd(); // return the body to idle (Task #284)
        if (this._fallbackAudio === el) this._fallbackAudio = null;
        this._fallbackStop = null;
        resolve();
      };
      // Explicit stop hook for _interruptSpeech: end THIS clip now, clear its
      // timer/handlers, resolve its promise, and bump the generation so any
      // already-scheduled timeout from it is neutralised.
      this._fallbackStop = () => {
        if (done) return;
        done = true;
        this._fallbackGen = (this._fallbackGen || 0) + 1;
        try { clearTimeout(cap); } catch (_) {}
        try { el.onended = null; el.onerror = null; el.onplay = null; el.pause(); } catch (_) {}
        if (a2fOn) { try { this._stopLessonA2F(); } catch (_) {} }
        animEnd(); // return the body to idle (Task #284)
        if (this._fallbackAudio === el) this._fallbackAudio = null;
        this._fallbackStop = null;
        resolve();
      };
      // Cap includes the prepare-then-play gate ceiling so a slow / silent A2F
      // bridge can never let the cap fire mid-speech (or before audio starts).
      cap = setTimeout(finish, this._estimateMs(text) + 4000 +
        (a2fOn ? LESSON_A2F_START_GATE_CEILING_MS : 0));
      el.onended = finish;
      el.onerror = finish;
      // Begin driving buffered face frames the instant the element actually
      // starts playing, so the lip cursor and audio share t=0. Also the first
      // audible moment on this path → clear the warming pill (Task #399).
      el.onplay = () => {
        if (a2fOn) { try { this._onLessonA2FPlay(a2fGen); } catch (_) {} }
        this._announceFirstWords();
      };
      this._fallbackAudio = el;
      // Start playback — and fire the at-voice reaction + chime — the instant
      // audio actually begins. On Safari WITH A2F, hold the start until the first
      // lip-frames are buffered (mirror the lab's prepare-then-play sync gate) so
      // the mouth tracks the voice from the first word, with a hard ceiling so it
      // can never hang. A2F is NOT yet active here (the driver's per-frame writes
      // begin on the element's 'play' event via _onLessonA2FPlay), so a nod fired
      // in onStart still triggers and then RIDES into the start of speech.
      const startPlayback = () => {
        if (done || gen !== this._fallbackGen) return;
        try { if (typeof onStart === "function") onStart(); } catch (_) {}
        // Task #347 — cue clock = THIS element's playhead (the same anchor
        // _startLessonA2F gives the lip cursor, so board cues and the mouth
        // share one clock). Null until the element really plays.
        try {
          this._cues.beginLine(() => {
            const t = el ? el.currentTime : null;
            return typeof t === "number" && isFinite(t) && (t > 0 || !el.paused)
              ? t * 1000 : null;
          });
        } catch (_) {}
        // Task #284 — switch the body to the speak clip as the voice starts,
        // exactly like the GreetingService path does for other browsers.
        if (ac) {
          try {
            ac.onTTSStart();
            ac.onAudioPlaybackStart();
            animStarted = true;
          } catch (_) {}
        }
        try {
          const p = el.play();
          if (p && typeof p.catch === "function") p.catch(() => finish());
        } catch (_) { finish(); }
      };
      if (a2fOn && framesReady && typeof framesReady.then === "function") {
        Promise.race([
          framesReady,
          new Promise((r) => setTimeout(r, LESSON_A2F_START_GATE_CEILING_MS)),
        ]).then(startPlayback);
      } else {
        startPlayback();
      }
    });
  }

  // ---------------- Safari lesson lip-sync (Task #259) ----------------
  // Whether the parallel A2F face-stream should run for this line: standalone
  // /learn, Safari (the only browser that needs the blessed-element audio path),
  // a resident viewer with blendshape controls, and an ARKit-52 model the A2F
  // config recognises. Non-Safari already lip-syncs through GreetingService, so
  // this side stream stays Safari-only to keep the blast radius small. It is
  // purely ADDITIVE — the audible clip plays regardless of whether it runs.
  _a2fLessonAvailable() {
    if (!this._standalone || !this._isSafari()) return false;
    const viewer = this._resident && this._resident.viewer;
    if (!viewer || !viewer.blendShapeControls) return false;
    if (Object.keys(viewer.blendShapeControls).length === 0) return false;
    try { return shouldUseA2FLipSync(viewer.currentModelKey || null); }
    catch (_) { return false; }
  }

  // Lazily create (and reuse) ONE A2F driver wired to the resident model's
  // blendshapes. Prefers the driver GreetingService already created so the model
  // only ever has a single A2F writer; otherwise creates our own and hands it to
  // the greeting service via its public setter so the two never diverge.
  _ensureA2FLessonDriver() {
    if (this._a2fLessonDriver) return this._a2fLessonDriver;
    const viewer = this._resident && this._resident.viewer;
    if (!viewer) return null;
    const gs = viewer.greetingService;
    if (gs && gs.a2fDriver) { this._a2fLessonDriver = gs.a2fDriver; return this._a2fLessonDriver; }
    const controls = viewer.blendShapeControls;
    if (!controls || Object.keys(controls).length === 0) return null;
    let driver = null;
    try {
      driver = new A2FLipSyncDriver({
        blendShapeControls: controls,
        debug: (typeof window !== "undefined" && window.__A2F_DEBUG__ === true),
      });
    } catch (_) { return null; }
    this._a2fLessonDriver = driver;
    try { if (gs && typeof gs.setA2FLipSyncDriver === "function") gs.setA2FLipSyncDriver(driver); } catch (_) {}
    return driver;
  }

  // Tear down the in-flight A2F line stream: stop the pacer, close the bridge
  // socket, end + reset the driver (which zeroes the mouth), drop the audio
  // clock, and bump the generation so any late frame/pacer callback is a no-op.
  // Safe to call repeatedly and when no stream is active.
  _stopLessonA2F() {
    this._a2fLessonActive = false;
    this._a2fLessonGen = (this._a2fLessonGen || 0) + 1;
    if (this._a2fLessonPacerTimer !== null) {
      try { clearTimeout(this._a2fLessonPacerTimer); } catch (_) {}
      this._a2fLessonPacerTimer = null;
    }
    if (this._a2fLessonWs) {
      try { this._a2fLessonWs.close(); } catch (_) {}
      this._a2fLessonWs = null;
    }
    const d = this._a2fLessonDriver;
    if (d) {
      try { d.setAudioClock(null); } catch (_) {}
      try { d.markSessionEnded(); } catch (_) {}
      try { d.reset(); } catch (_) {}
    }
    this._a2fLessonAudioEl = null;
  }

  // Decode a base64 mp3 to 16 kHz / 16-bit / mono PCM bytes for the bridge.
  // Decoding needs NO playback unlock, so it works on Safari even though the
  // audible clip must ride the blessed element. Mirrors GreetingService.
  async _decodeB64ToPCM(b64) {
    const bin = atob(b64 || "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new Ctx({ sampleRate: 16000 });
    let decoded;
    try { decoded = await audioCtx.decodeAudioData(bytes.buffer); }
    finally { try { audioCtx.close(); } catch (_) {} }
    const channelData = decoded.getChannelData(0);
    const int16 = new Int16Array(channelData.length);
    for (let i = 0; i < channelData.length; i++) {
      const s = Math.max(-1, Math.min(1, channelData[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return new Uint8Array(int16.buffer);
  }

  // Forward-pace the bridge-bound PCM copy to ~real-time after a short warm-up
  // (identical policy to the greeting/chat paths) so the bridge never floods and
  // starves the lip-sync queue. The speaker element is never touched here.
  _startLessonA2FPacer(pcmBytes, ws, gen) {
    if (this._a2fLessonPacerTimer !== null) {
      clearTimeout(this._a2fLessonPacerTimer);
      this._a2fLessonPacerTimer = null;
    }
    const total = pcmBytes.length;
    let offset = 0;
    let forwardedMs = 0;
    let engaged = false;
    let nextAllowedWall = 0;
    const sendChunkBytes = (chunk) => {
      let s = "";
      for (let j = 0; j < chunk.length; j++) s += String.fromCharCode(chunk[j]);
      ws.send(JSON.stringify({ type: "audio_base64", data: btoa(s), gen }));
    };
    const kick = () => {
      this._a2fLessonPacerTimer = null;
      while (offset < total) {
        if (!this._a2fLessonActive || this._a2fLessonGen !== gen ||
            ws.readyState !== WebSocket.OPEN) {
          return;
        }
        const now = performance.now();
        const bytes = Math.min(LESSON_A2F_PCM_CHUNK_BYTES, total - offset);
        const chunkMs = bytes / LESSON_A2F_PCM_BYTES_PER_MS;
        if (!LESSON_A2F_PACING_ENABLED || LESSON_A2F_PACING_RATE <= 0) {
          sendChunkBytes(pcmBytes.subarray(offset, offset + bytes));
          offset += bytes;
          continue;
        }
        if (!engaged) {
          if (forwardedMs + chunkMs <= LESSON_A2F_PACING_WARMUP_MS) {
            sendChunkBytes(pcmBytes.subarray(offset, offset + bytes));
            offset += bytes;
            forwardedMs += chunkMs;
            continue;
          }
          engaged = true;
          nextAllowedWall = now;
        }
        if (now >= nextAllowedWall) {
          sendChunkBytes(pcmBytes.subarray(offset, offset + bytes));
          offset += bytes;
          forwardedMs += chunkMs;
          nextAllowedWall = now + chunkMs / LESSON_A2F_PACING_RATE;
          continue;
        }
        this._a2fLessonPacerTimer = setTimeout(kick, Math.max(1, nextAllowedWall - now));
        return;
      }
      if (this._a2fLessonActive && this._a2fLessonGen === gen &&
          ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "end", gen }));
      }
    };
    kick();
  }

  // Start the parallel A2F face stream for one narration line. The audible clip
  // is owned by _playFallbackClip; this only produces face frames and anchors
  // the driver's lip cursor to `el.currentTime`, so the mouth tracks the audio
  // exactly (the audio-clock cursor self-heals the ~1.3s pipeline lead-in — once
  // frames for the current playhead exist they render on the audio with no
  // drift). NEVER pauses the speaker. startPlaybackNow() is deferred to the
  // element's "play" event (see _playFallbackClip).
  _startLessonA2F(b64, el) {
    // Default the prepare-then-play gate to "ready" so a missing driver / early
    // return never blocks the audio start in _playFallbackClip.
    this._a2fLessonFramesReady = Promise.resolve();
    let driver = null;
    try { driver = this._ensureA2FLessonDriver(); } catch (_) { driver = null; }
    if (!driver) return;
    // Clear any prior line's stream first (also resets the driver / mouth).
    this._stopLessonA2F();
    const gen = ++this._a2fLessonGen;
    this._a2fLessonActive = true;
    this._a2fLessonAudioEl = el;
    // Task #262 — frames-ready gate: resolve once enough lip-frames are buffered
    // to cover the start of the clip (or the stream ends / fails), so the caller
    // can hold the audio start until the mouth can move on the first word. It
    // resolves on ANY terminal signal so a dead / silent bridge never delays the
    // voice beyond the caller's ceiling.
    let _framesReadyResolve = null;
    this._a2fLessonFramesReady = new Promise((res) => { _framesReadyResolve = res; });
    let _framesReadyFired = false;
    let _bufferedMaxMs = 0;
    const markFramesReady = () => {
      if (_framesReadyFired) return;
      _framesReadyFired = true;
      try { if (_framesReadyResolve) _framesReadyResolve(); } catch (_) {}
    };
    // Anchor the lip cursor to the blessed element's playback position. Returns
    // null before playback (currentTime not yet finite/advancing) so the driver
    // holds frame 0 instead of racing ahead on the wall clock.
    driver.setAudioClock(() => {
      if (this._a2fLessonGen !== gen) return null;
      const ct = el && el.currentTime;
      return (typeof ct === "number" && Number.isFinite(ct)) ? ct * 1000 : null;
    });

    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const buildWs = async () => {
      let url = `${proto}//${location.host}/a2f-bridge`;
      try {
        const user = window.firebaseAuth && window.firebaseAuth.currentUser;
        if (user) {
          const tok = await user.getIdToken();
          if (tok) url += `?token=${encodeURIComponent(tok)}`;
        }
      } catch (_) {}
      return new WebSocket(url);
    };

    buildWs().then((ws) => {
      if (!this._a2fLessonActive || this._a2fLessonGen !== gen) {
        try { ws.close(); } catch (_) {}
        markFramesReady();
        return;
      }
      this._a2fLessonWs = ws;
      ws.addEventListener("open", () => {
        try {
          ws.send(JSON.stringify({
            type: "start", sampleRate: 16000, channels: 1, bitsPerSample: 16, gen,
          }));
        } catch (_) {}
      });
      ws.addEventListener("message", (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (_) { return; }
        if (this._a2fLessonGen !== gen || !this._a2fLessonActive) return;
        if (msg.type === "started") {
          this._decodeB64ToPCM(b64).then((pcm) => {
            if (!this._a2fLessonActive || this._a2fLessonGen !== gen ||
                ws.readyState !== WebSocket.OPEN) return;
            this._startLessonA2FPacer(pcm, ws, gen);
          }).catch(() => { markFramesReady(); try { ws.close(); } catch (_) {} });
        } else if (msg.type === "a2f_frames") {
          if (msg.gen !== undefined && msg.gen !== gen) return;
          const frames = msg.frames || [];
          if (frames.length) {
            try { driver.enqueueFrames(frames, { autoStart: false }); } catch (_) {}
            // Track buffered coverage and release the audio-start gate once we
            // have enough lead for the mouth to move on the first word.
            const last = frames[frames.length - 1];
            const t = last && typeof last.time_ms === "number" ? last.time_ms : 0;
            if (t > _bufferedMaxMs) _bufferedMaxMs = t;
            if (_bufferedMaxMs >= LESSON_A2F_START_LEAD_MS) markFramesReady();
          }
        } else if (msg.type === "a2f_done") {
          markFramesReady();
          try { driver.markSessionEnded(); } catch (_) {}
          try { ws.close(); } catch (_) {}
        } else if (msg.type === "error") {
          markFramesReady();
          try { ws.close(); } catch (_) {}
        }
      });
      ws.addEventListener("error", () => { markFramesReady(); });
      ws.addEventListener("close", () => {
        markFramesReady();
        if (this._a2fLessonWs === ws) this._a2fLessonWs = null;
      });
    }).catch(() => { markFramesReady(); });
  }

  // Begin driving the buffered face frames the instant the blessed element
  // actually starts playing. Idempotent (startPlaybackNow no-ops once playing).
  _onLessonA2FPlay(gen) {
    if (this._a2fLessonGen !== gen || !this._a2fLessonActive) return;
    const d = this._a2fLessonDriver;
    if (d) { try { d.startPlaybackNow(); } catch (_) {} }
  }

  _estimateMs(text) {
    const words = String(text || "").trim().split(/\s+/).length;
    return Math.max(1800, words * 360);
  }

  // Safari / iOS detection (cached). iPadOS 13+ reports a desktop Mac UA, so we
  // also treat a touch-capable "Macintosh" as Safari.
  _isSafari() {
    if (this.__isSafari != null) return this.__isSafari;
    let s = false;
    try {
      const ua = (navigator && navigator.userAgent) || "";
      const appleWebKit = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
      const iOS = /iphone|ipad|ipod/i.test(ua) ||
        (/macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
      s = appleWebKit || iOS;
    } catch (_) {}
    this.__isSafari = s;
    return s;
  }

  // Bless a persistent <audio> element for narration playback by playing a
  // truly-silent clip on it from WITHIN a user gesture (any early gesture /
  // the tap affordance / topic submit — Task #515) or as the no-gesture
  // auto-start probe. Safari/iOS unlock audio per-element: after this, swapping the
  // element's `.src` and calling .play() AFTER an await (out of the gesture
  // stack) stays audible — which is what every narrated beat does. The previous
  // unlock blessed a THROWAWAY element, so the greeting's own `new Audio()`
  // never inherited the grant and the lesson played silent on Safari. NEVER
  // touches the sacred /lab audio pipeline — this is an isolated <audio> tag.
  blessNarrationAudio() {
    // Task #515 — returns a Promise<boolean>: did the silent clip actually
    // play? Inside a gesture this is the bless (true). OUTSIDE any gesture the
    // stage entry uses the same attempt as the auto-start probe — a resolved
    // play() means the browser allows audible autoplay here (and this element
    // is now blessed either way); a rejection means a gesture is still needed.
    try {
      if (!this._narrationEl) {
        this._narrationEl = new Audio();
        this._narrationEl.preload = "auto";
      }
      const el = this._narrationEl;
      // A tiny, universally-decodable, truly-silent WAV (8 kHz / 8-bit / mono).
      el.src =
        "data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YRAAAACAgICAgICAgICAgICAgICA";
      el.muted = false;
      const p = el.play();
      if (p && typeof p.then === "function") {
        return p.then(
          () => {
            try { el.pause(); el.currentTime = 0; } catch (_) {}
            console.log("[LEARN-AUDIO] narration <audio> blessed (gesture or allowed autoplay)");
            return true;
          },
          () => false,
        );
      }
      // Ancient browsers where play() returns undefined: assume playable.
      console.log("[LEARN-AUDIO] narration <audio> bless attempted (no play promise)");
      return Promise.resolve(true);
    } catch (_) { return Promise.resolve(false); }
  }

  // Task #524 — interaction ledger: a tiny ring of what the learner actually
  // DID (checks with their entry + verdict, hints, resets, reveals, skips) so
  // the chat tutor can speak to recent actions ("you tried 3/8 twice").
  // Session-only, capped, never persisted, carries no identity.
  _logEvent(kind, detail) {
    if (kind === "skip" || kind === "reveal") this._excludeFirstAttempt(kind);
    try {
      const e = Object.assign({ kind: String(kind), beat: this.idx + 1 }, detail || {});
      this._ledger = this._ledger || [];
      this._ledger.push(e);
      if (this._ledger.length > 12) this._ledger.splice(0, this._ledger.length - 12);
    } catch (_) {}
  }

  /** Safe words for the live widget's current state (never throws). */
  _describeCurrentWidget() {
    try {
      const w = this._currentWidget;
      if (!w || typeof w.getState !== "function") return "";
      return describeWidgetState(w.getState() || {});
    } catch (_) { return ""; }
  }

  /** "" when the current board shows the student's own work; "skill" or
   * "practice" when it is displaying the REVEALED answer (strict miss vs.
   * chose-to-see). The ask context must then never treat the live widget
   * state as theirs — it is the answer key. */
  _answerShownNow() {
    try {
      if (!this._revealedBeats || !this._revealedBeats.has(this.idx)) return "";
      const b = this.lesson && this.lesson.beats && this.lesson.beats[this.idx];
      return (b && b.skillCheck && !this._reviewMode) ? "skill" : "practice";
    } catch (_) { return ""; }
  }

  /** The student's last recorded submission on the CURRENT beat (captured at
   * click time in the ledger, i.e. BEFORE any reveal overwrote the widget). */
  _lastTriedLabel() {
    try {
      const L = this._ledger || [];
      for (let i = L.length - 1; i >= 0; i--) {
        const e = L[i];
        if (e && e.kind === "check" && e.beat === this.idx + 1 && e.what) {
          return String(e.what);
        }
      }
    } catch (_) {}
    return "";
  }

  /** reference what the learner has ACTUALLY done (their shading vs. the target,
   * streak, hints, where they are). Compact + human-readable; no identity here.
   * Task #524 — grown into ONE full snapshot: beat kind/title, the on-screen
   * board text (same snapshot the flag pipeline trusts), the activity prompt,
   * widget/game state, skill-check tallies, the interaction ledger and the last
   * few chat turns. The server treats ALL of it as untrusted context
   * (whitelisted + capped in ask_context.py). */
  _buildAskContext() {
    const out = {};
    const lc = {};
    if (this.topic) lc.topic = this.topic;
    if (this.lesson && this.lesson.title && this.lesson.title !== this.topic) {
      lc.lesson = this.lesson.title;
    }
    const beats = this.lesson && this.lesson.beats;
    if (beats) { lc.beat = this.idx + 1; lc.of = beats.length; }
    const beat = beats && beats[this.idx];
    if (beat && beat.kind) lc.kind = String(beat.kind);
    if (beat && beat.title) lc.title = String(beat.title);
    const p = this._currentProblem;
    const g = this._currentGame;
    if (p && (p.prompt || p.title)) lc.prompt = String(p.prompt || p.title);
    else if (g && (g.prompt || g.title)) lc.prompt = String(g.prompt || g.title);
    out.lesson_context = lc;

    const base = {
      attempts: this._lastAttempts || 0,
      streak: this._combo || 0,
      hintsUsed: this._hintsUsed || 0,
      beat: this.idx + 1,
      of: beats ? beats.length : undefined,
    };
    const w = this._currentWidget;
    const shownKind = this._answerShownNow();
    if (w && shownKind) {
      // The board is displaying the REVEALED answer — the live widget state is
      // the answer key, not their work. Ship their last recorded attempt
      // instead, flagged so the server fences the displayed solution.
      const st = { answer_shown: shownKind };
      const tried = this._lastTriedLabel();
      if (tried) st.label = tried;
      out.student_state = Object.assign(st, base);
    } else if (w && typeof w.getState === "function") {
      try {
        const st = w.getState() || {};
        out.student_state = Object.assign({
          value: st.value,
          target: st.target,
          // Builders with a human label win; the shared serializer guarantees
          // array/object/string states still read as words, never silence.
          label: describeWidgetState(st),
        }, base);
      } catch (_) {}
    } else if (g && shownKind) {
      // Same fence as widgets: after a reveal the game's reported state IS
      // the walkthrough/solution, not their work. Ship the last recorded
      // attempt from the ledger instead, flagged for the server.
      const st = { activity: "mini-game", answer_shown: shownKind };
      const tried = this._lastTriedLabel();
      if (tried) st.label = tried;
      out.student_state = Object.assign(st, base);
    } else if (g) {
      // Mini-game beat: the sandboxed game reported its state through the
      // host bridge (refreshed just before the ask in _onAsk).
      let st = null;
      try {
        st = g.host && typeof g.host.lastKnownState === "function"
          ? g.host.lastKnownState() : null;
      } catch (_) {}
      out.student_state = Object.assign({
        activity: "mini-game",
        label: st != null
          ? (describeWidgetState({ value: st }) || "working in the activity")
          : "hasn't made a move in the activity yet",
      }, base);
    }

    // What is actually on the board right now — gives teaching beats real
    // context too (the tutor can reference what's on screen, not just the
    // narration line). EXCEPT when the answer is displayed: the board text is
    // then the answer key, so it is withheld entirely (the server enforces
    // this independently — belt and braces).
    if (shownKind) {
      out.board_omitted = true;
    } else {
      try {
        const b = this._captureBoardContext();
        if (b) out.board = b;
      } catch (_) {}
    }

    try {
      const total = this._scTotalN();
      if (total > 0 && (this._scCorrectN() > 0 || this._scMissList().length > 0)) {
        out.skill_checks = {
          correct: this._scCorrectN(),
          missed: this._scMissList().length,
          total,
        };
      }
    } catch (_) {}
    if (this._ledger && this._ledger.length) out.recent_events = this._ledger.slice(-10);
    if (this._chatTurns && this._chatTurns.length) out.chat_turns = this._chatTurns.slice(-6);
    return out;
  }

  // Task #264 — answer a question carried in from the course page before the
  // lecture begins. Renders beat 0 as silent visual context, opens the chat,
  // then routes the question through the SAME resident-legend ask pipeline as a
  // live interruption. `this.playing` is left true so the post-answer resume
  // control offers to begin the lecture from the top.
  async _askPending(q) {
    q = (q || "").trim();
    if (!q) { await this._goTo(0, { autoplay: true }); return; }
    await this._goTo(0, { autoplay: false });
    this._toggleChatPanel(true);
    this.playing = true;
    const d = this._dom;
    if (d && d.askInput) d.askInput.value = q;
    await this._onAsk();
  }

  async _onAsk() {
    if (this._captureMode) return;
    const d = this._dom;
    const q = (d.askInput.value || "").trim();
    if (!q) return;
    d.askInput.value = "";
    // pause autoplay while we field the question (current line will finish)
    const wasPlaying = this.playing;
    this.playing = false;
    this._syncTransport();
    // Teaching beats auto-advance off the AWAITED narration, so bumping the beat
    // token stops that chain when interrupted. Problem/game beats mount their
    // widget asynchronously and are NOT token-driven — bumping here would cancel
    // a still-loading widget and strand the card. So only bump for teaching beats.
    const curBeat = this.lesson && this.lesson.beats[this.idx];
    const interactive = !!(curBeat &&
      (curBeat.kind === "problem" || curBeat.kind === "game"));
    if (!interactive) this._runToken++;
    const token = this._runToken;
    this._interruptSpeech();

    this._appendAsk(q);
    let line = "";
    try {
      const ctxBeat = this.lesson && this.lesson.beats[this.idx];
      // Task #524 — a live mini-game holds its state inside the sandboxed
      // iframe; refresh the host's copy so the ask sees the board as it is now.
      try {
        const gh = this._currentGame && this._currentGame.host;
        // Skip the refresh when the answer is displayed — the game would just
        // report the solution state, which the fenced branch ignores anyway.
        if (gh && typeof gh.requestState === "function" &&
            !this._answerShownNow()) await gh.requestState(500);
      } catch (_) {}
      const askCtx = this._buildAskContext();
      const res = await fetch(API.ask, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          topic: this.topic,
          character_name: this.character,
          context: ctxBeat && ctxBeat.say ? ctxBeat.say : "",
          lesson_context: askCtx.lesson_context,
          student_state: askCtx.student_state,
          board: askCtx.board,
          board_omitted: askCtx.board_omitted,
          skill_checks: askCtx.skill_checks,
          recent_events: askCtx.recent_events,
          chat_turns: askCtx.chat_turns,
        }),
      });
      const data = await res.json();
      line = (data && data.line) || "";
    } catch (_) {
      line = "Let me come back to that — stay with me a moment.";
    }
    if (token !== this._runToken) return;
    // Task #524 — remember the exchange (session-only ring) so follow-up
    // questions carry the last few turns. Recorded only once the answer will
    // actually be spoken (an interrupting navigation drops the pair).
    try {
      this._chatTurns = this._chatTurns || [];
      this._chatTurns.push(
        { who: "student", text: String(q).slice(0, 240) },
        { who: "tutor", text: String(line).slice(0, 240) });
      if (this._chatTurns.length > 8) {
        this._chatTurns.splice(0, this._chatTurns.length - 8);
      }
    } catch (_) {}
    if (this._activeProblem) this._pulseHighlight();
    await this._speak(line, token);
    if (token !== this._runToken) return;
    // offer to resume the lecture
    this._showResume(wasPlaying);
  }

  async _runProblem(beat, token) {
    this.playing = false;
    this._syncTransport();
    // Task #353 — the clock holds while the check is open (released on resume).
    this._holdClockForInteraction();

    // Mount the interactive IMMEDIATELY — never behind speech. The line is
    // narrated in parallel (non-blocking) AND streamed into the chat rail, so a
    // slow / blocked / never-resolving audio clip can no longer trap the learner
    // on a "Getting this ready…" slot. (The sacred audio path itself is
    // untouched — we just stop awaiting it before showing the activity.)
    const card = this._appendCard("problem");

    // Task #301 — the problem is embedded IN the stored lesson version
    // (generated once, filed with the lesson, replays identically forever).
    // Render it directly — no network, no re-roll. Checked BEFORE the authored
    // widget path only in that embedded problems carry a full template spec.
    if (!beat.widget && beat.problem && typeof beat.problem === "object" &&
        beat.problem.spec) {
      this._speak(beat.say, token); // narrate alongside (do NOT await)
      this._renderProblemCard(card, beat.problem);
      return;
    }

    // Task #251 — authored fraction widget. When a problem beat carries an
    // inline `widget` spec we render a deterministic, client-graded fraction
    // activity (shade / build / numberline / compare) instead of asking the
    // server to generate a templated problem. This is synchronous — no network.
    if (beat.widget && typeof beat.widget === "object") {
      const w = beat.widget;
      const synthProblem = {
        title: w.title || "",
        // Bug fix (v2 handoff #1): most authored widgets carry no `prompt` —
        // the question text lives on the beat as `goal` (or `say`). Without
        // this fallback the card renders with no question at all.
        prompt: w.prompt || beat.goal || beat.say || "",
        coach: w.coach || {},
        template: "fraction",
        difficulty: beat.difficulty || "intro",
        skillCheck: !!beat.skillCheck,
        // Server-graded skill-checks: a GRADABLE widget's secrets are stripped
        // from the shipped beat, so we post the value keyed by beat index and
        // let /api/learn/grade decide. Eligibility comes from the one grader
        // table (graders.mjs isGradable) — the same predicate the server strips
        // and grades by, so lockstep is structural, not maintained.
        gradeKey: (beat.skillCheck && isGradable(w.type)) ? this.idx : null,
        // Brilliant tap-to-shade / area model: clean centered chrome (just
        // prompt + shape + Check), no kicker/title/Skip. Area-graded widgets.
        minimalChrome:
          (w.type === "shade" &&
            !!(w.layout || (Array.isArray(w.regions) && w.regions.length))) ||
          w.type === "overlapGrid",
      };
      // Skill-check mode: the tutor stands down — no narration, no hint/reveal.
      // During a REVIEW pass the question is re-taught, so skill behavior is off
      // and the tutor narrates normally (server-side grading via gradeKey stays).
      const inSkill = beat.skillCheck && !this._reviewMode;
      if (inSkill) {
        if (this._dom && this._dom.root) this._dom.root.dataset.skillcheck = "1";
        this._setFocus("board");
      }
      this._renderProblemCard(card, synthProblem, (p, el, cbs) =>
        renderFractionWidget(el, w, cbs));
      if (!inSkill) this._speak(beat.say, token); // narrate alongside (do NOT await)
      return;
    }

    card.innerHTML =
      '<div class="ts-loading"><span class="ts-spin"></span>' +
      '<span>Setting up a quick one to try…</span></div>';
    this._scrollToCurrent();
    this._speak(beat.say, token); // narrate alongside (do NOT await)

    let problem = null;
    try {
      const response = await this._contentJson(API.problemGenerate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: this.topic,
          difficulty: beat.difficulty || "intro",
          character_name: this.character,
          // Task #301 — pin to the served lesson version so the problems
          // endpoint serves the stored problem for THIS version when the
          // beat predates embedding (older stored lessons).
          lesson_key: this._lessonKey || undefined,
          lesson_version: this._lessonVersion || undefined,
          beat_id: beat.id || undefined,
          capture: this._captureMode === true,
        }),
      });
      const data = response.data;
      problem = data && data.problem;
    } catch (_) {}
    if (token !== this._runToken) return;

    if (!problem) {
      card.innerHTML =
        '<div class="ts-prob-head"><span class="ts-kicker">Try it</span>' +
        '<h3>That one slipped away</h3></div>' +
        '<p class="ts-prob-skip-note">No matter — let\'s carry on.</p>';
      const cont = document.createElement("button");
      cont.className = "ts-btn ts-btn-primary";
      cont.textContent = "Continue →";
      cont.addEventListener("click", () => this._resumeAfterProblem());
      card.appendChild(cont);
      this._filmAutoResume(token); // film: never strand the take on a failure card
      return;
    }

    this._renderProblemCard(card, problem);
  }

  // Task #353 (Trap 10) — film capture: hands-free resume for slots whose
  // resolution is already on screen (failure cards, skipped games). Fixed gap,
  // token-guarded; a no-op outside ?film=1.
  _filmAutoResume(token) {
    if (!this._filmMode) return;
    (async () => {
      await this._sleep(FILM_BEAT_GAP_MS, token);
      if (token !== this._runToken) return;
      this._resumeAfterProblem();
    })();
  }

  // Render a ready problem object (template path) into a card + grade it locally
  // via its trusted widget. Shared by _runProblem and the mini-game fallback.
  _renderProblemCard(card, problem, renderFn) {
    const token = this._runToken;
    // Start from a neutral card: clear any feedback/chrome state left over from a
    // previously-solved slot so back / replay / review navigation never inherits
    // stale border-glow or minimal-chrome styling.
    card.classList.remove(
      "is-correct", "is-wrong", "is-strict",
      "ts-flash-right", "ts-flash-wrong", "ts-card-minimal"
    );
    // During a review pass a skill-check question is replayed as a teaching
    // problem (hints + tutor speech + retry), so it is NOT counted again.
    const reviewing = !!problem.skillCheck && this._reviewMode;
    const skill = !!problem.skillCheck && !this._reviewMode;
    // Task #524 (hardening) — a freshly mounted widget displays NOTHING
    // revealed, whatever happened on an earlier visit to this beat (rebuilds
    // and the review pass re-mount blank widgets). The flag re-arms only via
    // the reveal call sites below.
    if (this._revealedBeats) this._revealedBeats.delete(this.idx);
    const kicker = skill ? "Skill check · No hints"
      : (reviewing ? ("Review · " + this.character) : (this.character + " · Try it"));
    let dotsHTML = "";
    if (skill) {
      const pos = this._skillPos(this.idx);
      let dots = "";
      for (let i = 1; i <= pos.total; i++) {
        const st = i < pos.n ? " done" : (i === pos.n ? " on" : "");
        dots += '<span class="ts-scdot' + st + '"></span>';
      }
      dotsHTML = '<div class="ts-scdots" aria-label="Question ' + pos.n + ' of ' +
        pos.total + '">' + dots + "</div>";
    }
    // Brilliant tap-to-shade chrome: just the prompt, the shape, an optional
    // "Start over" link (teaching only), and a centered Check disabled until the
    // learner shades something. No kicker, no title, no Skip.
    const minimal = !!problem.minimalChrome;
    if (minimal) {
      card.classList.add("ts-card-minimal");
      card.innerHTML =
        dotsHTML +
        '<p class="ts-prob-prompt ts-prob-prompt-center">' +
        this._esc(problem.prompt || problem.title || "") + "</p>" +
        '<div class="ts-prob-widget"></div>' +
        (skill ? "" :
          '<div class="ts-prob-reset-row"><button class="ts-prob-reset" type="button">' +
          "\u21ba Start over</button></div>") +
        '<div class="ts-prob-foot ts-prob-foot-center">' +
        '<button class="ts-btn ts-btn-primary ts-prob-check" type="button" data-demo-id="lesson-check" disabled>Check</button>' +
        "</div>";
    } else {
      card.innerHTML =
        dotsHTML +
        '<div class="ts-prob-head"><span class="ts-kicker">' + this._esc(kicker) + "</span>" +
        "<h3>" + this._esc(problem.title || this.topic) + "</h3></div>" +
        '<p class="ts-prob-prompt">' + this._esc(problem.prompt || "") + "</p>" +
        '<div class="ts-prob-widget"></div>' +
        '<div class="ts-prob-foot">' +
        (skill ? "" : '<button class="ts-btn ts-btn-ghost ts-prob-skip" type="button">Skip</button>') +
        '<button class="ts-btn ts-btn-primary ts-prob-check" type="button" data-demo-id="lesson-check" disabled>Check</button>' +
        "</div>";
    }

    const widgetEl = card.querySelector(".ts-prob-widget");
    const checkBtn = card.querySelector(".ts-prob-check");
    const skipBtn = card.querySelector(".ts-prob-skip");
    const resetBtn = card.querySelector(".ts-prob-reset");
    let attempts = 0;
    let solved = false;

    const widget = (renderFn || renderProblem)(problem, widgetEl, {
      // Re-evaluate the Check gate on every interaction so it disables again
      // when the learner clears their selection (Brilliant behaviour).
      onInteract: () => {
        if (checkBtn) checkBtn.disabled = !(widget && widget.isComplete && widget.isComplete());
        // Quiet, attentive "I'm with you" (throttled) + reset the stall nudge so
        // a working learner is never told they've gone quiet.
        this._emit("student_working");
        if (this._activeProblem) this._armStall();
      },
      onComplete: () => { if (checkBtn) checkBtn.disabled = false; },
    });
    // Keep a handle on the live widget + its problem so the chat can show Newton
    // the learner's actual work (FractionWidgets.getState()); cleared on nav.
    this._currentWidget = widget || null;
    this._currentProblem = problem || null;
    this._currentGame = null; // a template fallback replaces the game outright
    this._lastAttempts = 0;
    if (widget && widget.isComplete && widget.isComplete()) checkBtn.disabled = false;
    else if (checkBtn) checkBtn.disabled = true;

    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        if (solved) return;
        try { if (widget && typeof widget.reset === "function") widget.reset(); } catch (_) {}
        if (checkBtn) checkBtn.disabled = !(widget && widget.isComplete && widget.isComplete());
        this._logEvent("reset");
      });
    }

    // Hints / reveal assist — teaching problems only. Skill checks get no help.
    if (!skill) {
      this._setActiveProblem({
        why: () => {
          this._hintsUsed = (this._hintsUsed || 0) + 1;
          this._emit("hint_requested");
          this._logEvent("hint");
          const line = (problem.coach && (problem.coach.hint || problem.coach.why)) ||
            "Look closely at what you're given — what changes, and what stays the same?";
          this._speakAfterReaction(line, this._runToken);
        },
        reveal: () => {
          if (solved) return;
          solved = true;
          this._clearActiveProblem();
          this._emit("answer_revealed");
          this._logEvent("reveal");
          if (this._revealedBeats) this._revealedBeats.add(this.idx);
          try { if (widget && typeof widget.reveal === "function") widget.reveal(); } catch (_) {}
          const line = (problem.coach && (problem.coach.walkthrough || problem.coach.success)) ||
            "Here's the answer — let me walk you through how it comes out.";
          this._speakAfterReaction(line, this._runToken);
          this._finishProblemCard(card, checkBtn, skipBtn, "Continue the lesson \u2192");
        },
      });
    }

    if (skipBtn) {
      skipBtn.addEventListener("click", () => {
        if (solved) return;
        this._logEvent("skip");
        this._resumeAfterProblem();
      });
    }

    const onCorrect = () => {
      solved = true;
      this._clearActiveProblem();
      // A correct board shows THEIR work, never a reveal.
      if (this._revealedBeats) this._revealedBeats.delete(this.idx);
      if (skill) {
        (this._scCorrectSet || (this._scCorrectSet = new Set())).add(this.idx);
        if (this._scMissSet) this._scMissSet.delete(this.idx);
      }
      this._combo = (this._combo || 0) + 1;
      card.classList.remove("ts-flash-wrong", "is-wrong", "is-strict");
      card.classList.add("ts-flash-right", "is-correct");
      const praise = this._praise();
      const line = this._housePraise((problem.coach && problem.coach.success) || praise);
      // Task #262 (revised per user feedback) — the verdict (nod + smile + chime)
      // fires INSTANTLY on grading so the reward feels immediate; the spoken line
      // then follows and the head gesture rides into the first moment of speech
      // (react-then-speak, head-bone only). A skill check has no spoken line.
      this._emit("answer_correct");
      this._sfx("correct");
      if (!skill) {
        this._speakAfterReaction(line, this._runToken);
      }
      this._assess(true, problem, attempts);
      if (this._captureMode) this._showFeedback("correct", praise);
      else this._rewardCorrect(praise);
      this._finishProblemCard(card, checkBtn, skipBtn);
    };

    const onWrong = (revealAnswer, echo) => {
      this._combo = 0;
      card.classList.remove("ts-flash-wrong");
      void card.offsetWidth;
      card.classList.add("ts-flash-wrong");
      if (skill && this._captureMode) {
        // Capture grading is verdict-only: never request or reveal the
        // server-held answer/echo. Keep the real Check path and visual miss,
        // but leave the unsolved widget retryable for the take.
        this._emit("answer_wrong");
        this._sfx("incorrect");
        card.classList.add("is-wrong");
        this._showFeedback("wrong", "Incorrect \u2014 try again.");
        checkBtn.disabled = !(widget && widget.isComplete && widget.isComplete());
      } else if (skill) {
        // Strict, single-shot: reveal the answer, then move on. No retry —
        // but a "Why?" affordance explains the miss on demand. The shake +
        // chime fire now (there is no spoken line to sync them to).
        this._emit("answer_wrong");
        this._sfx("incorrect");
        solved = true;
        this._clearActiveProblem();
        (this._scMissSet || (this._scMissSet = new Set())).add(this.idx);
        this._assess(false, problem, attempts);
        if (this._revealedBeats) this._revealedBeats.add(this.idx);
        try { if (widget && typeof widget.reveal === "function") widget.reveal(revealAnswer); } catch (_) {}
        card.classList.add("is-strict");
        // Task #354 — echo: feedback written for THIS distractor (server-held,
        // arrived with the wrong verdict) replaces the generic miss message.
        const echoText = echo && typeof echo.text === "string" && echo.text
          ? this._subTokens(echo.text) : null;
        this._showFeedback("strict", echoText || "Incorrect");
        this._finishProblemCard(card, checkBtn, skipBtn);
        this._addWhyButton(card, problem);
        if (echo && typeof echo.say === "string" && echo.say) {
          // The legend speaks the per-distractor line (react-then-speak), and
          // where authored, a depth-1 remedy beat follows before the student
          // moves on. A navigation (Continue / segment click) bumps the run
          // token and quietly cancels the detour — the main line always wins.
          const t = this._runToken;
          (async () => {
            await this._speakAfterReaction(echo.say, t);
            if (t !== this._runToken) return;
            if (echo.remedy && typeof echo.remedy === "object") {
              this._playRemedyBeat(echo.remedy, t);
            }
          })();
        }
      } else {
        // Teaching miss — retry in place, rotating encouragement, Get-help offer.
        card.classList.add("is-wrong");
        const enc = this._encourage();
        const line = (problem.coach && problem.coach.retry) || enc;
        // Task #262 (revised) — shake "no" + chime fire INSTANTLY, then the
        // spoken encouragement follows (react-then-speak; head rides into speech).
        this._emit("answer_wrong");
        this._sfx("incorrect");
        this._speakAfterReaction(line, this._runToken);
        this._showFeedback("wrong", enc);
        this._ensureHelpButton(card);
        // Task #491 — after three genuine misses the quiet reveal affordance
        // appears (Brilliant's measured ladder). Skill checks are single-shot
        // strict and never reach this branch, so their rules are untouched.
        if (attempts >= 3) this._ensureSeeAnswerButton(card);
      }
    };

    checkBtn.addEventListener("click", async () => {
      if (!widget || solved || checkBtn.disabled || token !== this._runToken) return;
      card.classList.remove("is-wrong"); // clear any prior amber before re-grading
      const res = widget.check ? widget.check() : { correct: false };
      attempts += 1;
      // Task #524 — capture what they submitted AT CLICK TIME (a strict miss
      // reveals the answer into the widget right after grading, which would
      // otherwise overwrite the very entry the ledger should remember).
      const tried = this._describeCurrentWidget();
      // Task #354 — student tokens: remember what they answered (SESSION-ONLY;
      // dies with the lesson, never persisted). Board text renders it via
      // {{student.answer|…}}; narration never can.
      this._noteAnswerToken(problem, res);
      let correct = !!res.correct;
      let serverAnswer = null;
      let echo = null;
      // Server-graded beats (numberTile skill-check): the verdict is decided on
      // the server from the authored answer — the client copy is stripped.
      if (problem.gradeKey != null) {
        checkBtn.disabled = true;
        const verdict = await this._gradeOnServer(problem.gradeKey, res.value);
        if (token !== this._runToken || solved) return;
        checkBtn.disabled = false;
        if (verdict.unknown) {
          this._firstAttempts?.record(this.idx, null);
          this._showFeedback("wrong", "The answer could not be checked. Try again.");
          return;
        }
        correct = !!verdict.correct;
        serverAnswer = verdict.answer;
        echo = verdict.echo || null; // Task #354 — arrives ONLY on a miss (Trap 5)
      }
      this._recordFirstAttempt(problem.gradeKey != null || typeof res.correct === "boolean"
        ? correct : null);
      // Task #524 — one ledger line per submission, server verdicts included.
      this._logEvent("check", {
        what: tried,
        verdict: correct ? "right" : "wrong",
        skill: skill || undefined,
      });
      if (correct) onCorrect();
      else onWrong(serverAnswer, echo);
    });

    // Task #353 (Trap 10) — film capture: the take answers for itself after a
    // fixed beat. The widget's deterministic default state is submitted
    // through the REAL check path (server grading included); a wrong verdict
    // becomes a QUIET reveal — no miss recorded, no strict chrome, no red
    // flash — so the feed stays clean and the lesson films end to end.
    // Grading for real learners is untouched: this branch runs only under
    // ?film=1.
    if (this._filmMode) {
      const t = this._runToken;
      (async () => {
        await this._sleep(FILM_BEAT_GAP_MS, t);
        if (t !== this._runToken || solved) return;
        const res = widget && widget.check ? widget.check() : { correct: false };
        let correct = !!res.correct;
        let serverAnswer = null;
        if (problem.gradeKey != null) {
          const verdict = await this._gradeOnServer(problem.gradeKey, res.value);
          if (t !== this._runToken || solved) return;
          correct = !!verdict.correct;
          serverAnswer = verdict.answer;
        }
        if (correct) {
          onCorrect();
        } else {
          solved = true;
          this._clearActiveProblem();
          if (this._revealedBeats) this._revealedBeats.add(this.idx);
          try { if (widget && typeof widget.reveal === "function") widget.reveal(serverAnswer); } catch (_) {}
          this._finishProblemCard(card, checkBtn, skipBtn);
        }
        await this._sleep(FILM_BEAT_GAP_MS, t);
        if (t === this._runToken) this._resumeAfterProblem();
      })();
    }

    this._scrollToCurrent();
  }

  // Swap the Check/Skip footer for a single Continue once a problem resolves.
  _finishProblemCard(card, checkBtn, skipBtn, label) {
    if (checkBtn) checkBtn.style.display = "none";
    if (skipBtn) skipBtn.style.display = "none";
    this._hideAssistGhosts(card);
    const resetRow = card.querySelector(".ts-prob-reset-row");
    if (resetRow) resetRow.style.display = "none";
    const foot = card.querySelector(".ts-prob-foot");
    if (!foot || foot.querySelector(".ts-prob-continue")) return;
    const cont = document.createElement("button");
    cont.className = "ts-btn ts-btn-primary ts-prob-continue";
    cont.textContent = label || "Continue \u2192";
    cont.addEventListener("click", () => { this._sfx("continue"); this._resumeAfterProblem(); });
    foot.appendChild(cont);
  }

  // Post the chosen value to the server, which holds the authored answer (never
  // shipped for these beats). Returns {correct, answer}. Fails closed.
  async _gradeOnServer(beatIndex, value) {
    try {
      const response = await this._contentJson(API.grade, {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, this._authHeaders()),
        body: JSON.stringify({
          courseId: this.courseId || "",
          lessonId: this.lessonId || "",
          beatIndex,
          value,
          capture: this._captureMode === true,
        }),
      });
      const res = response.res;
      const data = response.data;
      return {
        unknown: !res.ok || !data || typeof data.correct !== "boolean",
        correct: !!(data && data.correct),
        answer: data ? data.answer : null,
        // Task #354 — per-distractor echo feedback rides the wrong verdict
        // (server-held mapping; never present in the lesson payload).
        echo: (data && data.echo) || null,
      };
    } catch (_) {
      return { correct: false, answer: null, echo: null, unknown: true };
    }
  }

  _authHeaders() {
    const h = {};
    try { if (window.__lastIdToken) h.Authorization = "Bearer " + window.__lastIdToken; } catch (_) {}
    return h;
  }

  /** Task #353 — the HOLD beat: an ungraded predict-pause inside the timed
   * performance. The legend turns to the student (direction, set in _goTo),
   * the clock holds (counted — released on resume), the student commits to a
   * prediction, and only then does the SERVER-HELD reveal arrive (Trap 5:
   * /api/learn/hold-commit — the payoff can't be read out of the lesson
   * payload). There is no verdict anywhere in this path — predictions are
   * NEVER punished; every option gets the same warm reveal. */
  async _runHold(beat, token) {
    const beatIndex = this.idx;
    this.playing = false;
    this._syncTransport();
    this._holdClockForInteraction();

    const card = this._appendCard("hold");
    card.classList.remove("is-correct", "is-wrong", "is-strict",
      "ts-flash-right", "ts-flash-wrong", "ts-card-minimal", "is-revealed");
    const handle = renderHoldCardInto(card, beat, {
      esc: (s) => this._esc(s),
      kicker: this.character + " \u00b7 Make a prediction",
      onSelect: () => {
        this._emit("student_working");
        if (this._activeProblem) this._armStall();
      },
      commit: async (choice) => {
        this._emit("student_working");
        // Task #354 — student tokens: remember the committed prediction
        // (SESSION-ONLY). Later boards may reference {{student.prediction|…}}.
        try {
          const opts = Array.isArray(beat.options) ? beat.options : [];
          const o = opts[choice];
          const label = typeof o === "string" ? o : (o && (o.text || o.label));
          if (label) (this._tokenValues || (this._tokenValues = {})).prediction = String(label);
        } catch (_) {}
        try {
          const response = await this._contentJson(API.holdCommit, {
            method: "POST",
            headers: Object.assign({ "Content-Type": "application/json" },
              this._authHeaders()),
            body: JSON.stringify({
              courseId: this.courseId || "",
              lessonId: this.lessonId || "",
              beatIndex: this.idx,
              choice, // acknowledged, never judged (echo lives on graded checks)
              capture: this._captureMode === true,
            }),
          });
          const data = response.data;
          if (token !== this._runToken || this.idx !== beatIndex) return null;
          if (this._captureMode) {
            // Never consume a reveal even if an older/misconfigured server
            // accidentally includes one on a capture response.
            return data && data.success && data.captured
              ? { text: "Prediction recorded." }
              : { text: "Prediction locked for this take." };
          }
          return (data && data.success && data.reveal) ? data.reveal : null;
        } catch (_) {
          return this._captureMode
            ? { text: "Prediction could not be recorded. Reset this take to try again." }
            : null; // normal lesson degrades softly to the renderer fallback
        }
      },
      onRevealShown: (reveal) => {
        if (token !== this._runToken || this.idx !== beatIndex) return;
        if (this._captureMode) {
          const kicker = card.querySelector(".ts-hold-reveal-kicker");
          if (kicker) kicker.textContent = "Prediction locked";
        }
        this._clearActiveProblem();
        const t = this._runToken;
        const say = reveal && reveal.say;
        if (this._filmMode) {
          // Film (Trap 10): let the spoken payoff finish, then roll on.
          (async () => {
            if (say) await this._speak(say, t);
            await this._sleep(FILM_BEAT_GAP_MS, t);
            if (t === this._runToken) this._resumeAfterProblem();
          })();
        } else if (say) {
          this._speak(say, t); // spoken payoff alongside the reveal text
        }
      },
      onContinue: () => { this._sfx("continue"); this._resumeAfterProblem(); },
    });
    this._holdHandle = handle;
    // Block a stray action-bar Continue while the pause is open and arm the
    // stall nudge — but NO assist rail: a prediction has no hints and no
    // reveal-on-demand (the payoff arrives only through commit, Trap 5).
    this._activeProblem = { hold: true };
    this._armStall();
    this._scrollToCurrent();
    this._speak(beat.say, token); // narrate alongside (do NOT await)

    // Film capture (Trap 10): the take commits for itself — deterministically
    // the FIRST option — through the REAL commit → reveal round trip; the
    // resume is driven by onRevealShown above once the reveal lands.
    if (this._filmMode) {
      await this._sleep(FILM_BEAT_GAP_MS, token);
      if (token !== this._runToken || handle.isCommitted()) return;
      handle.selectOption(0);
      await this._sleep(400, token);
      if (token !== this._runToken || handle.isCommitted()) return;
      const btn = card.querySelector(".ts-hold-commit");
      if (btn && !btn.disabled) btn.click();
    }
  }

  /** v9 — the authored custom widget: the lesson-maker ships a COMPLETE
   * little web page (html/css/js, full flexibility) and the stage performs
   * it EXCLUSIVELY inside a null-origin sandboxed iframe. THE BOUNDARY IS
   * SACRED: sandbox="allow-scripts" and NOTHING else — never add
   * allow-same-origin / allow-top-navigation / allow-popups, and never
   * render beat.html outside this iframe (it is the ONE string the store's
   * banned-pattern lint exempts, on exactly this guarantee). The widget
   * talks back over a tiny postMessage bridge: {legendWidget: 1, event:
   * "ready"|"resize"|"progress"|"complete", height?, correct?} — see
   * docs/LESSON_SCHEMA.md §4.5. */
  async _runCustom(beat, token) {
    const beatIndex = this.idx;
    this.playing = false;
    this._syncTransport();
    this._holdClockForInteraction();

    const card = this._appendCard("custom");
    card.classList.add("ts-card-custom");
    const ceremony = isCustomCeremonyBeat(beat);
    card.classList.toggle("ts-card-ceremony", ceremony);
    card.classList.toggle("ts-card-problem", !ceremony);
    // Player ceremony Iteration 2 (B2) — an authored celebration beat plays
    // over the WHOLE stage, not just inside its card (the widget keeps its
    // own card-level confetti).
    if (ceremony && this._bigCeremonyAllowed()) this._playStageCelebration("beat");
    const gate = beat.gate === "complete";
    const clampH = (h) => Math.max(240, Math.min(1600, (h | 0) || 420));

    card.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "ts-custom-wrap";
    if (beat.title) {
      const h = document.createElement("div");
      h.className = "ts-custom-title";
      h.textContent = String(beat.title);
      wrap.appendChild(h);
    }
    const mount = document.createElement("div");
    mount.className = "ts-custom-mount";
    wrap.appendChild(mount);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ts-btn ts-btn-primary ts-custom-continue";
    btn.textContent = "Continue";
    btn.disabled = gate;
    wrap.appendChild(btn);
    card.appendChild(wrap);

    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-scripts"); // the wall — never widen
    iframe.setAttribute("title",
      beat.title ? String(beat.title) : "Interactive activity");
    iframe.className = "ts-custom-frame";
    // Height contract (Task #491): the frame always honors the LARGER of the
    // authored height and the widget's own live report, so a stale small
    // number on either side can never sever the board mid-line.
    const authoredH = clampH(beat.height);
    iframe.style.height = authoredH + "px";

    let done = !gate; // gate:"complete" locks Continue until the bridge says so
    let booted = false;
    let attempted = false; // the widget reported real learner activity
    let rewarded = false;  // XP paid once, on the first genuine completion
    const unlock = () => { done = true; btn.disabled = false; };

    // Task #491 — explicit skip instead of the old silent 120s force-unlock:
    // the learner always ACTS to move past an unsolved check. Offered a short
    // think after the first real attempt; a booted-but-mute widget gets a
    // clearly-labeled offer after a much longer wait (armed below).
    let skipEl = null;
    let skipTimer = null;
    const removeSkipOffer = () => {
      if (skipTimer) { clearTimeout(skipTimer); skipTimer = null; }
      if (skipEl) { try { skipEl.remove(); } catch (_) {} skipEl = null; }
    };
    const offerSkip = (label) => {
      if (done || skipEl) return;
      skipEl = document.createElement("button");
      skipEl.type = "button";
      skipEl.className = "ts-btn ts-btn-ghost ts-custom-skip";
      skipEl.textContent = label;
      skipEl.addEventListener("click", () => {
        if (done) return;
        this._excludeFirstAttempt("skip", beatIndex);
        this._sfx("skip");
        unlock();
        finish(); // skipping pays no XP — only a real `complete` awards
      });
      wrap.insertBefore(skipEl, btn);
    };
    const armSkipOffer = () => {
      if (!gate || done || skipEl || skipTimer) return;
      skipTimer = setTimeout(() => {
        skipTimer = null;
        offerSkip("Skip for now");
      }, SKIP_OFFER_AFTER_TRY_MS);
    };

    const onMsg = (e) => {
      if (token !== this._runToken || this.idx !== beatIndex) return;
      if (!iframe.contentWindow || e.source !== iframe.contentWindow) return;
      const m = e.data;
      if (!m || typeof m !== "object" || m.legendWidget !== 1) return;
      if (m.event === "ready") {
        booted = true;
        iframe.contentWindow.postMessage({
          legendWidget: 1, event: "context", context: this._widgetCompletionContext(),
        }, "*");
      }
      else if (m.event === "resize" && typeof m.height === "number") {
        // Growth is accepted whenever it arrives — including reports long
        // after load — but never below the authored floor (Task #491).
        iframe.style.height = Math.max(authoredH, clampH(m.height)) + "px";
      } else if (m.event === "progress") {
        booted = true;
        if (!attempted) { attempted = true; armSkipOffer(); }
        this._emit("student_working");
        if (this._activeProblem) this._armStall();
      } else if (m.event === "verdict") {
        // A check is explicit; interaction/progress NEVER means a miss.
        if (!ceremony && gate && typeof m.correct === "boolean" && !done &&
            (m.attempt_id === undefined || (typeof m.attempt_id === "string" &&
              /^[A-Za-z0-9_-]{1,64}$/.test(m.attempt_id)))) {
          this._recordFirstAttempt(m.correct, beatIndex);
          attempted = true;
          armSkipOffer();
        }
      } else if (m.event === "complete") {
        booted = true;
        if (done && gate) return;
        // A legacy completion says nothing about preceding wrong checks.
        // Only explicit verdict events establish first-attempt accuracy.
        if (!ceremony && gate) this._excludeFirstAttempt("unreported", beatIndex);
        removeSkipOffer(); // solved — the escape hatch is moot
        // The reward loop (Task #491): a gated activity pays the same XP as a
        // solved check the moment the widget reports genuine completion —
        // once, and never for a skip / fallback unlock (those enable Continue
        // without ever raising this event). Film takes stay quiet.
        if (gate && !ceremony && !rewarded && m.correct !== false &&
            !this._filmMode && !this._captureMode) {
          rewarded = true;
          this._emit("answer_correct");
          this._sfx("correct");
          const praise = this._praise();
          this._speakAfterReaction(praise, token);
          this._rewardCorrect(praise, beatIndex);
        }
        if (btn.disabled) this._sfx("continue");
        unlock();
        this._emit("student_working");
      } else if (typeof m.event === "string") {
        // Source was checked above; the scheduler additionally validates the
        // closed event-name shape and fire-once semantics for this armed beat.
        if (token === this._runToken) {
          try { this._cues.onWidgetEvent(m.event); } catch (_) {}
        }
      }
    };
    window.addEventListener("message", onMsg);
    this._customCleanup = () => {
      removeSkipOffer();
      window.removeEventListener("message", onMsg);
    };
    iframe.srcdoc = String(beat.html || "");
    mount.appendChild(iframe);

    const finish = () => {
      if (token !== this._runToken) return;
      removeSkipOffer();
      try { window.removeEventListener("message", onMsg); } catch (_) {}
      this._resumeAfterProblem();
    };
    btn.addEventListener("click", () => {
      if (btn.disabled) return;
      this._sfx("continue");
      finish();
    });

    // Never-strand watchdogs (degradation law): a gated widget that never
    // boots degrades to the authored fallback line and unlocks; one that
    // boots but never reports ANY activity gets the explicit, labeled skip
    // after a generous ceiling — nothing unlocks silently anymore.
    // No run-token checks here — enabling a button on a stale card is
    // harmless, stranding the learner is not.
    if (gate) {
      setTimeout(() => {
        if (token !== this._runToken || done || booted) return;
        this._excludeFirstAttempt("fallback", beatIndex);
        const f = document.createElement("div");
        f.className = "ts-custom-fallback";
        f.textContent = String(beat.fallback ||
          "This activity could not start — carry the idea onward and continue.");
        wrap.insertBefore(f, btn);
        unlock();
      }, 8000);
      setTimeout(() => {
        if (token !== this._runToken) return;
        if (!done && !attempted) offerSkip("Stuck? Skip for now");
      }, SKIP_OFFER_IDLE_MS);
    }

    this._activeProblem = { custom: true };
    this._armStall();
    this._scrollToCurrent();
    if (!beat.say || beat.bubble === "none") {
      this._dismissNarrationBubble();
      if (beat.say && beat.bubble === "none") this._suppressNextBubble = true;
    }
    this._speak(beat.say, token); // narrate alongside (do NOT await)

    // Film capture (Trap 10): hands-free — let the widget breathe one gap,
    // then roll on regardless of gating.
    if (this._filmMode) {
      await this._sleep(FILM_BEAT_GAP_MS * 2, token);
      if (token !== this._runToken) return;
      unlock();
      finish();
    }
  }

  /** Ask the server for a VALIDATED generated game for this slot. The server
   * either returns a sandboxed game (mounted via MiniGameHost, graded
   * server-side) or a proven template fallback (rendered like a normal problem).
   * The answer never reaches the browser. */
  async _runGame(beat, token) {
    this.playing = false;
    this._syncTransport();
    // Task #353 — the clock holds while the activity is open (released on resume).
    this._holdClockForInteraction();

    // Mount the loading card FIRST, narrate in parallel (non-blocking). Same
    // reasoning as _runProblem: the activity must never wait on audio.
    const card = this._appendCard("problem");
    card.innerHTML =
      '<div class="ts-loading"><span class="ts-spin"></span>' +
      '<span>Building a little activity to try…</span></div>';
    this._scrollToCurrent();
    this._speak(beat.say, token); // do NOT await

    const g = (beat.game && typeof beat.game === "object") ? beat.game : {};
    const key = g.key ||
      [this.courseId || this.topic || "course", this.lessonId || "lesson",
       beat.id || ("beat" + this.idx)].join("__");

    let payload = null;
    try {
      const response = await this._contentJson("/api/learn/minigame/" + encodeURIComponent(key), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this._authHeader() },
        body: JSON.stringify({
          topic: this.topic,
          brief: g.brief || beat.say || "",
          concept: g.concept || "",
          difficulty: g.difficulty || beat.difficulty || "intro",
          character_name: this.character,
          capture: this._captureMode === true,
        }),
      });
      payload = response.data;
    } catch (_) {}
    if (token !== this._runToken) return;

    // Proven template fallback — render exactly like a normal problem.
    if (payload && payload.fallback) {
      this._renderProblemCard(card, payload.fallback);
      return;
    }

    const game = payload && payload.game;
    if (!game) {
      card.innerHTML =
        '<div class="ts-prob-head"><span class="ts-kicker">Try it</span>' +
        '<h3>That one slipped away</h3></div>' +
        '<p class="ts-prob-skip-note">No matter — let\'s carry on.</p>';
      const cont = document.createElement("button");
      cont.className = "ts-btn ts-btn-primary";
      cont.textContent = "Continue →";
      cont.addEventListener("click", () => this._resumeAfterProblem());
      card.appendChild(cont);
      this._filmAutoResume(token); // film: never strand the take on a failure card
      return;
    }

    this._renderGameCard(card, game, key);
  }

  _renderGameCard(card, game, key) {
    const token = this._runToken;
    const mountIndex = this.idx;
    card.innerHTML =
      '<div class="ts-prob-head"><span class="ts-kicker">' +
      this._esc(this.character) + " · Try it</span>" +
      "<h3>" + this._esc(game.title || this.topic) + "</h3></div>" +
      '<p class="ts-prob-prompt">' + this._esc(game.prompt || "") + "</p>" +
      '<div class="ts-game-mount"></div>' +
      '<div class="ts-prob-foot">' +
      '<button class="ts-btn ts-btn-ghost ts-prob-skip" type="button">Skip</button>' +
      '<button class="ts-btn ts-btn-primary ts-prob-check" type="button" data-demo-id="lesson-check">Check</button>' +
      "</div>";

    const mountEl = card.querySelector(".ts-game-mount");
    const checkBtn = card.querySelector(".ts-prob-check");
    const skipBtn = card.querySelector(".ts-prob-skip");
    let attempts = 0;
    let solved = false;
    let busy = false;

    if (this._gameHost) { try { this._gameHost.destroy(); } catch (_) {} }
    const host = new MiniGameHost({
      mount: mountEl,
      game: game,
      key: key,
      authHeader: () => this._authHeader(),
      captureMode: this._captureMode,
      onEvent: (event, payload) => {
        if (token !== this._runToken || this.idx !== mountIndex) return;
        this._emit(event, payload);
      },
    });
    this._gameHost = host;
    host.mount();
    // Freshly mounted game displays nothing revealed (mirrors the widget
    // mount reset — rebuilds and revisits start clean).
    if (this._revealedBeats) this._revealedBeats.delete(this.idx);
    // Task #524 — register the live game with the chat context bridge so an
    // ask mid-activity carries what the student is working on (cleared on nav,
    // and replaced outright when a template fallback renders as a problem).
    this._currentGame = {
      title: game.title || "",
      prompt: game.prompt || "",
      key: key || "",
      host,
    };

    if (game.coach && game.coach.intro) this._speak(game.coach.intro, this._runToken);

    this._setActiveProblem({
      why: () => {
        this._hintsUsed = (this._hintsUsed || 0) + 1;
        this._emit("hint_requested");
        this._logEvent("hint", { game: true });
        const line = (game.coach && (game.coach.hint || game.coach.why)) ||
          "Think about what the activity is really showing you — go a step at a time.";
        this._speakAfterReaction(line, this._runToken);
      },
      reveal: async () => {
        if (solved) return;
        solved = true;
        this._clearActiveProblem();
        this._logEvent("reveal", { game: true });
        if (this._revealedBeats) this._revealedBeats.add(this.idx);
        try { await host.reveal(); } catch (_) {} // host.reveal emits answer_revealed
        const line = (game.coach && (game.coach.walkthrough || game.coach.success)) ||
          "Here's the idea — watch how it comes together.";
        this._speakAfterReaction(line, this._runToken);
        checkBtn.style.display = "none";
        skipBtn.style.display = "none";
        this._hideAssistGhosts(card);
        const cont = document.createElement("button");
        cont.className = "ts-btn ts-btn-primary";
        cont.textContent = "Continue the lesson →";
        cont.addEventListener("click", () => this._resumeAfterProblem());
        card.querySelector(".ts-prob-foot").appendChild(cont);
      },
    });

    skipBtn.addEventListener("click", () => {
      if (solved) return;
      this._logEvent("skip", { game: true });
      this._resumeAfterProblem();
    });
    checkBtn.addEventListener("click", async () => {
      if (solved || busy) return;
      busy = true;
      checkBtn.disabled = true;
      let correct = false;
      let measured = false;
      try {
        const r = await host.checkAnswer();
        correct = !!(r && r.correct);
        measured = !!r && !r.error && typeof r.correct === "boolean";
      } catch (_) {}
      if (token !== this._runToken || solved) return;
      if (!measured) {
        this._firstAttempts?.record(this.idx, null);
        this._showFeedback("wrong", "The answer could not be checked. Try again.");
        checkBtn.disabled = false;
        busy = false;
        return;
      }
      this._recordFirstAttempt(correct);
      attempts += 1;
      // Task #524 — checkAnswer() just pulled a fresh state from the game for
      // grading; describe THAT snapshot for the ledger.
      let tried = "";
      try { tried = describeWidgetState({ value: host.lastKnownState() }); } catch (_) {}
      this._logEvent("check", {
        what: tried,
        verdict: correct ? "right" : "wrong",
        game: true,
      });
      if (correct) {
        solved = true;
        this._combo = (this._combo || 0) + 1;
        this._clearActiveProblem();
        // A correct game board shows THEIR winning state, never a reveal.
        if (this._revealedBeats) this._revealedBeats.delete(this.idx);
        card.classList.remove("ts-flash-wrong");
        card.classList.add("ts-flash-right");
        const praise = this._praise();
        const line = this._housePraise((game.coach && game.coach.success) || praise);
        // Task #262 (revised) — instant nod + smile + chime, then the praise.
        this._emit("answer_correct");
        this._sfx("correct");
        this._speakAfterReaction(line, this._runToken);
        this._assess(true, { topic: this.topic, template: "minigame",
          difficulty: game.difficulty || "intro" }, attempts);
        if (this._captureMode) this._showFeedback("correct", praise);
        else this._rewardCorrect(praise);
        checkBtn.style.display = "none";
        skipBtn.style.display = "none";
        this._hideAssistGhosts(card);
        const cont = document.createElement("button");
        cont.className = "ts-btn ts-btn-primary ts-prob-continue";
        cont.textContent = "Continue \u2192";
        cont.addEventListener("click", () => { this._sfx("continue"); this._resumeAfterProblem(); });
        card.querySelector(".ts-prob-foot").appendChild(cont);
      } else {
        this._combo = 0;
        card.classList.remove("ts-flash-wrong");
        void card.offsetWidth;
        card.classList.add("ts-flash-wrong");
        const enc = this._encourage();
        const line = (game.coach && game.coach.retry) || enc;
        // Task #262 (revised) — instant shake + chime, then the encouragement.
        this._emit("answer_wrong");
        this._sfx("incorrect");
        this._speakAfterReaction(line, this._runToken);
        this._showFeedback("wrong", enc);
        this._ensureHelpButton(card);
        // Task #491 — same reveal ladder as checks: three misses → See answer.
        if (attempts >= 3) this._ensureSeeAnswerButton(card);
        checkBtn.disabled = false;
      }
      busy = false;
    });

    // Task #353 (Trap 10) — film: sandboxed games are skipped quietly after a
    // beat (their generated content isn't deterministic enough for a take);
    // the film rolls on. Checks and holds, by contrast, auto-answer for real.
    if (this._filmMode) {
      const t = this._runToken;
      (async () => {
        await this._sleep(FILM_BEAT_GAP_MS * 2, t);
        if (t !== this._runToken || solved) return;
        solved = true;
        this._resumeAfterProblem();
      })();
    }

    this._scrollToCurrent();
  }

  _resumeAfterProblem() {
    // A capture owns one selected beat. Preserve its solved feedback until the
    // operator explicitly resets; never enter result/completion/navigation.
    if (this._captureMode) return;
    this._clearActiveProblem();
    // Task #353 — the interaction resolved: free the clock it held BEFORE the
    // isHeld() consult below, so only OTHER holders (an open modal) can still
    // pin the flow.
    this._releaseClockForInteraction();
    // Task #351 — the interaction resolved: back to the teaching stance.
    try { if (this._direction) this._direction.setMode("teaching"); } catch (_) {}
    const finishedIdx = this.idx;
    const b = this.lesson && this.lesson.beats[finishedIdx];
    // Just finished the FINAL skill-check question → show the result screen.
    // (Film mode skips it — a stats card is chrome on the take, Trap 10.)
    if (b && b.skillCheck && this._isLastSkill(finishedIdx) &&
        !this._reviewMode && !this._scResultShown && !this._filmMode) {
      this._showSkillResult(finishedIdx);
      return;
    }
    // In a review pass, walk the missed-question queue instead of the lesson.
    if (this._reviewMode) { this._advanceReview(); return; }
    this.playing = true;
    this._syncTransport();
    // Task #347 — clock-hold contract: while held (modal up), chaining to the
    // next beat is forbidden; the learner's explicit action (closing the modal,
    // Continue) resumes the flow. Never touches the audio.
    if (this._cues.isHeld()) return;
    this._proceedAfter(finishedIdx, () => {
      if (this.idx < this.lesson.beats.length - 1) this._goTo(this.idx + 1, { autoplay: true });
      else this._onLessonEnd();
    });
  }

  _skillBeats() {
    if (!this.lesson) return [];
    const out = [];
    this.lesson.beats.forEach((b, i) => { if (b && b.skillCheck) out.push(i); });
    return out;
  }

  _isFirstSkill(idx) {
    const b = this.lesson && this.lesson.beats;
    return !!(b && b[idx] && b[idx].skillCheck && (idx === 0 || !b[idx - 1].skillCheck));
  }

  _isLastSkill(idx) {
    const b = this.lesson && this.lesson.beats;
    return !!(b && b[idx] && b[idx].skillCheck &&
      (idx === b.length - 1 || !b[idx + 1].skillCheck));
  }

  // 1-based position of a skill-check beat among all skill-check beats.
  _skillPos(idx) {
    const arr = this._skillBeats();
    return { n: arr.indexOf(idx) + 1, total: arr.length };
  }

  // A fresh, full-width card appended after the current one (used for the
  // skill-check intro/result screens, which are not authored beats).
  _appendLooseCard(kind) {
    const d = this._dom;
    // Drop any prior loose card first so at most one is ever live.
    d.board.querySelectorAll(".ts-loose-card").forEach((c) => c.remove());
    d.board.querySelectorAll(".ts-card.is-current")
      .forEach((c) => c.classList.remove("is-current"));
    const card = document.createElement("div");
    // ts-loose-card has NO data-beat-card, so _revealUpTo can't manage it — it is
    // explicitly torn down on the next navigation (see _revealUpTo).
    card.className = "ts-card ts-loose-card ts-card-" + kind + " is-in is-current";
    d.board.appendChild(card);
    return card;
  }

  /** Task #354 — the depth-1 remedy detour. The remedy beat arrived INLINED
   * with the wrong verdict (server-held until the miss, Trap 5) and plays as
   * a LOOSE card — never inserted into lesson.beats, so scoring, review and
   * the segment bar stay untouched. Its narration was pre-generated with the
   * lesson (the resolver warmed it), so the legend performs it like any
   * beat; Continue returns to the main line. Depth 1 by validation: a remedy
   * can never carry echo/widgets, so this path can never re-enter itself. */
  _playRemedyBeat(remedy, token) {
    if (!remedy || typeof remedy !== "object") return;
    const card = this._appendLooseCard("remedy");
    let html = '<div class="ts-remedy-kicker">' +
      this._esc(this.character + " \u00b7 One more look") + "</div>";
    try {
      if (remedy.board && typeof remedy.board === "object") {
        html += this._boardHTML(remedy.board);
      }
    } catch (_) {}
    html += '<div class="ts-prob-foot">' +
      '<button class="ts-btn ts-btn-primary ts-remedy-continue" type="button">' +
      "Continue \u2192</button></div>";
    card.innerHTML = html;
    const btn = card.querySelector(".ts-remedy-continue");
    if (btn) {
      btn.addEventListener("click", () => {
        this._sfx("continue");
        this._resumeAfterProblem();
      });
    }
    // The remedy PERFORMS like any beat: arm the ONE scheduler with the
    // remedy's own cues (board `at` reveals, marks, plate focus, direction
    // verbs), stage its fragments on the loose card, and let the narration
    // line drive them — _speak attaches the pre-generated line's hash-pinned
    // timing (the resolver warmed remedy lines with their own alignment),
    // fires cues off the audio clock, and flushes on natural end. The
    // pseudo-beat is NEVER inserted into lesson.beats; cardIdx stays this.idx
    // (the interaction being detoured from), so every stale-card guard and
    // the navigation teardown treat it exactly like the main line: any nav
    // re-arms the scheduler, finalizes staged fragments, and tears the loose
    // card down (never-strand).
    if (typeof remedy.say === "string" && remedy.say.trim()) {
      try {
        const pseudo = {
          say: remedy.say,
          board: remedy.board,
          direction: remedy.direction,
        };
        this._cues.armBeat(this.idx + ":remedy", pseudo);
        this._stageCardEl(card, pseudo, this.idx);
        this._armMarksEl(card, pseudo, this.idx);
        this._armPlateFocusEl(card, pseudo, this.idx);
      } catch (_) {}
    }
    this._scrollToCurrent();
    this._speak(remedy.say, token); // narrate alongside (do NOT await)
  }

  /** Task #354 — student tokens, SESSION-ONLY (Trap: no cross-lesson
   * personalization — values die with the shell render; nothing persists).
   * Remembers the student's last graded submission as a display label for
   * {{student.answer|…}} in board text. Only clean scalar submissions (or a
   * resolvable option label) are kept — an unrenderable value keeps the
   * previous one, and the authored fallback covers the rest. */
  _noteAnswerToken(problem, res) {
    try {
      const v = res ? res.value : undefined;
      let label = res && typeof res.label === "string" ? res.label : null;
      if (!label && problem && problem.widget &&
          Array.isArray(problem.widget.options) &&
          typeof v === "number" && Number.isInteger(v)) {
        const o = problem.widget.options[v];
        label = typeof o === "string" ? o : (o && (o.text || o.label)) || null;
      }
      if (!label && (typeof v === "string" || typeof v === "number")) {
        label = String(v);
      }
      if (label != null && String(label).trim() !== "") {
        (this._tokenValues || (this._tokenValues = {})).answer = String(label);
      }
    } catch (_) {}
  }

  _subTokens(s) {
    try { return substituteStudentTokens(s, this._tokenValues || {}); } catch (_) { return s == null ? "" : String(s); }
  }

  /** Task #354 — the pin rail is a PURE function of the beat index (Trap 3:
   * replay/rewind must land on the same rail every time). Project every
   * beat's authored ops from the top through `index`: add appends (cap 6 —
   * the validator enforces it; runtime just refuses quietly), erase removes,
   * dim keeps the fact but recedes it. Text-only by grammar. */
  _computePins(index) {
    const beats = (this.lesson && this.lesson.beats) || [];
    const pins = []; // [{id, text, dim}] in append order
    const upTo = Math.min(index, beats.length - 1);
    for (let i = 0; i <= upTo; i++) {
      const beat = beats[i];
      const ops = beat && !Array.isArray(beat) && Array.isArray(beat.pin) ? beat.pin : null;
      if (!ops) continue;
      for (const op of ops) {
        if (!op || typeof op !== "object") continue;
        const at = pins.findIndex((p) => p.id === op.id);
        if (op.op === "add") {
          if (typeof op.id !== "string" || typeof op.text !== "string") continue;
          if (at !== -1 || pins.length >= 6) continue;
          pins.push({ id: op.id, text: op.text, dim: false });
        } else if (op.op === "erase") {
          if (at !== -1) pins.splice(at, 1);
        } else if (op.op === "dim") {
          if (at !== -1) pins[at].dim = true;
        }
      }
    }
    return pins;
  }

  _renderPinRail(pins) {
    const d = this._dom;
    if (!d || !d.pinrail || !d.pinrailList) return;
    const list = pins || [];
    if (!list.length) {
      d.pinrail.hidden = true;
      d.pinrail.dataset.count = "0";
      d.pinrailList.innerHTML = "";
      return;
    }
    d.pinrail.hidden = false;
    d.pinrail.dataset.count = String(list.length);
    d.pinrailList.innerHTML = list.map((p) =>
      '<li class="ts-pin' + (p.dim ? " is-dim" : "") + '" data-pin-id="' +
      this._esc(p.id) + '">' + this._esc(this._subTokens(p.text)) + "</li>").join("");
  }

  // Skill-check scoring is keyed by BEAT INDEX (idempotent sets), never by render
  // count — so segment-bar clicks / prev / replay can't inflate the score.
  _scTotalN() { return this._skillBeats().length; }

  _scCorrectN() { return this._scCorrectSet ? this._scCorrectSet.size : 0; }

  _scMissList() {
    return this._scMissSet ? Array.from(this._scMissSet).sort((a, b) => a - b) : [];
  }

  _showSkillIntro(idx) {
    this.playing = false;
    this._syncTransport();
    this._sfx("start");
    const total = this._skillPos(idx).total;
    const card = this._appendCard("skillintro");
    card.innerHTML =
      '<div class="ts-skillintro">' +
        '<span class="ts-skillintro-eyebrow">Skill check</span>' +
        '<h3 class="ts-skillintro-title">Show what you\u2019ve learned</h3>' +
        '<p class="ts-skillintro-sub">' + total +
        ' quick questions \u00b7 no hints. Take your time.</p>' +
        '<button class="ts-btn ts-btn-primary ts-skillintro-go" type="button">Start \u2192</button>' +
      "</div>";
    const go = card.querySelector(".ts-skillintro-go");
    if (go) go.addEventListener("click", () => {
      this._scIntroShown = true;
      this._sfx("tapOn");
      this._goTo(idx, { autoplay: true });
    });
    this._scrollToCurrent();
  }

  _showSkillResult(lastIdx) {
    this._scResultShown = true;
    const total = this._scTotalN();
    const correct = this._scCorrectN();
    const ratio = total ? correct / total : 1;
    const allRight = total > 0 && correct === total;
    this._sfx(allRight ? "courseComplete" : (ratio >= 0.6 ? "correct" : "lessonFailed"));
    const headline = allRight ? "Every one correct"
      : (ratio >= 0.6 ? "Nicely done." : "Good effort \u2014 let\u2019s look back.");
    const misses = this._scMissList();
    const card = this._appendLooseCard("skillresult");
    card.innerHTML =
      '<div class="ts-scresult">' +
        '<span class="ts-scresult-eyebrow">Skill check complete</span>' +
        '<div class="ts-scresult-score"><span class="ts-scresult-num">' + correct +
        '</span><span class="ts-scresult-of">/ ' + total + "</span></div>" +
        '<h3 class="ts-scresult-headline">' + this._esc(headline) + "</h3>" +
        '<div class="ts-scresult-actions"></div>' +
      "</div>";
    const acts = card.querySelector(".ts-scresult-actions");
    if (misses.length) {
      const review = document.createElement("button");
      review.type = "button";
      review.className = "ts-btn ts-btn-primary";
      review.textContent = "Review mistakes";
      review.addEventListener("click", () => { this._sfx("tapOn"); this._startReview(); });
      const skip = document.createElement("button");
      skip.type = "button";
      skip.className = "ts-btn ts-btn-ghost";
      skip.textContent = "Skip review";
      skip.addEventListener("click", () => { this._sfx("tapOn"); this._afterSkillCheck(); });
      acts.appendChild(review);
      acts.appendChild(skip);
    } else {
      const cont = document.createElement("button");
      cont.type = "button";
      cont.className = "ts-btn ts-btn-primary";
      cont.textContent = "Continue \u2192";
      cont.addEventListener("click", () => { this._sfx("continue"); this._afterSkillCheck(); });
      acts.appendChild(cont);
    }
    this._scrollToCurrent();
  }

  _startReview() {
    this._reviewMode = true;
    this._reviewQueue = this._scMissList();
    this._advanceReview();
  }

  _advanceReview() {
    if (!this._reviewQueue || !this._reviewQueue.length) {
      this._afterSkillCheck();
      return;
    }
    const idx = this._reviewQueue.shift();
    this.playing = true;
    this._syncTransport();
    this._goTo(idx, { autoplay: true });
  }

  // Leave the skill-check arc and continue into the completion / leagues beats.
  _afterSkillCheck() {
    this._reviewMode = false;
    const arr = this._skillBeats();
    const lastSkill = arr.length ? arr[arr.length - 1] : this.idx;
    this.playing = true;
    this._syncTransport();
    this._proceedAfter(lastSkill, () => {
      if (lastSkill < this.lesson.beats.length - 1) this._goTo(lastSkill + 1, { autoplay: true });
      else this._onLessonEnd();
    });
  }

  // "Practice complete!" — count up the XP earned this session + show accuracy.
  _playCompletion() {
    if (this._completionPlayed === this.idx) return;
    this._completionPlayed = this.idx;
    // Ceremony owns the gesture; the quiet results card never adds another.
    if (this._bigCeremonyAllowed()) this._sfx("courseComplete");
    const d = this._dom;
    const card = d && d.board && d.board.querySelector('[data-beat-card="' + this.idx + '"]');
    // Player ceremony Iteration 2 (B3) — the lesson's result is recorded ONCE
    // here (local-first store; server-replaceable), then the card's stats
    // read the board's scope: lesson (this play) / level / course totals.
    const measured = this._measuredResult();
    const saved = this._recordLessonResult(measured);
    const live = { ...measured, completionBonusXp: this._completionBonus || 0 };
    this._lastResult = { ...live, completed: !!saved };
    const beat = this.lesson && this.lesson.beats ? this.lesson.beats[this.idx] : null;
    const scope = completionScope(beat);
    if (!card) return;
    const stats = this._scopedStats(scope, live);
    const accEl = card.querySelector("[data-acc]");
    if (accEl) accEl.textContent = stats.acc == null ? "\u2014" : stats.acc + "%";
    const scopeNote = card.querySelector("[data-result-note]");
    if (scopeNote) scopeNote.textContent = (this._previewMode || this._filmMode
      ? "Preview \u00b7 Results not saved" : "History is local to this browser") +
      (stats.acc == null ? " \u00b7 Accuracy unavailable: not every first check was reported." : "");
    this._fillScopedLessons(card, stats);
    // Level/course totals depend on the course structure — if it is still on
    // its way, re-fill the numbers when it lands (the card is never blank:
    // the lesson-scope numbers above stand in until then).
    if (scope !== "lesson" && !this._course && this._courseP) {
      this._courseP.then(() => {
        if (!this._course || this._completionPlayed !== this.idx) return;
        const st = this._scopedStats(scope, live);
        const a = card.querySelector("[data-acc]");
        if (a) a.textContent = st.acc == null ? "\u2014" : st.acc + "%";
        const x = card.querySelector("[data-xp-total]");
        if (x) x.textContent = String(st.xp);
        this._completionCountGen = (this._completionCountGen || 0) + 1;
        this._fillScopedLessons(card, st);
      }).catch(() => {});
    }
    // Task #354 — the pinned facts assemble the end-of-lesson summary: every
    // fact still on the rail (dim included — receded, not erased) becomes a
    // recap line on the completion card. No pins → no section.
    try {
      const pins = this._computePins(this.idx);
      card.querySelectorAll(".ts-pin-summary").forEach((el) => el.remove());
      if (pins.length) {
        const wrap = document.createElement("div");
        wrap.className = "ts-pin-summary";
        wrap.innerHTML = '<h4 class="ts-pin-summary-title">What you pinned</h4>' +
          '<ul class="ts-pin-summary-list">' +
          pins.map((p) => '<li class="ts-pin' + (p.dim ? " is-dim" : "") + '">' +
            this._esc(this._subTokens(p.text)) + "</li>").join("") + "</ul>";
        card.appendChild(wrap);
      }
    } catch (_) {}
    const xpEl = card.querySelector("[data-xp-total]");
    if (xpEl) {
      // Aggregate totals already include this earned bonus. Only its lesson
      // summary labels/counts it separately; later milestones count one total.
      const bonus = scope === "lesson" ? (this._completionBonus || 0) : 0;
      const bonusRow = card.querySelector("[data-completion-bonus]");
      const bonusEl = card.querySelector("[data-bonus-total]");
      if (bonusRow) bonusRow.hidden = !bonus;
      const total = stats.xp;
      const base = Math.max(0, total - bonus);
      const reduced = this._prefersReducedMotion();
      const gen = this._completionCountGen = (this._completionCountGen || 0) + 1;
      const atIndex = this.idx;
      const run = this._runToken;
      const t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
      const tick = (now) => {
        if (gen !== this._completionCountGen || atIndex !== this.idx || run !== this._runToken) return;
        const elapsed = now - t0;
        const boardP = reduced ? 1 : Math.min(1, elapsed / 850);
        const bonusP = reduced ? 1 : Math.max(0, Math.min(1, (elapsed - 900) / 600));
        xpEl.textContent = String(Math.round(base * boardP + bonus * bonusP));
        if (bonusEl) bonusEl.textContent = "+" + Math.round(bonus * bonusP) + " XP";
        if (boardP < 1 || (bonus && bonusP < 1)) requestAnimationFrame(tick);
      };
      if (reduced) tick(t0); else requestAnimationFrame(tick);
    }
  }

  // ── Player ceremony Iteration 2 — default ceremony, stage layer, scope, next ──

  /** Called once per loaded lesson (standalone authored path): appends the
   * player's default completion board when none is authored and starts the
   * course-structure fetch the completion card's scope math + forward
   * control read. Lab / generated lectures are untouched. */
  _prepareLessonEnd() {
    this._ceremonyDone = false;
    this._resultCommitted = false;
    this._completionBonus = 0;
    this._lastResult = null;
    // Re-open the store partition pinned by _start before this run can record
    // completion. Never infer account history from a different learner.
    this._results = null;
    if (this._captureMode) {
      this._firstCompletion = null;
      this._course = null;
      return;
    }
    this._firstCompletion = this.courseId && this.lessonId
      ? this._resultStore().completionContext(this.courseId, this.lessonId).first_completion
      : null;
    this._course = this._course && this._course.id === this.courseId ? this._course : null;
    if (!this._standalone || !this.lesson) return;
    if (this.courseId) ensureDefaultCompletion(this.lesson);
    this._loadCourseStructure();
  }

  _loadCourseStructure() {
    if (!this.courseId || this._course || this._courseP) return this._courseP || null;
    const cid = this.courseId;
    this._courseP = (async () => {
      try {
        const r = await fetch("/api/learn/course/" + encodeURIComponent(cid), { credentials: "same-origin" });
        const j = r && r.ok ? await r.json() : null;
        const c = j && j.success && j.course && Array.isArray(j.course.levels) ? j.course : null;
        if (c && cid === this.courseId) {
          this._course = c;
          this._unlockAll = !!j.unlockedAll;
          // The end action may already be showing "Finish" — name the way on.
          try { if (this.lesson && this.idx === this.lesson.beats.length - 1) this._syncActionBar(this.lesson.beats[this.idx]); } catch (_) {}
        }
        return c;
      } catch (_) { return null; }
    })();
    return this._courseP;
  }

  _resultStore() {
    if (!this._results) {
      let st = null;
      try { st = window.localStorage; } catch (_) { st = null; }
      this._results = new LessonResultStore(st, this._resultLearner);
    }
    return this._results;
  }

  _recordLessonResult(live) {
    if (!this.courseId || !this.lessonId || this._previewMode || this._filmMode ||
        this._captureMode ||
        this._reviewMode || this._resultCommitted) return this._lastResult || null;
    this._resultCommitted = true;
    const store = this._resultStore();
    const saved = store.complete(this.courseId, this.lessonId, live);
    if (saved.first_completion && this._firstCompletion === true) {
      const claim = store.claimCompletionBonus(this.courseId, this.lessonId, 20);
      if (claim.claimed) {
        this._completionBonus = claim.amount;
        this._awardXp(claim.amount, { bonus: true });
      }
    }
    return store.get(this.courseId, this.lessonId);
  }

  _bigCeremonyAllowed() {
    return !this._captureMode && this._firstCompletion !== false && !this._reviewMode;
  }

  _recordFirstAttempt(correct, index = this.idx) {
    if (this._previewMode || this._filmMode || this._captureMode || this._reviewMode) return;
    if (!isGradedBeat(this.lesson?.beats?.[index])) return;
    (this._firstAttempts || (this._firstAttempts = new FirstAttemptLedger())).record(index, correct);
  }

  _excludeFirstAttempt(reason, index = this.idx) {
    if (this._previewMode || this._filmMode || this._captureMode || this._reviewMode) return;
    if (!isGradedBeat(this.lesson?.beats?.[index])) return;
    (this._firstAttempts || (this._firstAttempts = new FirstAttemptLedger())).exclude(index, reason);
  }

  _measuredResult() {
    const expected = (this.lesson?.beats || []).filter(isGradedBeat).length;
    return {
      xp: this._sessionXp || 0,
      ...(this._firstAttempts || new FirstAttemptLedger()).summary(expected),
    };
  }

  _widgetCompletionContext() {
    const measured = this._measuredResult();
    const bound = (n) => Number.isFinite(n) ? Math.min(10000, Math.max(0, n | 0)) : null;
    return {
      first_completion: typeof this._firstCompletion === "boolean" ? this._firstCompletion : null,
      attempts: bound(measured.attempts), misses: bound(measured.misses),
      streak: null, // no cross-lesson/day evidence in this local-first store
    };
  }

  _rewardCorrect(praise, index = this.idx) {
    if (this._previewMode || this._filmMode || this._captureMode || this._reviewMode) return false;
    const awards = this._rewardedBeats || (this._rewardedBeats = new Set());
    if (awards.has(index) || this._revealedBeats?.has(index)) return false;
    awards.add(index);
    this._awardXp(XP_PER_PROBLEM);
    this._showFeedback("correct", praise, XP_PER_PROBLEM);
    return true;
  }

  _scopedStats(scope, live) {
    return scopedStats(scope, this._course, this.lessonId, this._resultStore(), live);
  }

  /** Return the completion board governing a ceremony performance, or null.
   * This is the single boundary that prevents unrelated authored celebrations
   * from inheriting the lesson's eventual milestone tier. */
  _completionArcBoard(verb, beatIndex, cueId) {
    if (verb !== "celebrate") return null;
    const beats = (this.lesson && Array.isArray(this.lesson.beats))
      ? this.lesson.beats : [];
    const beat = beats[beatIndex];
    if (beat && beat.board && beat.board.kind === "completion") return beat;
    const synthesized = cueId === "ceremony:celebrate";
    if (!synthesized && !isCelebrationBeat(beat)) return null;
    // End arcs may contain an ungraded recap, but cannot cross more work or
    // take a board belonging to a later ceremony. No lesson-wide find().
    if (beats.slice(beatIndex + 1).some(isGradedBeat)) return null;
    for (let i = beatIndex + 1; i < beats.length; i++) {
      const next = beats[i];
      if (isGradedBeat(next) || isCelebrationBeat(next)) return null;
      if (next?.board?.kind === "completion") return next;
    }
    return null;
  }

  _fillScopedLessons(card, stats) {
    const n = card.querySelector("[data-lessons]");
    const l = card.querySelector("[data-lessons-label]");
    if (!n) return;
    if (stats.scope === "lesson") {
      n.textContent = "1";
      if (l) l.textContent = "Lesson";
    } else {
      n.textContent = stats.done + "/" + stats.total;
      if (l) l.textContent = "Lessons";
    }
  }

  /** What the end action does: resolved from the course's level list. */
  _forwardControl() {
    if (!this._standalone || !this.courseId || !this.lessonId) {
      return { kind: "none", label: "Finish \u2713", href: "" };
    }
    if (!this._course) {
      // Structure not here (yet / at all): the course page is always a real
      // way on for a course lesson — never a dead end while we wait.
      return { kind: "course", label: "Back to the course", href: courseHref(this.courseId), pending: true };
    }
    return resolveNext(this._course, this.lessonId, { courseId: this.courseId, unlocked: !!this._unlockAll });
  }

  _shouldRunDefaultCeremony(finishedIdx) {
    if (!this._standalone || this._ceremonyDone || !this.lesson) return false;
    if (typeof this._bigCeremonyAllowed === "function" && !this._bigCeremonyAllowed()) return false;
    if (finishedIdx !== lessonEndIndex(this.lesson)) return false;
    return !this.lesson.beats.some(isCelebrationBeat);
  }

  /** Gate the flow past `finishedIdx` through the default ceremony when the
   * lesson end was just crossed and the author wrote no celebration; then
   * `advance()` — unless a navigation superseded us meanwhile. Nothing here
   * touches audio; the gesture rides the authored-verb queue. */
  _proceedAfter(finishedIdx, advance) {
    if (!this._shouldRunDefaultCeremony(finishedIdx)) { advance(); return false; }
    const tok = this._runToken;
    const at = this.idx;
    this._runDefaultCeremony(finishedIdx).then(() => {
      if (tok !== this._runToken || !this.isOpen || this.idx !== at) return;
      advance();
    }).catch(() => {
      if (tok !== this._runToken || !this.isOpen || this.idx !== at) return;
      try { advance(); } catch (_) {}
    });
    return true;
  }

  async _runDefaultCeremony(finishedIdx) {
    this._ceremonyDone = true;
    // Preview renders one static beat: nothing to wait out.
    if (this._previewMode) return;
    const generation = this._runToken;
    // The ceremony owns a SILENT window. A completion action can arrive while
    // the final line is still winding down, so do not start its four-second
    // clock (or ask the rig to move) until every speech signal has released.
    while (generation === this._runToken && this.isOpen !== false &&
        this._directionSpeechActive()) {
      await this._sleep(50, generation);
    }
    if (generation !== this._runToken || this.isOpen === false) return;
    const d = this._dom;
    const title = String((this.lesson && this.lesson.title) || this.topic || "Lesson complete");
    const who = String(this.character || (this._record && this._record.name) || "").trim();
    let card = null;
    if (d && d.board) {
      card = this._appendLooseCard("ceremony");
      card.classList.add("ts-card-default-ceremony");
      card.setAttribute("data-default-ceremony", "1");
      card.innerHTML =
        '<div class="ts-ceremony-default">' +
        '<span class="ts-kicker">Lesson complete</span>' +
        '<h3 class="ts-ceremony-title">' + this._esc(title) + "</h3>" +
        (who ? '<p class="ts-ceremony-by">with ' + this._esc(who) + "</p>" : "") +
        "</div>";
    }
    // Silent: no praise pill, no XP, no bubble, no caption.
    try { this._clearFeedback(); } catch (_) {}
    try { this._dismissNarrationBubble(); } catch (_) {}
    try { this._renderCaptionBar(""); } catch (_) {}
    this._playStageCelebration("default");
    // The teacher's celebrate — deferred behind a still-running line via the
    // same queue authored gestures use (never over speech, never a face write).
    this._performAuthoredVerb("celebrate", finishedIdx, "ceremony:celebrate");
    const ms = this._prefersReducedMotion() ? Math.round(DEFAULT_CEREMONY_MS * 0.6) : DEFAULT_CEREMONY_MS;
    await this._sleep(ms, generation);
    // If another owner legitimately began speaking during the visual window,
    // yield until its natural end. Advancing here would make _onLessonEnd cut a
    // newer line, which is never an acceptable way to finish a ceremony.
    while (generation === this._runToken && this.isOpen !== false &&
        this._directionSpeechActive()) {
      await this._sleep(50, generation);
    }
    if (generation !== this._runToken || this.isOpen === false) return;
    this._drainPendingDirection(finishedIdx);
    // A rate/busy gate may have made the performance start near the end of the
    // visual window. Let it settle before advancing, bounded by the future
    // level/course tier's ~3s maximum. This never pauses or cuts audio.
    const perf = this._ceremonyPerformance;
    if (perf && perf.generation === generation) {
      const extra = Math.min(3000, Math.max(0, perf.settleUntil - Date.now()));
      if (extra) await this._sleep(extra, generation);
    }
    while (generation === this._runToken && this.isOpen !== false &&
        this._directionSpeechActive()) {
      await this._sleep(50, generation);
    }
    if (generation !== this._runToken || this.isOpen === false) return;
    // The next navigation tears the loose card down; if none follows (the
    // lesson ends right here) fade it ourselves so nothing stays on stage
    // over the endstate.
    if (card && card.isConnected) card.classList.add("is-leaving");
  }

  /** B2 — the stage-wide celebration layer: a light ground wash + house
   * tokens rising-and-settling over the WHOLE stage (character side included,
   * click-through). One verb, nothing spins; reduced motion keeps the wash
   * only (CSS). Re-triggers inside a running burst are ignored. */
  _playStageCelebration(source) {
    const d = this._dom;
    const layer = d && d.celebrateLayer;
    if (!layer) return false;
    const now = Date.now();
    if (this._celebrateUntil && now < this._celebrateUntil) return false;
    this._celebrateUntil = now + STAGE_CELEBRATION_MS;
    layer.dataset.source = source || "";
    layer.innerHTML = "";
    const reduced = this._prefersReducedMotion();
    if (!reduced) {
      const N = 18;
      const glyphs = ["dot", "dot", "dash", "ring", "dot", "spark"];
      for (let i = 0; i < N; i++) {
        const t = document.createElement("span");
        t.className = "ts-celebrate-token ts-ct-" + glyphs[i % glyphs.length] +
          " ts-ct-tone" + (i % 3);
        // Spread across the full width (both halves of the stage), staggered.
        t.style.setProperty("--x", (4 + ((i * 37) % 92)) + "%");
        t.style.setProperty("--d", (i % 6) * 70 + "ms");
        t.style.setProperty("--h", (46 + ((i * 23) % 40)) + "vh");
        layer.appendChild(t);
      }
    }
    layer.classList.remove("is-on");
    void layer.offsetWidth;
    layer.classList.add("is-on");
    if (this._celebrateTimer) clearTimeout(this._celebrateTimer);
    this._celebrateTimer = setTimeout(() => {
      layer.classList.remove("is-on");
      layer.innerHTML = "";
      this._celebrateTimer = null;
    }, STAGE_CELEBRATION_MS);
    return true;
  }

  // Leaderboard reveal — slide the learner's row up a rank.
  _playLeagues() {
    if (this._leaguesPlayed === this.idx) return;
    this._leaguesPlayed = this.idx;
    this._sfx("leaguesUnlocked");
    try { setTimeout(() => this._sfx("welcomeLeagues"), 1600); } catch (_) {}
    const d = this._dom;
    const card = d && d.board && d.board.querySelector('[data-beat-card="' + this.idx + '"]');
    if (!card) return;
    const you = card.querySelector(".ts-league-row.is-you");
    if (you) setTimeout(() => { try { you.classList.add("rise"); } catch (_) {} }, 450);
  }

  /** Three contextual controls shown only while a problem/game beat is live.
   * Each render path registers an active-problem spec (why/reveal closures that
   * close over that card's state). The controls emit semantic events only — the
   * legend's reaction is decided in the one reaction mapper, never here. */
  _setActiveProblem(spec) {
    this._activeProblem = spec || null;
    const d = this._dom;
    if (d && d.assist) d.assist.hidden = !this._activeProblem;
    if (this._activeProblem) this._armStall();
  }

  _clearActiveProblem() {
    if (this._customCleanup) {
      this._customCleanup();
      this._customCleanup = null;
    }
    this._activeProblem = null;
    this._clearStall();
    const d = this._dom;
    if (d && d.assist) d.assist.hidden = true;
  }

  _destroyGameHost() {
    if (!this._gameHost) return;
    try { this._gameHost.destroy(); } catch (_) {}
    this._gameHost = null;
    this._currentGame = null;
  }

  // Task #353 — interaction clock-hold: while a check/game/hold card is OPEN
  // the narration clock is held (CueScheduler counted hold) so no advance can
  // chain under it; the sacred speaker is never touched — an in-flight line
  // keeps playing and its cues keep firing. ONE flag pairs exactly one hold()
  // with one release() across every exit path (resume, navigation, lesson
  // end); the counted scheduler lets a modal stack its own hold on top safely.
  _holdClockForInteraction() {
    if (this._interactionHoldOn) return;
    this._interactionHoldOn = true;
    try { this._cues.hold(); } catch (_) {}
  }

  _releaseClockForInteraction() {
    if (!this._interactionHoldOn) return;
    this._interactionHoldOn = false;
    try { this._cues.release(); } catch (_) {}
  }

  /** a while, Newton gives a single gentle "long_think" glance toward the board.
   * Re-armed on every interaction; cancelled on navigation / solve / reveal. The
   * token + index guard ensures we never nudge after the learner has moved on. */
  _armStall() {
    this._clearStall();
    const idx = this.idx;
    const token = this._runToken;
    this._stallTimer = setTimeout(() => {
      this._stallTimer = null;
      if (this.idx === idx && this._runToken === token && this._activeProblem) {
        this._emit("long_think");
      }
    }, 12000);
  }

  _clearStall() {
    if (this._stallTimer) { clearTimeout(this._stallTimer); this._stallTimer = null; }
  }

  _onWhy() {
    this._sfx("hint");
    const p = this._activeProblem;
    if (p && typeof p.why === "function") p.why();
  }

  _onReveal() {
    if (this._captureMode) return;
    const p = this._activeProblem;
    if (p && typeof p.reveal === "function") p.reveal();
  }

  _onSkipExplanation() {
    this._sfx("skip");
    // Jump past the rest of this beat's explanation and carry on.
    this._resumeAfterProblem();
  }

  _showModal(opts) {
    const d = this._dom;
    if (!d || !d.overlay) return () => {};
    const o = opts || {};
    const close = () => this._closeModal();
    const card = document.createElement("div");
    card.className = "ts-modal-card";
    if (o.title) {
      const h = document.createElement("h3");
      h.className = "ts-modal-title";
      h.textContent = o.title;
      card.appendChild(h);
    }
    const body = document.createElement("div");
    body.className = "ts-modal-body";
    if (o.bodyHtml) body.innerHTML = o.body || "";
    else body.textContent = o.body || "";
    card.appendChild(body);
    const acts = document.createElement("div");
    acts.className = "ts-modal-actions";
    (o.actions || [{ label: "OK", kind: "solid" }]).forEach((a) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ts-modal-btn " + (a.kind === "ghost" ? "ts-modal-ghost" : "ts-modal-solid");
      b.textContent = a.label || "OK";
      b.addEventListener("click", () => {
        let keep = false;
        try { if (typeof a.onClick === "function") keep = a.onClick(card) === true; } catch (_) {}
        if (!keep) close();
      });
      acts.appendChild(b);
    });
    card.appendChild(acts);
    const wrap = document.createElement("div");
    wrap.className = "ts-modal";
    wrap.appendChild(card);
    d.overlay.innerHTML = "";
    d.overlay.appendChild(wrap);
    d.overlay.hidden = false;
    if (o.dismissible !== false) {
      wrap.addEventListener("click", (e) => { if (e.target === wrap) close(); });
    }
    this._modalOpen = true;
    // Task #347 — clock-hold contract: a modal HOLDS the lesson clock (no beat
    // may chain while it is up) but the speaker is NEVER suspended (sacred) —
    // an in-flight narration line keeps playing and its cues keep firing so
    // the board never desyncs from audible narration.
    try { this._cues.hold(); } catch (_) {}
    // Task #351 — Trap 2: the hold is a PERFORMANCE state. The legend settles
    // to idle (any running gesture decays out on its own — never a hard cut)
    // and breathes on the calmer attentive loop until the clock releases.
    try { if (this._direction) this._direction.setHeld(true); } catch (_) {}
    return close;
  }

  _closeModal() {
    const d = this._dom;
    if (!d || !d.overlay) return;
    d.overlay.hidden = true;
    d.overlay.innerHTML = "";
    this._modalOpen = false;
    try { this._cues.release(); } catch (_) {}
    try { if (this._direction) this._direction.setHeld(false); } catch (_) {}
  }

  _toast(msg) {
    const d = this._dom;
    if (!d || !d.root) return;
    let t = d.root.querySelector(".ts-toast");
    if (!t) { t = document.createElement("div"); t.className = "ts-toast"; d.root.appendChild(t); }
    t.textContent = msg;
    t.classList.add("show");
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => t.classList.remove("show"), 2200);
  }

  // Exit button → confirm before leaving (replaces a silent history.back).
  _confirmExit() {
    this._sfx("tapOn");
    this._showModal({
      title: "Leave the lesson?",
      body: "Your progress is saved — you can pick up right where you left off.",
      actions: [
        { label: "Keep learning", kind: "ghost" },
        { label: "Leave", kind: "solid", onClick: () => this.close() },
      ],
      dismissible: true,
    });
  }

  // Flag the current question as broken/confusing — opens a report form that
  // actually submits to the team (Firestore + email), not just localStorage.
  _onFlag() {
    this._sfx("tapOn");
    const reasons = [
      { key: "typo", label: "There's a typo or error" },
      { key: "confusing", label: "The wording is confusing" },
      { key: "broken", label: "Something is broken or won't load" },
      { key: "wrong", label: "The answer seems wrong" },
      { key: "other", label: "Something else" },
    ];
    const nm = "ts-flag-" + Date.now();
    const list = reasons.map((r, i) =>
      `<label class="ts-modal-opt"><input type="radio" name="${nm}" value="${r.key}"${i === 0 ? " checked" : ""}><span>${this._esc(r.label)}</span></label>`
    ).join("");
    const knownEmail = this._esc(this._knownEmail() || "");
    const body =
      `<div class="ts-modal-opts">${list}</div>` +
      `<textarea class="ts-modal-text" rows="3" placeholder="Tell us what's wrong (optional)"></textarea>` +
      `<input class="ts-modal-input ts-flag-email" type="email" autocomplete="email" placeholder="Your email (optional — so we can follow up)" value="${knownEmail}">`;
    this._showModal({
      title: "Report a problem",
      body, bodyHtml: true,
      actions: [
        { label: "Cancel", kind: "ghost" },
        { label: "Send report", kind: "solid", onClick: (root) => {
            const sel = root.querySelector(`input[name="${nm}"]:checked`);
            const note = root.querySelector(".ts-modal-text");
            const email = root.querySelector(".ts-flag-email");
            // Show a sending state and keep the modal open until the POST
            // resolves (return true keeps it open per _showModal's contract).
            root.querySelectorAll(".ts-modal-btn").forEach((b) => { b.disabled = true; });
            const solid = root.querySelector(".ts-modal-solid");
            if (solid) solid.textContent = "Sending…";
            this._submitFlag({
              reason: sel ? sel.value : "other",
              description: note ? note.value : "",
              email: email ? email.value : "",
            });
            return true;
          } },
      ],
      dismissible: true,
    });
  }

  // Best-effort: the signed-in user's email, if the app has exposed one.
  _knownEmail() {
    try {
      const a = window.firebaseAuth || (window.firebase && window.firebase.auth && window.firebase.auth());
      if (a && a.currentUser && a.currentUser.email) return a.currentUser.email;
    } catch (_) {}
    try { if (window.__lastUserEmail) return window.__lastUserEmail; } catch (_) {}
    return "";
  }

  // A compact, reproducible snapshot of the flagged board so the team can see
  // exactly what the learner was looking at (more reliable than a pixel shot
  // of the live WebGL scene, which we must not touch).
  _captureBoardContext() {
    const out = [];
    try {
      const beat = this.lesson && this.lesson.beats[this.idx];
      if (beat) {
        if (beat.kind) out.push("kind: " + beat.kind);
        if (beat.title) out.push("title: " + beat.title);
        if (beat.prompt) out.push("prompt: " + beat.prompt);
        if (Array.isArray(beat.choices)) out.push("choices: " + beat.choices.map(c => (c && c.label) || c).join(" | "));
        if (beat.answer != null) out.push("(has answer key)");
      }
    } catch (_) {}
    try {
      const d = this._dom;
      const card = d && d.board && (d.board.querySelector(".ts-card.is-current") || d.board.querySelector(".ts-card"));
      if (card) {
        const txt = (card.innerText || "").replace(/\s+\n/g, "\n").trim();
        if (txt) out.push("--- on-screen ---\n" + txt.slice(0, 1500));
      }
    } catch (_) {}
    return out.join("\n").slice(0, 3500);
  }

  async _submitFlag(input) {
    this._sfx("choice");
    const beat = this.lesson && this.lesson.beats[this.idx];
    const payload = {
      reason: (input && input.reason) || "other",
      description: ((input && input.description) || "").slice(0, 2000),
      email: ((input && input.email) || "").slice(0, 200),
      courseId: this.courseId || "",
      lessonId: this.lessonId || "",
      beatId: (beat && beat.id) || "",
      beatIndex: this.idx,
      url: (typeof location !== "undefined" && location.href) || "",
      context: this._captureBoardContext(),
    };

    let sent = false;
    try {
      const res = await fetch("/api/learn/flag", {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, this._authHeaders()),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      sent = !!(res.ok && data && data.success);
    } catch (_) {
      sent = false;
    }

    if (sent) {
      this._closeModal();
      this._toast("Thanks — your report was sent to our team.");
      return;
    }

    // Delivery failed: keep an offline copy so it isn't lost, and be honest.
    try {
      const key = "legendlabs.lessonFlags";
      const arr = JSON.parse(localStorage.getItem(key) || "[]");
      arr.push(Object.assign({ t: Date.now() }, payload));
      localStorage.setItem(key, JSON.stringify(arr.slice(-50)));
    } catch (_) {}
    this._closeModal();
    this._toast("Couldn't reach our team right now — we saved it on this device and will retry next time.");
  }

  // Resend reports that were saved offline (delivery failed earlier). Best-effort
  // and quiet: drops each report from the queue only once the server confirms it.
  async _drainOfflineFlags() {
    let arr;
    try {
      arr = JSON.parse(localStorage.getItem("legendlabs.lessonFlags") || "[]");
    } catch (_) { return; }
    if (!Array.isArray(arr) || !arr.length) return;
    const remaining = [];
    for (const rec of arr) {
      let ok = false;
      try {
        const res = await fetch("/api/learn/flag", {
          method: "POST",
          headers: Object.assign({ "Content-Type": "application/json" }, this._authHeaders()),
          body: JSON.stringify(rec),
        });
        const data = await res.json().catch(() => ({}));
        ok = !!(res.ok && data && data.success);
      } catch (_) { ok = false; }
      if (!ok) remaining.push(rec);
    }
    try {
      if (remaining.length) localStorage.setItem("legendlabs.lessonFlags", JSON.stringify(remaining.slice(-50)));
      else localStorage.removeItem("legendlabs.lessonFlags");
    } catch (_) {}
  }

  // Brilliant-style: when the tutor answers a chat question about the activity,
  // briefly glow the live widget green in sync with the message.
  _pulseHighlight() {
    const d = this._dom;
    if (!d || !d.board) return;
    const card = d.board.querySelector(".ts-card.is-current") || d.board.querySelector(".ts-card");
    if (!card) return;
    this._sfx("highlight");
    card.classList.remove("ts-hl-pulse");
    void card.offsetWidth; // reflow so the animation restarts
    card.classList.add("ts-hl-pulse");
    clearTimeout(this._hlTimer);
    this._hlTimer = setTimeout(() => card.classList.remove("ts-hl-pulse"), 1400);
  }

  async _assess(correct, problem, attempts) {
    this._lastAttempts = attempts || 0; // mirrored for the chat student-state
    if (this._captureMode) return;
    try {
      await fetch(API.assess, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this._authHeader() },
        body: JSON.stringify({
          correct: !!correct,
          attempts: attempts || 1,
          hints_used: 0,
          topic: this.topic,
          template: problem && problem.template,
          difficulty: (problem && problem.difficulty) || "intro",
          legend: this.legendToken || this.character,
        }),
      });
    } catch (_) {}
  }

  _showCheckpoint(beat, token) {
    const d = this._dom;
    if (!d) return;
    d.checkpoint.innerHTML = "";
    const q = document.createElement("span");
    q.className = "ts-cp-q";
    q.textContent = beat.checkpoint.ask;
    const yes = document.createElement("button");
    yes.className = "ts-cp-btn";
    yes.textContent = "Makes sense";
    const again = document.createElement("button");
    again.className = "ts-cp-btn ts-cp-again";
    again.textContent = "Show me again";
    yes.addEventListener("click", () => {
      this._recordFelt(true);
      this._clearCheckpoint();
    });
    again.addEventListener("click", () => {
      this._recordFelt(false);
      this._clearCheckpoint();
      this._replay();
    });
    d.checkpoint.appendChild(q);
    d.checkpoint.appendChild(yes);
    d.checkpoint.appendChild(again);
    d.checkpoint.classList.add("show");
    // Task #351 — a check is on screen: face the student, attentive loop.
    try { if (this._direction) this._direction.onCheckOpened(); } catch (_) {}
  }

  _clearCheckpoint() {
    const d = this._dom;
    if (d) d.checkpoint.classList.remove("show");
  }

  _recordFelt(landed) {
    // Felt-not-shown: a light mastery nudge. Best-effort, anonymous-safe.
    if (this._captureMode) return;
    try {
      fetch(API.assess, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this._authHeader() },
        body: JSON.stringify({
          correct: !!landed,
          attempts: 1,
          hints_used: landed ? 0 : 1,
          topic: this.topic,
          template: "checkpoint",
          difficulty: "intro",
          legend: this.legendToken || this.character,
        }),
      }).catch(() => {});
    } catch (_) {}
  }

  _revealUpTo(index) {
    const d = this._dom;
    if (!d) return;
    const cards = d.board.querySelectorAll("[data-beat-card]");
    cards.forEach((c) => {
      const i = parseInt(c.getAttribute("data-beat-card"), 10);
      // Single-focus board (Task #251): exactly ONE card is on stage at a time.
      // Past/future cards are cross-faded out (CSS), not stacked into a wall.
      c.classList.toggle("is-current", i === index);
      c.classList.toggle("is-past", i < index);
      c.classList.toggle("is-future", i > index);
      // is-in retained for back-compat with inline `is-in is-current` writers.
      c.classList.toggle("is-in", i <= index);
    });
    // Task #348 — navigation projection: any still-staged fragments snap to
    // final visible state, WITHOUT emitting board events (the scheduler was
    // just re-armed for the target beat; stale chains must not fire into it).
    // The target card is re-staged right after by _stageCurrentCard.
    this._finalizeStagedNow(false);
    d.board.querySelectorAll(".ts-frag-staged").forEach((el) => {
      el.classList.remove("ts-frag-staged");
      el.removeAttribute("aria-hidden");
    });
    // Synthesized skill-check result cards aren't authored beats, so they live
    // outside the single-focus model — remove them whenever we navigate so they
    // can never stay is-current and overlay the target beat.
    d.board.querySelectorAll(".ts-loose-card").forEach((c) => c.remove());
    // Task #354 — student tokens: a teaching board that references
    // {{student.…}} re-renders on arrival so it reflects the CURRENT session
    // values (they may have changed since the shell render — e.g. the hold
    // commit landed after this card was first built). Cheap: one serialize
    // test, only the target card, card-level listeners survive innerHTML.
    const beat = this.lesson && this.lesson.beats ? this.lesson.beats[index] : null;
    if (beat && beat.board && beat.kind !== "problem" && beat.kind !== "game" &&
        beat.kind !== "hold") {
      try {
        if (JSON.stringify(beat.board).indexOf("{{student.") !== -1) {
          const card = d.board.querySelector('[data-beat-card="' + index + '"]');
          if (card) card.innerHTML = this._boardHTML(beat.board);
        }
      } catch (_) {}
    }
    // Task #354 — the margin rail accumulates across beats; recompute the
    // projection for THIS index (pure function — replay-safe, and goldframe's
    // gotoBeat gets the exact rail state for free).
    this._renderPinRail(this._computePins(index));
  }

  _renderShell() {
    const d = this._dom;
    // reset per-lesson tallies (XP earned this session, skill-check accuracy)
    this._sessionXp = 0;
    this._residentFetchStarted = false;
    this._firstAttempts = new FirstAttemptLedger();
    this._rewardedBeats = new Set();
    // The HUD pill always opens on the loaded balance (Task #491) — awards
    // then count up from it, and the completion tally stays session-only.
    this._renderXp();
    this._scCorrectSet = new Set();
    this._scMissSet = new Set();
    this._scIntroShown = false;
    this._scResultShown = false;
    this._reviewMode = false;
    this._reviewQueue = [];
    this._completionPlayed = -1;
    this._leaguesPlayed = -1;
    // Task #354 — student tokens are WITHIN-SESSION only: a fresh shell is a
    // fresh session (no cross-lesson personalization — deliberately unbuilt).
    this._tokenValues = {};
    // Task #524 — fresh lesson, fresh tutor memory: the interaction ledger and
    // chat-turn ring are session-only context for the ask bridge.
    this._ledger = [];
    this._chatTurns = [];
    // Beats whose board is CURRENTLY displaying the revealed answer (strict
    // skill-check miss, see-answer, film quiet reveal). Cleared when the
    // beat's widget re-mounts fresh or is answered correctly, so the ask
    // context always matches what is actually on the student's screen.
    this._revealedBeats = new Set();
    this._renderPinRail([]);
    d.title.textContent = this.lesson.title || this.topic;
    d.subtitle.textContent = this.lesson.subtitle || (this.character + " · live lesson");
    d.board.innerHTML = "";
    // fresh conversation for a fresh lesson
    if (d.chat) d.chat.innerHTML = "";
    this._lastBubbleLegendText = "";
    d.endNote.classList.remove("show");
    this.lesson.beats.forEach((beat, i) => {
      // Problem, game, hold AND custom beats render into a pre-built empty
      // slot that the interactive card fills in later. These beats carry no
      // `board`, so they must NOT fall through to the teaching-card branch
      // (which dereferences beat.board.kind and would crash the shell render).
      if (beat.kind === "problem" || beat.kind === "game" ||
          beat.kind === "hold" || beat.kind === "custom") {
        const slot = document.createElement("div");
        const ceremony = isCustomCeremonyBeat(beat);
        slot.className = "ts-card" + (ceremony ? " ts-card-ceremony" : " ts-card-problem") +
          (beat.kind === "hold" ? " ts-card-hold" : "") +
          (beat.kind === "custom" ? " ts-card-custom" : "");
        slot.setAttribute("data-beat-card", String(i));
        if (beat.kind === "problem") slot.setAttribute("data-problem-slot", "1");
        // Inline placeholder so the slot is never a blank card during the
        // brief window between becoming current and the widget mounting.
        slot.innerHTML =
          '<div class="ts-slot-ph"><span class="ts-spin"></span>' +
          "<span>Getting this ready…</span></div>";
        d.board.appendChild(slot);
      } else {
        const card = document.createElement("div");
        card.className = "ts-card ts-card-" + beat.board.kind;
        card.setAttribute("data-beat-card", String(i));
        card.innerHTML = this._boardHTML(beat.board);
        card.addEventListener("click", () => {
          // free scroll-back: clicking a past card jumps the lecture there
          if (i !== this.idx) this._goTo(i, { autoplay: false });
        });
        d.board.appendChild(card);
      }
    });
    this._updateProgress();
  }

  _appendCard(kind) {
    // For problem beats we render into the pre-built slot for this beat.
    const d = this._dom;
    const slot = d.board.querySelector('[data-beat-card="' + this.idx + '"]');
    if (slot) {
      slot.classList.add("is-in", "is-current");
      return slot;
    }
    const card = document.createElement("div");
    card.className = "ts-card ts-card-" + kind + " is-in is-current";
    d.board.appendChild(card);
    return card;
  }

  _boardHTML(board) {
    // Task #301 — delegates to the SHARED LessonBoardRenderer module (the
    // admin panel preview imports the same renderer; never fork it).
    //
    // Task #354 — student tokens substitute BEFORE the renderer runs, on a
    // deep copy, so mark-range math and fragment planning see the SAME text
    // the card renders (esc still applies after — a session value can never
    // smuggle markup). Token-free boards take the zero-cost path.
    let b = board;
    try {
      if (board && JSON.stringify(board).indexOf("{{student.") !== -1) {
        b = JSON.parse(JSON.stringify(board), (k, v) =>
          (typeof v === "string" ? this._subTokens(v) : v));
      }
    } catch (_) { b = board; }
    return boardHTML(b, { esc: (s) => this._esc(s) });
  }

  _setFocus(focus) {
    if (this._dom) this._dom.root.dataset.focus = focus === "board" ? "board" : "tutor";
  }

  // Brilliant-style segmented progress: one filled segment per completed beat,
  // the current one half-lit, the rest empty. Each segment is clickable.
  _updateProgress() {
    const d = this._dom;
    if (!d || !this.lesson) return;
    const total = this.lesson.beats.length;
    if (d.segbar) {
      d.segbar.innerHTML = "";
      for (let i = 0; i < total; i++) {
        const seg = document.createElement("button");
        seg.type = "button";
        seg.className = "ts-seg" + (i < this.idx ? " done" : (i === this.idx ? " on" : ""));
        seg.setAttribute("aria-label", "Go to step " + (i + 1));
        seg.addEventListener("click", () => this._goTo(i, { autoplay: false }));
        d.segbar.appendChild(seg);
      }
    }
    if (d.count) d.count.textContent = (this.idx + 1) + " / " + total;
    if (d.progress) {
      d.progress.setAttribute("aria-valuenow", String(this.idx + 1));
      d.progress.setAttribute("aria-valuemax", String(total));
    }
  }

  // Big Brilliant-style primary button. It advances teaching beats; on
  // interactive (problem/game/hold) beats the card owns the flow, so it's hidden.
  _syncActionBar(beat) {
    const d = this._dom;
    if (!d || !d.actionbar) return;
    const interactive = beat && (beat.kind === "problem" || beat.kind === "game" ||
      beat.kind === "hold" || beat.kind === "custom" ||
      (beat.widget && typeof beat.widget === "object"));
    d.actionbar.classList.toggle("is-on", !interactive);
    const last = this.lesson && this.idx >= this.lesson.beats.length - 1;
    if (d.continueBtn) {
      // Player ceremony Iteration 2 (B4) — the end action names what follows
      // ("Next: …" / "Start Level N" / "Back to the course"); "Finish" only
      // when nothing follows. Re-synced when the course structure arrives.
      d.continueBtn.textContent = last ? this._forwardControl().label : "Continue";
      d.continueBtn.dataset.forward = last ? this._forwardControl().kind : "";
    }
  }

  // Transport (play/pause/replay) was removed in the Brilliant reskin (Task
  // #251) — the learner drives pace with Continue. Kept as a no-op so the many
  // existing callers don't need touching.
  _syncTransport() {}

  _renderXp() {
    const d = this._dom;
    if (d && d.xpNum) d.xpNum.textContent = String(this.xp || 0);
  }

  // Award XP with a Brilliant-style count-up + rising tally cues. The pill
  // bumps, the number rolls from old→new over ~700ms, and a short pitched
  // sequence plays. Never blocks the lesson; purely cosmetic on top of state.
  _awardXp(amount, options = {}) {
    if (this._captureMode) return;
    amount = Math.max(0, amount | 0);
    if (!amount) return;
    const from = this.xp || 0;
    this.xp = from + amount;
    if (!options.bonus) this._sessionXp = (this._sessionXp || 0) + amount;
    this._saveXp();
    // State and persistence are authoritative and synchronous. _showFeedback
    // consumes this presentation record immediately after the genuine award.
    this._cancelRewardMotion();
    this._renderXp();
    this._pendingReward = { from, to: this.xp, amount };
  }

  // Rotating praise so the same line never repeats back-to-back.
  _praise() {
    if (!PRAISE_POOL.length) return HOUSE_PRAISE_FALLBACK;
    let i = Math.floor(Math.random() * PRAISE_POOL.length);
    if (i === this._lastPraise && PRAISE_POOL.length > 1) i = (i + 1) % PRAISE_POOL.length;
    this._lastPraise = i;
    return PRAISE_POOL[i];
  }

  // Bring authored success copy into the same restrained house voice as the
  // rotating pool while preserving its problem-specific explanation.
  _housePraise(line) {
    return cleanHousePraise(line, HOUSE_PRAISE_FALLBACK);
  }

  // Rotating encouragement for a teaching miss (no immediate repeat).
  _encourage() {
    if (!ENCOURAGE_POOL.length) return "Not quite — try again.";
    let i = Math.floor(Math.random() * ENCOURAGE_POOL.length);
    if (i === this._lastEncourage && ENCOURAGE_POOL.length > 1) i = (i + 1) % ENCOURAGE_POOL.length;
    this._lastEncourage = i;
    return ENCOURAGE_POOL[i];
  }

  // Teaching miss: drop a "Get help" ghost button beside Check (once). It runs
  // the same context-aware hint as the assist — the authored coach line names
  // the specific mistake for this problem.
  _ensureHelpButton(card) {
    const foot = card && card.querySelector(".ts-prob-foot");
    if (!foot || foot.querySelector(".ts-prob-help")) return;
    const help = document.createElement("button");
    help.type = "button";
    help.className = "ts-btn ts-btn-ghost ts-prob-help";
    help.textContent = "Get help";
    help.addEventListener("click", () => this._onWhy());
    const check = foot.querySelector(".ts-prob-check");
    if (check) foot.insertBefore(help, check); else foot.appendChild(help);
  }

  // Task #491 — after three misses on a gradable teaching interaction a
  // "See answer" ghost appears beside Get help (Brilliant's measured
  // behavior): it runs the EXISTING reveal path — the answer is shown, the
  // page pays 0 XP (reveal never awards), and the lesson continues. Skill
  // checks never call this (single-shot strict), so their rules are intact.
  _ensureSeeAnswerButton(card) {
    const foot = card && card.querySelector(".ts-prob-foot");
    if (!foot || foot.querySelector(".ts-prob-seeanswer")) return;
    const see = document.createElement("button");
    see.type = "button";
    see.className = "ts-btn ts-btn-ghost ts-prob-seeanswer";
    see.textContent = "See answer";
    see.addEventListener("click", () => {
      this._sfx("hint");
      this._onReveal();
    });
    const check = foot.querySelector(".ts-prob-check");
    if (check) foot.insertBefore(see, check); else foot.appendChild(see);
  }

  // Resolved cards keep only their Continue: the assist ghosts (Get help /
  // See answer) leave with Check and Skip.
  _hideAssistGhosts(card) {
    if (!card) return;
    const help = card.querySelector(".ts-prob-help");
    if (help) help.style.display = "none";
    const see = card.querySelector(".ts-prob-seeanswer");
    if (see) see.style.display = "none";
  }

  // Skill-check miss: a "Why?" button that opens a short explanation modal.
  _addWhyButton(card, problem) {
    const foot = card && card.querySelector(".ts-prob-foot");
    if (!foot || foot.querySelector(".ts-prob-why")) return;
    const why = document.createElement("button");
    why.type = "button";
    why.className = "ts-btn ts-btn-ghost ts-prob-why";
    why.textContent = "Why?";
    why.addEventListener("click", () => {
      this._sfx("hint");
      const expl = (problem && problem.coach &&
        (problem.coach.walkthrough || problem.coach.why || problem.coach.success)) ||
        "The correct answer is shown above — compare it with your choice to see what differs.";
      this._showModal({
        title: "Why?", body: expl,
        actions: [{ label: "Got it", kind: "solid" }], dismissible: true,
      });
    });
    const cont = foot.querySelector(".ts-prob-continue");
    if (cont) foot.insertBefore(why, cont); else foot.appendChild(why);
  }

  // The Brilliant feedback banner above the action bar: green for correct (with
  // +XP), amber for a teaching retry, teal for review. Persists until the next
  // beat clears it.
  _showFeedback(kind, text, xp) {
    const d = this._dom;
    if (!d || !d.feedback) return;
    d.feedback.classList.remove("is-correct", "is-wrong", "is-review", "is-strict");
    const cls = kind === "correct" ? "is-correct"
      : (kind === "review" ? "is-review"
      : (kind === "strict" ? "is-strict" : "is-wrong"));
    d.feedback.classList.add(cls);
    if (d.feedbackIcon) {
      d.feedbackIcon.textContent = kind === "correct" ? "\u2713" : (kind === "review" ? "\u2192" : "\u2715");
    }
    if (d.feedbackText) {
      // Correct praise belongs in the teacher bubble. The reward pill is only
      // the verdict mark and the amount, so it never repeats that praise.
      d.feedbackText.textContent = kind === "correct" ? "" :
        (text || (kind === "review" ? "Review" : "Not quite"));
    }
    if (d.feedbackXp) d.feedbackXp.textContent = (kind === "correct" && xp) ? ("+" + xp + " XP") : "";
    d.feedback.hidden = false;
    d.feedback.classList.add("show");
    if (kind === "correct" && xp) {
      const pending = this._pendingReward;
      this._pendingReward = null;
      const reduced = (() => {
        try { return !!window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
        catch (_) { return false; }
      })();
      this._rewardMotion = new RewardMotion({
        duration: 600,
        reducedMotion: reduced,
        quiet: this._quiet || reduced,
        onStart: () => {
          this._sfx("xpStart");
          try { lessonSound.tallyStep(this._combo || 1); } catch (_) {}
        },
        onFinish: () => {
          this._sfx("xpStop");
          if (d.xpPill) {
            d.xpPill.classList.remove("is-bump");
            void d.xpPill.offsetWidth;
            d.xpPill.classList.add("is-bump");
          }
        },
      });
      const from = pending ? pending.from : Math.max(0, (this.xp || 0) - (xp | 0));
      this._rewardMotion.count({ from, to: this.xp || 0, number: d.xpNum });
      this._rewardMotion.fly({ source: d.feedbackXp, target: d.xpPill, text: "+" + (xp | 0) + " XP" });
    }
  }

  _cancelRewardMotion() {
    if (this._rewardMotion) {
      this._rewardMotion.cancel(this.xp || 0);
      this._rewardMotion = null;
    }
    const d = this._dom;
    if (d && d.xpNum) d.xpNum.textContent = String(this.xp || 0);
  }

  _clearFeedback() {
    const d = this._dom;
    if (!d || !d.feedback) return;
    d.feedback.classList.remove("show", "is-correct", "is-wrong", "is-review", "is-strict");
    d.feedback.hidden = true;
  }

  _toggleChatPanel(force) {
    const d = this._dom;
    if (!d || !d.chatPanel) return;
    if (this._captureMode && force !== false) return;
    const open = (typeof force === "boolean") ? force : !this._chatOpen;
    this._chatOpen = open;
    if (this._chatCloseTimer) {
      clearTimeout(this._chatCloseTimer);
      this._chatCloseTimer = null;
    }
    if (open) {
      // Task #284 ROOT-CAUSE FIX (janky open): dock the panel at its LEFT
      // start position first, flush styles so that state is what the browser
      // has painted, and only THEN start the slide + board split. Previously
      // `ts-chat-open` (which re-anchored the panel left) and `.show` landed
      // in the same frame, so the transition ran from the last painted state
      // — the base right-edge position — and the panel flew across the stage.
      d.chatPanel.hidden = false;
      d.chatPanel.classList.add("dock-left");
      void d.chatPanel.offsetWidth; // style flush: paint off-screen LEFT
      d.chatPanel.classList.add("show");
      if (d.root) d.root.classList.add("ts-chat-open");
      this._syncResidentDock(true);
      if (d.chatToggle) d.chatToggle.classList.add("is-on");
      this._scrollChat();
      // preventScroll: a plain focus() mid-transition scrolls the panel into
      // view and visibly yanks the layout.
      try { d.askInput && d.askInput.focus({ preventScroll: true }); } catch (_) {}
      this._sfx("choice");
      // A small attentive lean-in toward the learner as the conversation opens.
      this._emit("chat_opened");
    } else {
      // Slide out to the LEFT while the board animates back to center.
      // `dock-left` must OUTLIVE the transition — removing it now would snap
      // the panel back to its right-anchored base mid-slide.
      d.chatPanel.classList.remove("show");
      if (d.root) d.root.classList.remove("ts-chat-open");
      this._syncResidentDock(false);
      if (d.chatToggle) d.chatToggle.classList.remove("is-on");
      this._chatCloseTimer = setTimeout(() => {
        this._chatCloseTimer = null;
        d.chatPanel.hidden = true;
        d.chatPanel.classList.remove("dock-left");
      }, 480); // --ts-anim-dur (.42s) + a small buffer
    }
  }

  _syncResidentDock(open) {
    const corner = document.getElementById("legend-corner");
    if (!corner) return;
    const w0 = corner.offsetWidth;
    const h0 = corner.offsetHeight;
    document.body.classList.toggle("ts-chat-docked", !!open);
    // Only poke the renderer when the box actually changed size (on phones
    // the docked/undocked tiers are identical — a same-size setSize() still
    // re-allocates the drawing buffer, which can flash a frame).
    if (corner.offsetWidth === w0 && corner.offsetHeight === h0) return;
    try { window.dispatchEvent(new Event("resize")); } catch (_) {}
  }

  _onToggleSound() {
    const muted = lessonSound.toggleMuted();
    if (!muted) this._sfx("choice");
    this._syncSoundBtn();
  }

  _syncSoundBtn() {
    const d = this._dom;
    if (!d || !d.soundBtn) return;
    const muted = lessonSound.isMuted();
    d.soundBtn.classList.toggle("is-muted", muted);
    d.soundBtn.setAttribute("aria-label", muted ? "Unmute lesson sounds" : "Mute lesson sounds");
  }

  _scrollToCurrent() {
    // Single-focus board (Task #251): cards are stacked and cross-faded, so the
    // board itself never scrolls. Kept as a no-op so existing callers are safe.
  }

  // Narration now STREAMS into the left chat rail as bubbles (Brilliant-style)
  // instead of a single bottom caption. Each legend line the resident speaks is
  // a left bubble; each learner question is a right bubble. They accumulate and
  // auto-scroll, so the lesson reads like a conversation.
  /* ──────────────────────────────────────────────────────────────────────
   * Task #348 — the board writes itself: fragment staging, reveal effects,
   * chalk foley, default staging from the alignment, captions. Grammar +
   * degradation rules: docs/learning-platform/CUE_GRAMMAR_NOTES.md.
   * ────────────────────────────────────────────────────────────────────── */

  _prefersReducedMotion() {
    try {
      if (!this._rmMql && typeof matchMedia === "function") {
        this._rmMql = matchMedia("(prefers-reduced-motion: reduce)");
      }
      return !!(this._rmMql && this._rmMql.matches);
    } catch (_) { return false; }
  }

  // Hide the current teaching card's fragments so the narration can reveal
  // them ("the board writes itself"). Staging is skipped ENTIRELY — today's
  // all-at-once board — in preview, under prefers-reduced-motion, in Lab
  // (non-standalone) mode, and for kinds outside AUTO_STAGE_KINDS unless the
  // author staged them explicitly with `at`/`fx`. A lesson can never break by
  // being old: an unstaged board renders exactly as before this task.
  _stageCurrentCard(index, beat) {
    const d = this._dom;
    const card = d && d.board.querySelector('[data-beat-card="' + index + '"]');
    this._stageCardEl(card, beat, index);
  }

  // Element-parameterized core: beat cards pass their [data-beat-card] node;
  // the remedy detour (Task #354) passes its LOOSE card with cardIdx=this.idx
  // so every stale-card guard (_revealFrag emit, _armAutoReveals) treats the
  // remedy exactly like the interaction it detours from — navigation bumps
  // the generation + re-arms, so its timers/cues can never leak forward.
  _stageCardEl(card, beat, cardIdx) {
    // New generation: any delayed verb-completion emit still pending from a
    // PREVIOUS staging is invalidated the moment we (re)stage — see the gen
    // guard in _revealFrag.
    this._revealGen = (this._revealGen | 0) + 1;
    this._revealPlan = null;
    this._revealFrags = null;
    this._revealByContainer = null;
    this._revealCardIdx = -1;
    this._cardDoneSent = false;
    if (!beat || beat.kind === "problem" || beat.kind === "game" || !beat.board) return;
    if (!this._standalone || this._previewMode) return;
    if (this._prefersReducedMotion()) return;
    if (!card) return;
    let plan = null;
    try { plan = boardRevealPlan(beat.board); } catch (_) { return; }
    if (!plan || !plan.fragments.length) return;
    // Explicit authoring — an `at` anchor OR a picked verb — opts ANY kind in
    // (including completion/leagues, which never auto-stage); otherwise only
    // auto-stageable kinds with narration stage.
    const authoredStaging = plan.fragments.some((f) => f.at !== null || f.fxAuthored);
    if (!authoredStaging && !(AUTO_STAGE_KINDS.has(beat.board.kind) && beat.say)) return;
    const groups = [];
    const byContainer = new Map();
    plan.fragments.forEach((f, i) => {
      const els = Array.from(card.querySelectorAll('[data-frag="' + i + '"]'));
      els.forEach((el) => {
        el.classList.remove("ts-fx-write", "ts-fx-draw", "ts-fx-fade");
        el.classList.add("ts-frag-staged");
        el.setAttribute("aria-hidden", "true");
      });
      groups.push({ f, els, revealed: els.length === 0, emitted: false });
      if (f.container) {
        const arr = byContainer.get(f.container) || [];
        arr.push(i);
        byContainer.set(f.container, arr);
      }
    });
    this._revealPlan = plan;
    this._revealFrags = groups;
    this._revealByContainer = byContainer;
    this._revealCardIdx = cardIdx;
  }

  // The ONE reveal performer, registered for "*" — projects EVERY cue
  // (authored anchors AND runtime-synthesized default staging) onto staged
  // fragments. Idempotent per fragment. Scheduler modes map to: live/event →
  // animated verb + chalk foley; start/flush/project → instant final state.
  _onRevealCue(payload) {
    const cue = payload && payload.cue;
    if (!cue) return;
    const animated = payload.mode === "live" || payload.mode === "event";
    // Task #351 — a cue whose container is an authored direction entry
    // ({verb, at}) is a PERFORMANCE, not board state: it plays only when the
    // moment actually happens (live/event). Projections and flushes
    // (start/flush/project) rebuild board STATE — replaying a gesture there
    // would be flailing, not teaching, and would break performer idempotence.
    if (cue.container && typeof cue.container.verb === "string") {
      if (animated) this._performAuthoredVerb(cue.container.verb, payload.beatIndex, cue.id);
      return;
    }
    // Task #352 — a cue whose container is a plate focus entry ({region, at})
    // moves the plate camera to that named manifest rect: a slow pan/zoom
    // when the moment happens live, an instant cut when state is being
    // rebuilt (start/flush/project) or under reduced motion. Focus is board
    // activity — the legend turns toward the plate like any reveal.
    if (cue.container && typeof cue.container.region === "string" &&
        !("verb" in cue.container) && !("mark" in cue.container)) {
      const moved = this._firePlateFocusCue(cue.container, animated);
      if (moved && animated && this._direction) {
        try { this._direction.onBoardActivity("reveal"); } catch (_) {}
      }
      return;
    }
    let revealed = false;
    if (this._revealFrags && this._revealByContainer) {
      const idxs = this._revealByContainer.get(cue.container);
      if (idxs && idxs.length) {
        revealed = true;
        for (const i of idxs) this._revealFrag(i, animated ? "live" : "instant");
      }
    }
    // Task #350 — the SAME cue stream drives the lecturer's hand: a cue whose
    // container is an authored mark dict fires that mark.
    let marked = false;
    if (this._marksByContainer) {
      const mIdxs = this._marksByContainer.get(cue.container);
      if (mIdxs && mIdxs.length) {
        marked = true;
        for (const i of mIdxs) this._fireMark(i, animated);
      }
    }
    // Task #351 — the derived defaults: the board just did something LIVE, so
    // the legend turns toward it (reveals alternate point/gaze, marks get a
    // glance). Heavily rate-capped in the direction layer so a dense cue
    // stream reads as one deliberate turn, never a flail.
    if (animated && this._direction && (revealed || marked)) {
      try {
        this._direction.onBoardActivity(marked && !revealed ? "mark" : "reveal");
      } catch (_) {}
    }
  }

  _revealFrag(i, mode, allowEmit = true) {
    const groups = this._revealFrags;
    const g = groups && groups[i];
    if (!g || g.revealed) return;
    g.revealed = true;
    const fx = mode === "live" ? (g.f.fx || "write") : "none";
    for (const el of g.els) {
      el.classList.remove("ts-frag-staged");
      el.removeAttribute("aria-hidden");
      // The fx class STAYS on the element (animation fill keeps the final
      // frame). Removing it would swap `animation` back to the legacy entry
      // animation and visibly re-run it; _stageCurrentCard strips stale fx
      // classes whenever it (re)stages a card.
      if (fx !== "none") el.classList.add("ts-fx-" + fx);
    }
    if (fx === "write" || fx === "draw") this._playChalk();
    if (!allowEmit) return;
    const gen = this._revealGen;
    const emit = () => {
      // Generation guard: navigation bumps _revealGen (finalize + re-stage),
      // so a delayed stroke-completion from a PREVIOUS staging can never emit
      // into the newly armed beat (a same-id `after` chain there would fire
      // early). Comparing _revealCardIdx to this.idx alone is NOT enough —
      // after _goTo both sides describe the NEW beat and the stale timer
      // would pass.
      if (gen !== this._revealGen) return;
      if (this._revealCardIdx !== this.idx) return; // stale card — never chain
      try { if (g.f.id && !g.emitted) { g.emitted = true; this._cues.onBoardEvent(g.f.id); } } catch (_) {}
      this._maybeEmitCardDone();
    };
    // A LIVE write finishes when the stroke does — after-chains ("the moment
    // the equation lands") wait for the verb, instant projections don't.
    if (mode === "live" && fx !== "none") setTimeout(emit, FX_MS[fx] || 0);
    else emit();
  }

  _maybeEmitCardDone() {
    const plan = this._revealPlan;
    if (!plan || !plan.cardId || this._cardDoneSent) return;
    if (!this._revealFrags || !this._revealFrags.every((g) => g.revealed)) return;
    this._cardDoneSent = true;
    try { this._cues.onBoardEvent(plan.cardId); } catch (_) {}
  }

  // Player ceremony Iteration 2 (A1) — card-done for a board that staging
  // did NOT take (unstaged kinds such as completion/leagues, legacy boards,
  // preview/reduced-motion). The board's authored id is the event name, the
  // same one a staged card emits after its last fragment. One emit per
  // arming (_cardDoneSent resets in _stageCardEl on every _goTo). Called at
  // natural line end (say-bearing boards) or on land (say-less boards).
  _emitUnstagedCardDone(beatIndex) {
    if (!this.lesson || !this.lesson.beats) return false;
    if (beatIndex !== this.idx) return false;
    const beat = this.lesson.beats[beatIndex];
    const id = beat && beat.board && beat.kind !== "problem" && beat.kind !== "game" &&
      typeof beat.board.id === "string" ? beat.board.id : "";
    if (!id || this._cardDoneSent) return false;
    // Staged cards own their card-done (emitted when the last fragment lands).
    if (this._revealPlan && this._revealCardIdx === beatIndex) return false;
    this._cardDoneSent = true;
    try { this._cues.onBoardEvent(id); } catch (_) {}
    return true;
  }

  // Snap every still-staged fragment to its final visible state. emitEvents
  // is true only when the CURRENT beat's line is being completed in place
  // (interrupt / no-speaker) so chains finish; navigation passes false — the
  // scheduler was re-armed and stale events must not fire into the new beat.
  _finalizeStagedNow(emitEvents = false) {
    // Task #350 — marks finalize at the same moments staging does (interrupt /
    // no-speaker / navigation-away), and must do so even when NOTHING was
    // staged (marks arm independently of staging), so this runs before the
    // groups bail-out.
    this._finalizeMarksNow();
    // Task #352 — plate focus finalizes with them: the camera snaps to the
    // last authored focus state so an interrupted line still lands the shot.
    this._finalizePlateFocusNow();
    const groups = this._revealFrags;
    if (!groups) return;
    // Invalidate every pending delayed emit from live reveals FIRST: on
    // navigation (emitEvents=false) they must never fire later; on in-place
    // completion (emitEvents=true) the catch-up below emits synchronously
    // instead, so the cancelled timers can't double-fire.
    this._revealGen = (this._revealGen | 0) + 1;
    for (let i = 0; i < groups.length; i++) this._revealFrag(i, "instant", emitEvents);
    if (!emitEvents) return;
    // Catch-up: fragments revealed LIVE whose stroke-completion timer we just
    // cancelled still owe their board event — chains finish in place.
    for (const g of groups) {
      if (g.revealed && !g.emitted && g.f && g.f.id) {
        g.emitted = true;
        try { this._cues.onBoardEvent(g.f.id); } catch (_) {}
      }
    }
    this._maybeEmitCardDone();
  }

  // Default staging (Law 3 — retrofit before invent): anchor-less staged
  // fragments get runtime-synthesized, fully-pinned cues spread across the
  // line's word timeline — fragment 0 on word 0, the k-th at floor(k·W/n) —
  // so every current-catalog lesson performs without re-authoring. Explicit
  // authored anchors always win (their fragments never reach this list). No
  // usable alignment → those fragments snap visible NOW (today's board).
  _armAutoReveals(timing) {
    const groups = this._revealFrags;
    if (!groups || this._revealCardIdx !== this.idx) return;
    const pending = [];
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      if (!g.revealed && !g.f.at && !g.f.container) pending.push(i);
    }
    if (!pending.length) return;
    const words = timing && timing.kind === "words" && Array.isArray(timing.words) &&
      timing.words.length && typeof timing.hash === "string" ? timing.words : null;
    if (!words) {
      // No usable alignment: degradation law says all-at-once — but a verb
      // the author PICKED still performs (at line start); only default-staged
      // fragments snap straight to final state.
      for (const i of pending) {
        this._revealFrag(i, groups[i].f.fxAuthored ? "live" : "instant");
      }
      return;
    }
    const W = words.length;
    const n = pending.length;
    const cues = [];
    for (let k = 0; k < n; k++) {
      const fragIdx = pending[k];
      const wi = Math.min(W - 1, k === 0 ? 0 : Math.floor((k * W) / n));
      const container = groups[fragIdx]; // the group object IS the cue container
      this._revealByContainer.set(container, [fragIdx]);
      cues.push({
        path: "auto:frag#" + fragIdx,
        container,
        at: { word: wi, tMs: Math.max(0, Math.round(words[wi].startMs || 0)), align: timing.hash },
      });
    }
    try { this._cues.armRuntimeCues(cues); } catch (_) {}
  }

  // fx-only staging with no narration to cue it: the card performs the moment
  // the learner lands on it — authored verbs animate once, defaulted fragments
  // appear instantly. Emits in place so after-chains among fragments finish.
  _revealStagedOnLand() {
    const groups = this._revealFrags;
    if (!groups || this._revealCardIdx !== this.idx) return;
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      this._revealFrag(i, g.f && g.f.fxAuthored ? "live" : "instant");
    }
  }

  // ── Task #350 — MARK: the lecturer's hand ────────────────────────────────
  // Authored {mark, target, at} dicts draw chalk emphasis over the REAL
  // rendered text as the anchored word is spoken. The renderer stamped inert
  // .ts-mk-<mi> spans (including inside KaTeX via \htmlClass); here we arm
  // them against the cue stream and draw the overlay strokes. Palette and
  // geometry are runtime-owned (Law 2). A2F/audio is never touched.

  // Arm the current card's marks. Called from _goTo after _stageCurrentCard.
  // Unlike staging, marks arm in preview (the scheduler's start-mode flush
  // applies them instantly) and on legacy unstaged boards; Lab (non-
  // standalone) mode leaves the stamps inert. Reduced motion jumps straight
  // to the final marked state on land (degradation law).
  _armMarks(index, beat) {
    const d = this._dom;
    const card = d && d.board.querySelector('[data-beat-card="' + index + '"]');
    this._armMarksEl(card, beat, index);
  }

  // Element-parameterized core (see _stageCardEl): the remedy detour arms the
  // lecturer's hand on its loose card with cardIdx=this.idx.
  _armMarksEl(card, beat, cardIdx) {
    if (this._markResize) {
      try { this._markResize.disconnect(); } catch (_) {}
      this._markResize = null;
    }
    this._markPlan = null;
    this._marks = null;
    this._marksByContainer = null;
    this._markCardIdx = -1;
    if (!beat || beat.kind === "problem" || beat.kind === "game" || !beat.board) return;
    if (!this._standalone) return;
    if (!card) return;
    let plan = null;
    try { plan = boardMarkPlan(beat.board); } catch (_) { return; }
    if (!plan || !plan.marks.length) return;
    // Deterministic replay (Trap 3): revisiting a beat re-arms from scratch —
    // clear any overlay/dim state a previous visit left on this card.
    try {
      card.querySelectorAll("svg.ts-mark-layer").forEach((el) => el.remove());
      card.querySelectorAll(".ts-mark-t.is-on").forEach((el) =>
        el.classList.remove("is-on", "is-instant"));
    } catch (_) {}
    const marks = [];
    const byContainer = new Map();
    plan.marks.forEach((m, i) => {
      const els = Array.from(card.querySelectorAll(".ts-mk-" + m.mi));
      marks.push({ m, els, applied: false, nodes: [] });
      if (m.container) {
        const arr = byContainer.get(m.container) || [];
        arr.push(i);
        byContainer.set(m.container, arr);
      }
    });
    this._markPlan = plan;
    this._marks = marks;
    this._marksByContainer = byContainer;
    this._markCardIdx = cardIdx;
    if (this._prefersReducedMotion()) {
      for (let i = 0; i < marks.length; i++) this._fireMark(i, false);
      return;
    }
    // Overlay strokes are drawn in the card's CONTENT coordinates, so
    // scrolling never moves them — only reflow (chat-split drag, viewport
    // resize) does. Redraw applied marks in place when the card resizes.
    if (typeof ResizeObserver === "function") {
      let raf = 0;
      this._markResize = new ResizeObserver(() => {
        if (raf) return;
        raf = requestAnimationFrame(() => { raf = 0; this._repositionMarks(); });
      });
      try { this._markResize.observe(card); } catch (_) {}
    }
  }

  // Apply one mark. Idempotent (a mark fires once per arming). animated=true
  // (live/event cue) draws the stroke on and plays the chalk foley; anything
  // else (start/flush/project, reduced motion, finalize) jumps to the final
  // state silently.
  _fireMark(i, animated) {
    const marks = this._marks;
    const rec = marks && marks[i];
    if (!rec || rec.applied) return;
    rec.applied = true;
    // Never mark invisible text: if the target's fragment is still staged the
    // narration hasn't written it yet — snap it to final state first (the
    // hand can't circle words that aren't on the board).
    for (const el of rec.els) {
      const fragEl = el.closest ? el.closest("[data-frag]") : null;
      if (fragEl && fragEl.classList.contains("ts-frag-staged")) {
        const fi = parseInt(fragEl.getAttribute("data-frag"), 10);
        if (Number.isFinite(fi) && this._revealFrags) this._revealFrag(fi, "instant");
        else {
          fragEl.classList.remove("ts-frag-staged");
          fragEl.removeAttribute("aria-hidden");
        }
      }
    }
    const live = !!animated && !this._prefersReducedMotion();
    if (rec.m.style === "dim") {
      // dim is pure CSS on the stamped span — the text recedes (no overlay,
      // no foley: nothing is being written).
      for (const el of rec.els) {
        if (!live) el.classList.add("is-instant");
        el.classList.add("is-on");
      }
      return;
    }
    const drawn = this._drawMarkOverlay(rec, live);
    if (drawn && live) this._playChalk();
  }

  // Draw the chalk stroke(s) for one applied mark: hl = one ellipse around
  // the target's union box; term/warn = an underline per rendered line box.
  // The SVG layer is hosted inside the equation scroller when the target is
  // KaTeX (so the stroke scrolls WITH the math), else on the card itself.
  _drawMarkOverlay(rec, live) {
    const els = rec.els.filter((el) => el.isConnected);
    if (!els.length) return false;
    const d = this._dom;
    const card = d && d.board.querySelector('[data-beat-card="' + this._markCardIdx + '"]');
    if (!card) return false;
    const SVGNS = "http://www.w3.org/2000/svg";
    const host = els[0].closest(".ts-equation") || card;
    const rects = [];
    for (const el of els) {
      const list = el.getClientRects ? el.getClientRects() : [];
      for (const r of list) if (r.width > 0 && r.height > 0) rects.push(r);
    }
    // No boxes (headless / display:none) — nothing to draw, and don't leave
    // an empty layer behind.
    if (!rects.length) return false;
    let layer = null;
    try { layer = host.querySelector(":scope > svg.ts-mark-layer"); } catch (_) {}
    if (!layer) {
      layer = document.createElementNS(SVGNS, "svg");
      layer.setAttribute("class", "ts-mark-layer");
      layer.setAttribute("aria-hidden", "true");
      host.appendChild(layer);
    }
    // Chrome refuses to paint a zero-size SVG root even with overflow:visible,
    // so size the layer to the host's full scroll extent on every draw. Zero it
    // first so a stale large layer can't hold the scroll extent open after the
    // content shrinks (inline style also beats the generic card-child rules).
    layer.style.width = "0px";
    layer.style.height = "0px";
    layer.style.width = Math.max(1, host.scrollWidth || 0) + "px";
    layer.style.height = Math.max(1, host.scrollHeight || 0) + "px";
    // Content-space offsets come from the layer's own box — self-correcting
    // for borders, padding and current scroll, whatever the containing block.
    const layerR = layer.getBoundingClientRect();
    const ox = -layerR.left;
    const oy = -layerR.top;
    const style = rec.m.style;
    const nodes = [];
    if (style === "hl") {
      let L = Infinity, T = Infinity, R = -Infinity, B = -Infinity;
      for (const r of rects) {
        L = Math.min(L, r.left); T = Math.min(T, r.top);
        R = Math.max(R, r.right); B = Math.max(B, r.bottom);
      }
      const cx = (L + R) / 2 + ox;
      const cy = (T + B) / 2 + oy;
      const rx = Math.max(16, (R - L) / 2 + 10);
      const ry = Math.max(12, (B - T) / 2 + 6);
      const p = document.createElementNS(SVGNS, "path");
      p.setAttribute("class", "ts-mark-stroke ts-mkp-hl");
      p.setAttribute("d", this._ellipsePath(cx, cy, rx, ry));
      // A slight tilt reads as a hand, not a compass. Deterministic — no
      // randomness anywhere (goldframe stills must be byte-stable).
      p.setAttribute("transform", "rotate(-2 " + cx + " " + cy + ")");
      nodes.push(p);
    } else {
      for (const r of rects) {
        const y = r.bottom + oy - Math.max(1.5, r.height * 0.05);
        const p = document.createElementNS(SVGNS, "path");
        p.setAttribute("class", "ts-mark-stroke ts-mkp-" + style);
        p.setAttribute("d", "M " + (r.left + ox) + " " + y +
          " Q " + (r.left + r.width / 2 + ox) + " " + (y + 2.5) + " " +
          (r.right + ox) + " " + y);
        nodes.push(p);
      }
    }
    for (const p of nodes) {
      layer.appendChild(p);
      let len = 300;
      try { if (p.getTotalLength) len = Math.max(1, Math.ceil(p.getTotalLength())); } catch (_) {}
      p.style.strokeDasharray = String(len);
      if (live) {
        // Paint fully offset first, then let the CSS transition draw the
        // stroke on — double rAF so the initial state actually commits.
        p.style.strokeDashoffset = String(len);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          p.classList.add("is-drawn");
          p.style.strokeDashoffset = "0";
        }));
      } else {
        p.classList.add("is-instant");
        p.style.strokeDashoffset = "0";
      }
    }
    rec.nodes = nodes;
    return true;
  }

  // Cubic-bezier ellipse (deterministic; k = the circle constant).
  _ellipsePath(cx, cy, rx, ry) {
    const k = 0.5522847498;
    const kx = rx * k;
    const ky = ry * k;
    return "M " + (cx - rx) + " " + cy +
      " C " + (cx - rx) + " " + (cy - ky) + " " + (cx - kx) + " " + (cy - ry) + " " + cx + " " + (cy - ry) +
      " C " + (cx + kx) + " " + (cy - ry) + " " + (cx + rx) + " " + (cy - ky) + " " + (cx + rx) + " " + cy +
      " C " + (cx + rx) + " " + (cy + ky) + " " + (cx + kx) + " " + (cy + ry) + " " + cx + " " + (cy + ry) +
      " C " + (cx - kx) + " " + (cy + ry) + " " + (cx - rx) + " " + (cy + ky) + " " + (cx - rx) + " " + cy;
  }

  // Reflow re-draw: strokes are geometry snapshots — when the card's box
  // changes, redraw every APPLIED mark in place (instant, no foley).
  _repositionMarks() {
    const marks = this._marks;
    if (!marks) return;
    for (const rec of marks) {
      if (!rec.applied || !rec.nodes || !rec.nodes.length) continue;
      for (const n of rec.nodes) { try { n.remove(); } catch (_) {} }
      rec.nodes = [];
      this._drawMarkOverlay(rec, false);
    }
  }

  // Snap every un-applied mark to its final state (same moments as reveal
  // finalize: interrupt / no-speaker / no-say land / navigation). Marks never
  // emit board events, so there is no emit split; navigation-away re-arms on
  // the next _goTo, which resets this card's mark state for replay.
  _finalizeMarksNow() {
    const marks = this._marks;
    if (!marks) return;
    for (let i = 0; i < marks.length; i++) this._fireMark(i, false);
  }

  /* Task #352 — the plate camera (primary sources on the board). A plate
   * board's `focus` entries name CURATED manifest rects; the runtime owns
   * every coordinate and easing (Law 2 — authors never type numbers). The
   * transform is percentage-based translate + scale on the img inside the
   * clipped viewport, so reflow (chat-split drag, resize) keeps an applied
   * focus perfectly framed with zero listeners. */
  _armPlateFocus(index, beat) {
    const d = this._dom;
    const card = d && d.board.querySelector('[data-beat-card="' + index + '"]');
    this._armPlateFocusEl(card, beat, index);
  }

  // Element-parameterized core (see _stageCardEl): a remedy whose board is a
  // resolved plate arms the same camera on its loose card (cardIdx=this.idx).
  _armPlateFocusEl(card, beat, cardIdx) {
    this._plateFocus = null;
    if (!beat || beat.kind === "problem" || beat.kind === "game" || !beat.board) return;
    const board = beat.board;
    if (board.kind !== "plate") return;
    if (!this._standalone) return; // Lab mode: static plate, camera inert
    if (!card) return;
    const vp = card.querySelector(".ts-plate-viewport");
    const img = vp && vp.querySelector("img.ts-plate-img");
    // Deterministic replay (Trap 3): revisiting the beat re-arms from scratch
    // — reset any camera state a previous visit left behind.
    if (img) {
      img.classList.add("is-instant");
      img.style.transform = "";
    }
    if (vp) vp.removeAttribute("data-plate-focus");
    const res = board._resolved && typeof board._resolved === "object"
      ? board._resolved : null;
    const focus = Array.isArray(board.focus) ? board.focus : [];
    // Degraded plate (caption card — no viewport/resolution) or no authored
    // focus → nothing to arm; cues into a missing camera are silent no-ops.
    if (!vp || !img || !res || !focus.length) return;
    const steps = [];
    const byContainer = new Map();
    focus.forEach((f, i) => {
      const region = f && typeof f === "object" && typeof f.region === "string"
        ? f.region : null;
      steps.push({ region, fired: false });
      if (region) {
        const arr = byContainer.get(f) || [];
        arr.push(i);
        byContainer.set(f, arr);
      }
    });
    this._plateFocus = { cardIdx, steps, byContainer, viewport: vp, img, resolved: res };
  }

  // Cue router hook: the container IS the authored focus entry (identity
  // match, exactly like reveal fragments and marks). Returns true when a
  // camera move was ours to make.
  _firePlateFocusCue(container, animated) {
    const st = this._plateFocus;
    if (!st || !st.byContainer) return false;
    const idxs = st.byContainer.get(container);
    if (!idxs || !idxs.length) return false;
    for (const i of idxs) this._firePlateFocusIdx(i, animated);
    return true;
  }

  // Apply one focus step. Idempotent per arming (a step fires once — seek
  // projections replay the whole ladder in order after a fresh re-arm, so
  // the LAST passed focus wins deterministically).
  _firePlateFocusIdx(i, animated) {
    const st = this._plateFocus;
    const step = st && st.steps && st.steps[i];
    if (!step || step.fired || !step.region) return;
    step.fired = true;
    if (st.cardIdx !== this.idx) return; // stale card — never move its camera
    this._applyPlateFocus(step.region, animated);
  }

  // The camera math. Region rects live in STORED-IMAGE pixels (integer, from
  // the signed manifest); the img is laid out at exactly the viewport size
  // (the viewport's aspect-ratio IS the image's), so normalized coordinates
  // convert to percentage translate of the img's own box — resize-proof.
  // Scale fills ~86% of the frame with the region (1.16 breathing margin),
  // clamped to [1, 4]; translate centers the region and clamps to the plate's
  // edges so the camera never shows past the paper.
  _applyPlateFocus(regionName, animated) {
    const st = this._plateFocus;
    if (!st || !st.viewport || !st.img || !st.resolved) return;
    const live = !!animated && !this._prefersReducedMotion();
    let tf = "translate(0%, 0%) scale(1)";
    if (regionName !== "full") {
      const regions = st.resolved.regions || {};
      const r = regions[regionName];
      const W = Number(st.resolved.w) || 0;
      const H = Number(st.resolved.h) || 0;
      // Unknown region at play (manifest re-curated after publish): the
      // publish gate owns that error — the runtime never guesses; the plate
      // simply holds its current shot.
      if (!r || !(W > 0) || !(H > 0)) return;
      const rw = Math.max(Number(r.w) || 0, 1) / W;
      const rh = Math.max(Number(r.h) || 0, 1) / H;
      const cx = ((Number(r.x) || 0) + (Number(r.w) || 0) / 2) / W;
      const cy = ((Number(r.y) || 0) + (Number(r.h) || 0) / 2) / H;
      const PAD = 1.16;
      let s = Math.min(1 / (rw * PAD), 1 / (rh * PAD));
      s = Math.max(1, Math.min(4, s));
      const fx = Math.min(0, Math.max(1 - s, 0.5 - s * cx));
      const fy = Math.min(0, Math.max(1 - s, 0.5 - s * cy));
      tf = "translate(" + (fx * 100).toFixed(3) + "%, " + (fy * 100).toFixed(3) +
        "%) scale(" + s.toFixed(4) + ")";
    }
    st.img.classList.toggle("is-instant", !live);
    st.img.style.transform = tf;
    st.viewport.setAttribute("data-plate-focus", regionName);
  }

  // No narration / interrupt / navigation-away: snap through the ladder to
  // the LAST authored focus instantly — the shot the author built toward.
  _finalizePlateFocusNow() {
    const st = this._plateFocus;
    if (!st || !st.steps) return;
    for (let i = 0; i < st.steps.length; i++) this._firePlateFocusIdx(i, false);
  }

  // Chalk foley — runtime-OWNED (authors never control sound; the write verb
  // triggers it). Isolated LessonSound clone path, NEVER the narration
  // pipeline. Rate-limited so dense reveals sound like strokes, not hail;
  // silent in film-quiet/preview and under reduced motion; lessonSound
  // itself honors the learner's persisted mute.
  _playChalk() {
    if (this._quiet || this._previewMode || this._prefersReducedMotion()) return;
    const now = Date.now();
    if (now - this._chalkLastAt < 170) return;
    this._chalkLastAt = now;
    try { lessonSound.chalk(); } catch (_) {}
  }

  /* Captions primitive (Trap 7) — a deaf student gets the full lecture. The
   * bar mirrors the CURRENT narration line; when the alignment's word count
   * matches the caption's whitespace tokens (count-guarded, CJK-safe — the
   * client never re-tokenizes for anchors, this is presentation-only) the
   * words pace with the scheduler's word ticks; otherwise the whole line
   * shows at once. aria-hidden: SR users already get the same text via the
   * chat log's aria-live region + the reveal-ordered board. */
  _renderCaptionBar(text) {
    const d = this._dom;
    if (!d || !d.captions) return;
    this._capSpans = null;
    this._capKaraoke = false;
    d.captions.classList.remove("is-karaoke");
    const t = (text == null ? "" : String(text)).trim();
    if (!this._captionsOn || !t) {
      d.captions.hidden = true;
      if (d.captionsLine) d.captionsLine.textContent = "";
      return;
    }
    const line = d.captionsLine;
    line.textContent = "";
    const toks = t.split(/\s+/);
    const spans = [];
    for (let i = 0; i < toks.length; i++) {
      if (i) line.appendChild(document.createTextNode(" "));
      const s = document.createElement("span");
      s.className = "ts-cap-word";
      s.textContent = toks[i];
      line.appendChild(s);
      spans.push(s);
    }
    this._capSpans = spans;
    d.captions.hidden = false;
  }

  _armCaptionWords(timing) {
    this._capKaraoke = false;
    const d = this._dom;
    if (d && d.captions) d.captions.classList.remove("is-karaoke");
    const spans = this._capSpans;
    if (!spans || !spans.length || !this._captionsOn) return;
    const words = timing && Array.isArray(timing.words) ? timing.words : null;
    if (words && words.length === spans.length) {
      this._capKaraoke = true;
      if (d && d.captions) d.captions.classList.add("is-karaoke");
    }
  }

  _onWordTick(e) {
    if (!this._capKaraoke || !this._capSpans || !e) return;
    const upTo = Math.min(e.index | 0, this._capSpans.length - 1);
    for (let i = 0; i <= upTo; i++) {
      const s = this._capSpans[i];
      if (!s.classList.contains("is-said")) s.classList.add("is-said");
    }
  }

  _onToggleCaptions() {
    this._captionsOn = !this._captionsOn;
    try { localStorage.setItem("legendlabs.learnCaptions", this._captionsOn ? "on" : "off"); } catch (_) {}
    this._syncCaptionsBtn();
    const beat = this.lesson && this.lesson.beats && this.lesson.beats[this.idx];
    this._renderCaptionBar(beat && beat.say ? beat.say : "");
    this._sfx("tapOn");
  }

  _syncCaptionsBtn() {
    const d = this._dom;
    if (!d || !d.ccBtn) return;
    d.ccBtn.classList.toggle("is-on", !!this._captionsOn);
    d.ccBtn.setAttribute("aria-pressed", this._captionsOn ? "true" : "false");
  }

  _setCaption(text) {
    this._bubbleGen = (this._bubbleGen || 0) + 1;
    const suppressBubble = !!this._suppressNextBubble;
    this._suppressNextBubble = false;
    if (!suppressBubble) this._appendBubble("legend", text);
    this._renderCaptionBar(suppressBubble ? "" : text);
    const d = this._dom;
    if (suppressBubble && d && d.speech) {
      d.speech.classList.remove("show");
      d.speech.hidden = true;
      if (d.speechText) d.speechText.textContent = "";
    }
    if (d && d.speech && text && !suppressBubble) {
      if (d.speechWho) d.speechWho.textContent = this.character || "";
      if (d.speechText) d.speechText.textContent = text;
      d.speech.hidden = false;
      d.speech.classList.add("show");
    }
  }

  _dismissNarrationBubble() {
    const gen = (this._bubbleGen = (this._bubbleGen || 0) + 1);
    const d = this._dom;
    if (!d) return;
    const bubbles = d.chat ? d.chat.querySelectorAll(".ts-bubble.is-legend") : [];
    const bubble = bubbles && bubbles.length ? bubbles[bubbles.length - 1] : null;
    if (bubble) bubble.classList.add("is-dismissing");
    if (d.speech) d.speech.classList.remove("show");
    setTimeout(() => {
      if (bubble && bubble.parentNode) bubble.remove();
      if (gen !== this._bubbleGen) return;
      if (d.speech) {
        d.speech.hidden = true;
        if (d.speechText) d.speechText.textContent = "";
      }
      this._lastBubbleLegendText = "";
    }, 180);
  }

  _drainPendingDirection(beatIndex) {
    if (!this._pendingDirection?.length) return true;
    // Director.tick runs during narration as well as between lines. It may
    // RETRY here, but must never dispatch a queued face-capable ceremony while
    // any speech clock/A2F/audio signal still owns the turn.
    if (this._directionSpeechActive()) return false;
    const pending = this._pendingDirection || [];
    this._pendingDirection = [];
    if (beatIndex !== this.idx) return false;
    const now = Date.now();
    let retained = false;
    for (const item of pending) {
      if (item.beatIndex !== beatIndex) continue;
      if (item.generation !== undefined && item.generation !== this._runToken) continue;
      if (item.deadline && now > item.deadline) continue;
      if (!this._dispatchAuthoredVerb(item.verb, item.context)) {
        this._pendingDirection.push(item);
        retained = true;
      } else if (item.cueId === "ceremony:celebrate") {
        // Reserve enough tail for future lesson/level/course variants without
        // implementing their rig. _runDefaultCeremony waits only the remainder.
        this._ceremonyPerformance = {
          generation: item.generation,
          startedAt: now,
          settleUntil: now + 3000,
        };
      }
    }
    return !retained;
  }

  // An authored (or player-issued) gesture verb: while a line is live it
  // waits in the SAME queue authored gestures use and plays when the line
  // ends naturally (an interrupt discards it); otherwise it plays now.
  // `celebrate` additionally lights the stage-wide celebration layer (B2).
  _performAuthoredVerb(verb, beatIndex, cueId, context) {
    if (typeof verb !== "string" || !verb) return;
    const isDefaultCeremony = cueId === "ceremony:celebrate";
    const completionBoard = this._completionArcBoard(verb, beatIndex, cueId);
    if (completionBoard && !this._bigCeremonyAllowed()) return;
    const deliveryContext = this._authoredPerformanceContext(
      completionBoard, context,
    );
    const item = {
      verb,
      beatIndex,
      cueId: cueId || ("verb:" + verb),
      generation: this._runToken,
      context: deliveryContext,
      // The default card is a short silent window. Keep retrying busy/rate
      // rejects throughout it, but never leak the gesture into the next screen.
      deadline: isDefaultCeremony ? Date.now() + 3900 : 0,
    };
    const speechActive = this._directionSpeechActive();
    if (speechActive || isDefaultCeremony) {
      this._pendingDirection.push(item);
      if (!speechActive) this._drainPendingDirection(beatIndex);
      return;
    }
    if (!this._dispatchAuthoredVerb(verb, deliveryContext)) {
      this._pendingDirection.push(item);
    }
  }

  _directionSpeechActive() {
    try {
      if (this._cues && typeof this._cues.isLineActive === "function" &&
          this._cues.isLineActive()) return true;
    } catch (_) {}
    try { if (A2FLipSyncDriver.isAnyActive()) return true; } catch (_) {}
    try { if (this._lineAudiblyStarted()) return true; } catch (_) {}
    try {
      if (this._reaction && this._reaction._speechActive) return true;
    } catch (_) {}
    try {
      const root = this._dom && this._dom.root;
      if (root && root.classList.contains("is-speaking")) return true;
    } catch (_) {}
    return false;
  }

  _performanceContext(fallbackTier) {
    let next = null;
    try { next = this._forwardControl(); } catch (_) {}
    // A pending course fetch uses "course" merely as a safe navigation
    // fallback; it is not evidence that the learner completed the course.
    const tier = next && !next.pending && next.kind === "course" ? "course"
      : next && !next.pending && next.kind === "level" ? "level"
        : (fallbackTier || "lesson");
    return {
      tier,
      courseId: this.courseId || "",
      lessonId: this.lessonId || "",
      lessonIndex: this.idx,
      lesson: this.lesson || null,
      course: this._course || null,
    };
  }

  /** Build the one normalized direction context. Explicit tier (including
   * lesson) wins. Otherwise only a verified completion arc may use the
   * completion board's scope. Only a player-synthesized board can fall back
   * to navigation: an omitted/invalid authored scope still means lesson. */
  _authoredPerformanceContext(completionBoard, explicitContext) {
    const base = this._performanceContext("lesson");
    if (explicitContext !== undefined) {
      const explicitTier = explicitContext &&
        (explicitContext.tier === "level" || explicitContext.tier === "course")
        ? explicitContext.tier : "lesson";
      return { ...base, ...explicitContext, tier: explicitTier };
    }
    if (!completionBoard) return { ...base, tier: "lesson" };
    const authoredTier = completionScope(completionBoard);
    return {
      ...base,
      tier: completionBoard.synthetic === "completion" && authoredTier === "lesson"
        ? base.tier : authoredTier,
    };
  }

  _dispatchAuthoredVerb(verb, context) {
    if (!this._direction) return false;
    try {
      const tier = context &&
        (context.tier === "level" || context.tier === "course")
        ? context.tier : "lesson";
      const deliveryContext = { ...(context || {}), tier };
      // Legacy test doubles returned undefined after performing; only an
      // explicit false means the rig rejected this attempt as busy/rate-gated.
      const performed = this._direction.onAuthoredVerb(
        verb, deliveryContext,
      ) !== false;
      if (performed && verb === "celebrate") this._playStageCelebration("verb");
      return performed;
    } catch (_) { return false; }
  }

  _appendAsk(q) {
    this._appendBubble("you", q);
  }

  _appendBubble(who, text) {
    const d = this._dom;
    if (!d || !d.chat || !text) return;
    const isYou = who === "you";
    // Pause/resume and replay re-speak the current beat — don't stack an
    // identical legend line back-to-back. A learner question breaks the run, so a
    // later identical legend line is no longer "consecutive" and should show.
    if (isYou) {
      this._lastBubbleLegendText = "";
    } else {
      if (this._lastBubbleLegendText === text) { this._scrollChat(); return; }
      this._lastBubbleLegendText = text;
    }
    const b = document.createElement("div");
    b.className = "ts-bubble " + (isYou ? "is-you" : "is-legend");
    if (!isYou) {
      const who2 = document.createElement("span");
      who2.className = "ts-bubble-who";
      who2.textContent = this.character || "";
      b.appendChild(who2);
    }
    const t = document.createElement("span");
    t.className = "ts-bubble-text";
    t.textContent = text;
    b.appendChild(t);
    d.chat.appendChild(b);
    this._scrollChat();
  }

  _scrollChat() {
    const d = this._dom;
    if (d && d.chat) d.chat.scrollTop = d.chat.scrollHeight;
  }

  _setSpeaking(on) {
    if (this._dom) this._dom.root.classList.toggle("is-speaking", !!on);
  }

  // Optional voice input for the rail's "How can I help?" field. Uses the
  // browser's built-in SpeechRecognition (Chrome/Safari); if unavailable the
  // mic button stays hidden rather than shipping a dead control.
  _initMic() {
    const d = this._dom;
    if (!d || !d.micBtn) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { d.micBtn.hidden = true; return; }
    d.micBtn.hidden = false;
    let rec = null;
    let listening = false;
    d.micBtn.addEventListener("click", () => {
      if (listening) { try { rec && rec.stop(); } catch (_) {} return; }
      try {
        rec = new SR();
        rec.lang = "en-US";
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
          const t = (e.results && e.results[0] && e.results[0][0] &&
            e.results[0][0].transcript) || "";
          if (t && d.askInput) { d.askInput.value = t; this._onAsk(); }
        };
        rec.onend = () => { listening = false; d.micBtn.classList.remove("is-live"); };
        rec.onerror = () => { listening = false; d.micBtn.classList.remove("is-live"); };
        rec.start();
        listening = true;
        d.micBtn.classList.add("is-live");
      } catch (_) {
        listening = false;
        d.micBtn.classList.remove("is-live");
      }
    });
  }

  _showResume(wasPlaying) {
    const d = this._dom;
    if (!d) return;
    d.resume.classList.add("show");
    d.resume.onclick = () => {
      d.resume.classList.remove("show");
      this.playing = wasPlaying;
      this._syncTransport();
      if (wasPlaying) this._goTo(this.idx, { autoplay: true });
    };
  }

  // Task #302 — the opening is theater, not a spinner: a shimmering board
  // skeleton assembles behind the legend's gathering line (see _playGathering).
  _renderLoading() {
    const d = this._dom;
    d.board.innerHTML =
      '<div class="ts-card is-in is-current ts-skel-card" aria-label="Preparing the lesson">' +
      '<div class="ts-skel ts-skel-kicker"></div>' +
      '<div class="ts-skel ts-skel-title"></div>' +
      '<div class="ts-skel ts-skel-line"></div>' +
      '<div class="ts-skel ts-skel-line is-short"></div>' +
      '<div class="ts-skel ts-skel-fig"></div>' +
      '<div class="ts-skel-note">' + this._esc(this.character) +
      " is preparing your lesson…</div>" +
      "</div>";
    if (d.title) d.title.textContent = "Just a moment";
    if (d.subtitle) d.subtitle.textContent = this.character + " · live lesson";
    if (d.segbar) d.segbar.innerHTML = "";
    if (d.count) d.count.textContent = "";
  }

  // Generation failure = a professor pausing, never a dead end. Retry is only
  // offered after a FAILED serve (allowRetry) and re-requests through the SAME
  // serve endpoint with fresh+after_error (revision #9 — fresh handling is
  // server policy; the regenerated lesson files into the store automatically).
  _renderError(allowRetry) {
    const d = this._dom;
    d.board.innerHTML =
      '<div class="ts-card is-in is-current ts-error-card">' +
      '<span class="ts-kicker">A brief pause</span>' +
      '<h3 class="ts-card-title">Even great minds misplace a note</h3>' +
      '<p class="ts-line">The lesson didn\'t come together this time. ' +
      "It happens to the best of us.</p>" +
      (allowRetry
        ? '<button class="ts-btn ts-btn-primary ts-retry" type="button">Try again</button>'
        : "") +
      "</div>";
    const btn = d.board.querySelector(".ts-retry");
    if (btn) {
      btn.addEventListener("click", () => {
        this._interruptSpeech();
        this._loadLesson(true);
      });
    }
  }

  _ensureDom() {
    if (this._dom) return;
    let root = document.getElementById("tutor-stage");
    if (!root) {
      root = document.createElement("div");
      root.id = "tutor-stage";
      document.body.appendChild(root);
    }
    root.dataset.focus = "board";
    root.innerHTML = `
      <div class="ts-celebrate-layer" aria-hidden="true"></div>
      <div class="ts-hud">
        <button class="ts-exit" type="button" aria-label="Exit lesson">
          <svg viewBox="0 0 24 24"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
        </button>
        <button class="ts-flag" type="button" aria-label="Report a problem with this question" title="Report a problem">
          <svg viewBox="0 0 24 24"><path d="M4 21V4M4 4h12l-2 4 2 4H4"/></svg>
        </button>
        <button class="ts-sound" type="button" aria-label="Toggle sound">
          <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>
        </button>
        <button class="ts-cc" type="button" aria-label="Toggle captions" aria-pressed="false">CC</button>
        <div class="ts-progress" role="progressbar" aria-label="Lesson progress">
          <div class="ts-segbar"></div>
        </div>
        <div class="ts-xp" aria-label="Experience points">
          <svg class="ts-xp-bolt" viewBox="0 0 24 24"><polygon points="13 2 4 14 11 14 10 22 20 9 13 9 13 2"/></svg>
          <span class="ts-xp-num">0</span>
        </div>
        <span class="ts-count" aria-hidden="true"></span>
      </div>

      <section class="ts-stage-board" aria-label="Lesson board">
        <div class="ts-board"></div>
        <aside class="ts-pinrail" hidden aria-label="Pinned facts" data-count="0">
          <h3 class="ts-pinrail-title">Pinned</h3>
          <ol class="ts-pinrail-list"></ol>
        </aside>
        <div class="ts-endnote">That's the lesson. Replay any moment, or ask me anything.</div>
      </section>

      <div class="ts-feedback" hidden aria-live="polite">
        <span class="ts-feedback-icon"></span>
        <span class="ts-feedback-text"></span>
        <span class="ts-feedback-xp"></span>
      </div>

      <div class="ts-actionbar">
        <button class="ts-continue ts-btn ts-btn-primary" type="button">Continue</button>
      </div>

      <div class="ts-speech" hidden aria-live="polite">
        <span class="ts-speech-who"></span>
        <span class="ts-speech-text"></span>
      </div>
      <div class="ts-captions" aria-hidden="true" hidden><span class="ts-captions-line"></span></div>
      <div class="ts-tutorbar">
        <button class="ts-chat-toggle" type="button" aria-label="Open conversation">
          <svg viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5Z"/></svg>
        </button>
        <button class="ts-mic" type="button" aria-label="Speak your question" hidden>
          <svg viewBox="0 0 24 24"><path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"/><path d="M19 11a7 7 0 0 1-14 0"/><line x1="12" y1="18" x2="12" y2="22"/></svg>
        </button>
      </div>

      <aside class="ts-chatpanel" hidden aria-label="Lesson conversation">
        <div class="ts-chatpanel-head">
          <div class="ts-chatpanel-head-text">
            <p class="ts-subtitle"></p>
            <h2 class="ts-title"></h2>
          </div>
          <button class="ts-chat-close" type="button" aria-label="Close conversation">×</button>
        </div>
        <div class="ts-chat" role="log" aria-live="polite"></div>
        <form class="ts-ask">
          <input class="ts-ask-input" type="text" placeholder="Ask a question…" aria-label="Ask a question" autocomplete="off" />
          <button class="ts-ask-send" type="submit" aria-label="Send">
            <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </form>
      </aside>

      <div class="ts-divider" aria-hidden="true"><span class="ts-divider-grip"></span></div>

      <button class="ts-resume" type="button">Resume the lesson →</button>
      <div class="ts-checkpoint"></div>
      <div class="ts-overlay" hidden></div>
    `;

    // Task #251 (Brilliant reskin): the resident portrait stays where boot put
    // it — the fixed #legend-corner, bottom-left — which IS the Brilliant tutor
    // position. The speech bubble + tutor controls sit just above it. No
    // re-parenting (which would risk the WebGL context).

    this._dom = {
      root,
      title: root.querySelector(".ts-title"),
      subtitle: root.querySelector(".ts-subtitle"),
      board: root.querySelector(".ts-board"),
      pinrail: root.querySelector(".ts-pinrail"),
      pinrailList: root.querySelector(".ts-pinrail-list"),
      segbar: root.querySelector(".ts-segbar"),
      progress: root.querySelector(".ts-progress"),
      count: root.querySelector(".ts-count"),
      xpPill: root.querySelector(".ts-xp"),
      xpNum: root.querySelector(".ts-xp-num"),
      actionbar: root.querySelector(".ts-actionbar"),
      continueBtn: root.querySelector(".ts-continue"),
      feedback: root.querySelector(".ts-feedback"),
      feedbackIcon: root.querySelector(".ts-feedback-icon"),
      feedbackText: root.querySelector(".ts-feedback-text"),
      feedbackXp: root.querySelector(".ts-feedback-xp"),
      speech: root.querySelector(".ts-speech"),
      speechWho: root.querySelector(".ts-speech-who"),
      speechText: root.querySelector(".ts-speech-text"),
      tutorbar: root.querySelector(".ts-tutorbar"),
      chatToggle: root.querySelector(".ts-chat-toggle"),
      soundBtn: root.querySelector(".ts-sound"),
      ccBtn: root.querySelector(".ts-cc"),
      captions: root.querySelector(".ts-captions"),
      captionsLine: root.querySelector(".ts-captions-line"),
      chatPanel: root.querySelector(".ts-chatpanel"),
      chatClose: root.querySelector(".ts-chat-close"),
      divider: root.querySelector(".ts-divider"),
      chat: root.querySelector(".ts-chat"),
      checkpoint: root.querySelector(".ts-checkpoint"),
      endNote: root.querySelector(".ts-endnote"),
      celebrateLayer: root.querySelector(".ts-celebrate-layer"),
      overlay: root.querySelector(".ts-overlay"),
      resume: root.querySelector(".ts-resume"),
      exitBtn: root.querySelector(".ts-exit"),
      askForm: root.querySelector(".ts-ask"),
      askInput: root.querySelector(".ts-ask-input"),
      micBtn: root.querySelector(".ts-mic"),
      flagBtn: root.querySelector(".ts-flag"),
    };
    const d = this._dom;
    d.exitBtn.addEventListener("click", () => this._confirmExit());
    d.continueBtn.addEventListener("click", () => this._onContinue());
    d.askForm.addEventListener("submit", (e) => { e.preventDefault(); this._onAsk(); });
    if (d.flagBtn) d.flagBtn.addEventListener("click", () => this._onFlag());
    d.chatToggle.addEventListener("click", () => this._toggleChatPanel());
    d.chatClose.addEventListener("click", () => this._toggleChatPanel(false));
    d.soundBtn.addEventListener("click", () => this._onToggleSound());
    if (d.ccBtn) d.ccBtn.addEventListener("click", () => this._onToggleCaptions());
    this._syncSoundBtn();
    this._syncCaptionsBtn();
    this._renderXp(true);
    this._initMic();
    this._initDivider();
  }

  _initDivider() {
    const d = this._dom;
    if (!d || !d.divider || !d.root) return;
    // Clamp any split width to the CURRENT viewport so a value saved on a wide
    // screen can never push the lesson column off a narrower one.
    const clampSplit = (px) => {
      const w = window.innerWidth || d.root.getBoundingClientRect().width || 1024;
      const min = 340;
      const max = Math.max(min + 120, w * 0.6);
      return Math.min(max, Math.max(min, px));
    };
    const applySplit = (px) => {
      const c = clampSplit(px);
      d.root.style.setProperty("--ts-split", c + "px");
      this._splitPx = c;
      return c;
    };
    try {
      const saved = parseInt(localStorage.getItem("legendlabs.lessonSplit") || "", 10);
      if (saved > 0) applySplit(saved);
    } catch (_) {}
    let dragging = false;
    const onMove = (e) => {
      if (!dragging) return;
      const rect = d.root.getBoundingClientRect();
      const cx = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
      applySplit(cx - rect.left);
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      d.divider.classList.remove("is-dragging");
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (this._splitPx) {
        try { localStorage.setItem("legendlabs.lessonSplit", String(Math.round(this._splitPx))); } catch (_) {}
      }
    };
    d.divider.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      dragging = true;
      d.divider.classList.add("is-dragging");
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    });
    // Re-clamp a persisted split when the window shrinks so the lesson column
    // can never be pushed off a now-narrower screen.
    window.addEventListener("resize", () => {
      if (this._splitPx && d.root.classList.contains("ts-chat-open")) applySplit(this._splitPx);
    });
  }

  _show() {
    this._dom.root.classList.add("is-open");
    document.body.classList.add("tutor-stage-open");
    this.isOpen = true;
    // Task #302 (hall mode) — ONE surface: trap keyboard focus inside the
    // stage and shift the legend into the free left column beside the board.
    if (this._hall) {
      this._prevFocus = document.activeElement || null;
      this._trapOn();
      this._applyStageCamera(true);
      if (!this._onStageResize) {
        this._onStageResize = () => {
          if (this.isOpen && this._hall) this._applyStageCamera(true);
        };
      }
      window.addEventListener("resize", this._onStageResize);
    }
  }

  _hide() {
    if (this._dom) this._dom.root.classList.remove("is-open");
    document.body.classList.remove("tutor-stage-open");
    document.body.classList.remove("ts-endphase");
    this._trapOff();
    this._applyStageCamera(false);
    if (this._onStageResize) {
      window.removeEventListener("resize", this._onStageResize);
    }
    if (this._prevFocus && typeof this._prevFocus.focus === "function") {
      try { this._prevFocus.focus(); } catch (_) {}
    }
    this._prevFocus = null;
  }

  _trapOn() {
    if (this._trapHandler) return;
    this._trapHandler = (e) => {
      if (!this.isOpen || !this._hall) return;
      if (e.key === "Escape") {
        e.preventDefault();
        this._confirmExit();
        return;
      }
      if (e.key !== "Tab") return;
      const root = this._dom && this._dom.root;
      if (!root) return;
      const els = root.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const vis = Array.prototype.filter.call(els, (el) =>
        !el.disabled && !el.hidden && el.closest("[hidden]") === null &&
        (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement));
      if (!vis.length) return;
      const first = vis[0];
      const last = vis[vis.length - 1];
      const inside = root.contains(document.activeElement);
      if (e.shiftKey) {
        if (!inside || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", this._trapHandler, true);
  }

  _trapOff() {
    if (!this._trapHandler) return;
    document.removeEventListener("keydown", this._trapHandler, true);
    this._trapHandler = null;
  }

  _applyStageCamera(on) {
    try {
      const v = window.modelViewer;
      if (!v || !v.camera || typeof v.camera.setViewOffset !== "function") return;
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      const desktop = w > 720;
      if (on && desktop && this._hall) {
        // Mirror the CSS board column: measure the real board box when it is
        // laid out, otherwise fall back to the stylesheet's clamp() math.
        let boardW = 0;
        try {
          const boardEl = this._dom && this._dom.root
            ? this._dom.root.querySelector(".ts-stage-board") : null;
          if (boardEl) boardW = boardEl.getBoundingClientRect().width || 0;
        } catch (_) {}
        if (!boardW) boardW = Math.min(820, Math.max(460, w * 0.5));
        const legendCol = Math.max(240, w - boardW - 48);
        const offsetX = (w - legendCol) / 2;
        v.camera.setViewOffset(w, h, offsetX, 0, w, h);
        this._cameraShifted = true;
      } else if (this._cameraShifted) {
        if (typeof v.camera.clearViewOffset === "function") v.camera.clearViewOffset();
        this._cameraShifted = false;
      }
    } catch (_) {}
  }

  _sleep(ms, token) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(token === undefined || token === this._runToken), ms);
    });
  }

  async _contentJson(url, options) {
    if (!this._captureMode) {
      const res = await fetch(url, options);
      return { res, data: await res.json() };
    }
    const ceiling = Number.isFinite(this._captureRequestMs) && this._captureRequestMs > 0
      ? this._captureRequestMs : CAPTURE_REQUEST_CEILING_MS;
    const ac = typeof AbortController !== "undefined" ? new AbortController() : null;
    const opts = Object.assign({}, options || {}, ac ? { signal: ac.signal } : {});
    let timer = null;
    try {
      return await Promise.race([
        (async () => {
          const res = await fetch(url, opts);
          const data = await res.json();
          return { res, data };
        })(),
        new Promise((_, reject) => {
          timer = setTimeout(() => {
            try { if (ac) ac.abort(); } catch (_) {}
            reject(new Error("capture request timed out"));
          }, ceiling);
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // Stable anonymous id used ONLY to rate-limit fresh-regeneration requests
  // server-side (Task #301). Not an identity; never attached to counters.
  _clientId() {
    try {
      let id = localStorage.getItem("legendlabs.lessonClientId");
      if (!id) {
        id = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("legendlabs.lessonClientId", id);
      }
      return id;
    } catch (_) { return ""; }
  }

  // Task #301 — completion beacon. Echoes the pinned {lessonKey, version}
  // exactly as served (even if approve/retire landed mid-play). Authored
  // /learn catalog lessons carry no pin and never send one. Uses fetch (page
  // is alive at lesson end) so the auth header can ride along for test-account
  // exclusion; keepalive covers an immediate tab close.
  _sendCompletion() {
    if (this._captureMode) return;
    if (this._completeSent) return;
    if (!this._lessonKey || !this._lessonVersion) { this._sendAuthoredCompletion(); return; }
    this._completeSent = true;
    // Task #308 — the fetch MUST be initiated synchronously: on a close-at-end
    // tab, keepalive only preserves a request that already started, and a
    // pending getIdToken() promise is commonly terminated by unload. The token
    // was cached by _authHeaderAsync at lesson load (and Firebase tokens live
    // ~1h, far longer than a lecture), so the cached header is fresh here.
    // NEVER make anything here asynchronous before this fetch is initiated.
    const authH = this._cachedAuthHeader || this._authHeader();
    try {
      fetch("/api/tutor-stage/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authH },
        body: JSON.stringify({
          lessonKey: this._lessonKey,
          version: this._lessonVersion,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) {}
  }
}

// Player ceremony Iteration 2 (B4) — authored /learn course lessons carry no
// library pin, so their completion telemetry is the Scholar's Journal activity
// log (the ONE per-user activity source; anonymous → 401, best-effort). Same
// unload law as the library beacon: initiated SYNCHRONOUSLY with the cached
// auth header, keepalive, BEFORE the forward control navigates. Fires once.
TutorStageController.prototype._sendAuthoredCompletion = function () {
  if (this._captureMode || this._completeSent || !this._standalone ||
      !this.courseId || !this.lessonId) return false;
  this._completeSent = true;
  const authH = this._cachedAuthHeader || this._authHeader();
  const live = this._lastResult || { xp: this._sessionXp || 0, correct: 0, total: 0 };
  try {
    fetch("/api/journal/activity", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authH },
      body: JSON.stringify({
        type: "lesson_complete",
        legend: String(this.character || ""),
        meta: {
          courseId: String(this.courseId), lessonId: String(this.lessonId),
          xp: live.xp | 0, correct: live.correct | 0, total: live.total | 0,
        },
      }),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
  return true;
};

const tutorStage = new TutorStageController();
if (typeof window !== "undefined") {
  window.tutorStage = tutorStage;
}
export default tutorStage;
